import React, { useState, useEffect, useContext, useRef, useCallback, useMemo } from 'react';
import { buildCellReviewReasons } from '../../../utils/pattern/reviewReasons';
import ReviewSummaryPanel from '../../uiParts/ReviewSummaryPanel';
import SubPatternChip from '../../uiParts/SubPatternChip';
import SubPatternEditModal from '../../uiParts/modal/SubPatternEditModal';
import { createSubPattern } from '../../../api/firebase/firestore/subPattern/create';
import { getSubPatterns } from '../../../api/firebase/firestore/subPattern/read';
import { updateSubPattern } from '../../../api/firebase/firestore/subPattern/update';
import type { ItemOverrideType, SubPatternType } from '../../../types/subPattern';
import { subPatternLabel } from '../../../constants/subPattern';
import { buildSubPatternOverrideCandidates } from '../../../utils/pattern/subPatternCandidates';
import { handleSuggestSubPatternRules } from '../../../api/firebase/function/handleSuggestSubPatternRules';
import type { ReviewIssue } from '../../../types/reviewIssue';
import * as Sentry from '@sentry/react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import AddIcon from '@mui/icons-material/Add';
import KeyboardArrowLeftIcon from '@mui/icons-material/KeyboardArrowLeft';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import CloseIcon from '@mui/icons-material/Close';
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined';
import DownloadIcon from '@mui/icons-material/Download';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import ModeEditOutlinedIcon from '@mui/icons-material/ModeEditOutlined';
import EditIcon from '@mui/icons-material/Edit';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import FactCheckOutlinedIcon from '@mui/icons-material/FactCheckOutlined';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import ReplayIcon from '@mui/icons-material/Replay';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import RotateRightRoundedIcon from '@mui/icons-material/RotateRightRounded';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import ViewQuiltIcon from '@mui/icons-material/ViewQuilt';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import TuneIcon from '@mui/icons-material/Tune';
import ManageSearchIcon from '@mui/icons-material/ManageSearch';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';
import {
  Avatar,
  Box,
  Button,
  createTheme,
  FormControl,
  Grid2,
  IconButton,
  Menu,
  MenuItem,
  Paper,
  Select,
  Stack,
  TableContainer,
  ThemeProvider,
  Tooltip,
  Typography,
} from '@mui/material';
import { Controller, useForm } from 'react-hook-form';

import { getFileUrl } from '../../../api/firebase/storage';
import {
  gray100,
  gray5,
  gray40,
  gray80,
  orange50,
  red50,
  primaryButtonBackgroundColor,
  secondaryButtonBackgroundColor,
  tertiaryButtonBackgroundColor,
  tertiaryButtonBackgroundColorWhiteOverlay80,
  white,
  white_overlay80,
} from '../../../constants/colors';
import {
  boldMd,
  boldMdGray,
  boldMdWhite,
  boldSm,
  regularLg,
  regularLgWhite,
  regularMd,
  regularMdWhite,
  regularSm,
  regularXl,
  regularXs,
  baseTypography,
  basePalette,
  baseSwitchStyle,
  baseMenuItemStyle,
  baseRadioStyle,
  baseButtonHoverStyle,
  spHeaderHeight,
} from '../../../constants/location';
import { HELP_FAQ_CONSUMPTION_COUNT } from '../../../constants/externalUrls';
import { aiOcrResultMainInfoSchema } from '../../../validation/aiocrResult';
import { createPatternResultSchema } from '../../../validation/pattern';
import { toFormFieldName } from '../../../utils/pattern/formFieldName';
import MatchReasonLabel from '../../uiParts/MatchReasonLabel';
import { evaluateReviewIssues, parseScore } from '../../../utils/pattern/reviewIssues';
import {
  buildLiveReviewChoiceIssues,
  withoutStaleReviewChoices,
  type LiveReviewChoiceRowValue,
} from '../../../utils/pattern/liveReviewChoice';
import {
  buildLiveValidationIssues,
  withoutStaleValidationIssues,
  type LiveValidationCellError,
} from '../../../utils/pattern/liveValidationIssues';
import { InputFileType } from '../../../types/input';
import { AuthContext } from '../../../contexts/auth';
import { useAuthRedirect } from '../../../hooks/useAuthRedirect';
import CustomDatePicker from '../../uiParts/form/CustomDatePicker';
import CustomLoading from '../../uiParts/form/CustomLoading';
import LoadingModal from '../../uiParts/modal/LoadingModal';
import CustomNumberField from '../../uiParts/form/CustomNumberField';
import CustomSnackbar from '../../uiParts/form/CustomSnackbar';
import CustomTextfield from '../../uiParts/form/CustomTextfield';
import DeleteModal from '../../uiParts/modal/DeleteModal';
import PatternSelectModal from '../../uiParts/modal/PatternSelectModal';
import AssigneeChangeDialog from '../../uiParts/modal/AssigneeChangeDialog';
import { getUsersByCompanyId } from '../../../api/firebase/firestore/user/read';
import { assigneeLabels } from '../../../constants/assignee';
import { resolveAssigneeDisplay, type AssigneeOption } from '../../../utils/assigneeDisplay';
import OcrLimitDialog from '../../uiParts/modal/OcrLimitDialog';
import ItemEditModal, {
  type CalculationEditContext,
  EditItemData,
} from '../../uiParts/modal/ItemEditModal';
import ReOcrSettingsModal, { ReOcrSettings } from '../../uiParts/modal/ReOcrSettingsModal';
import OcrCreditConsumeConfirmModal from '../../uiParts/modal/OcrCreditConsumeConfirmModal';
import { useMediaQuery } from 'react-responsive';
import ImageOverlay from '../../uiParts/overlay/ImageOverlay';
import {
  FocusItemProvider,
  useFocusItem,
  useSetFocusItem,
} from '../../../contexts/FocusItemContext';
import MagnifierToggle from '../../uiParts/overlay/MagnifierToggle';
import SideBar from '../../uiParts/menu/SideBar';
import DeletedBadge from '../../uiParts/parts/DeletedBadge';
import { FirebaseError } from 'firebase/app';
import { getUserErrorMessage } from '../../../modules/error';
import { DocumentSnapshot, type DocumentData } from 'firebase/firestore';
import {
  processingStatus,
  processingStatusLabel,
  registerStatus,
  resultStatus,
  patternStatus,
  geminiModel,
  isProGeminiModel,
  structuredEngineType,
  geminiThinkingBudget,
  displayWidth as displayWidthEnum,
  PATTERN_ID_OTHER,
  ocrExecutionType,
  OCR_EXECUTION_FETCH_LIMIT,
  exportFileType,
  exportEncoding,
  exportQuoteMode,
  reviewIssueCode,
  reviewIssueSource,
  matchReason,
  MASTER_MATCH_REVIEW_THRESHOLD,
  PREVIEW_ZOOM,
  zIndexLayer,
  detailTableStickyHeader,
  toAvailableGeminiModel,
  GEMINI_MODEL_DEFAULT,
} from '../../../constants/enum';
import { isOperatorStaff } from '../../../utils/permission';
import OcrCostDisplay from '../../uiParts/parts/OcrCostDisplay';
import {
  getOcrEngineDisplayName,
  getModelNameDisplay,
  getThinkingConfigDisplayName,
} from '../../../utils/ocrCostDisplay';
import { canViewSharedResource } from '../../../utils/groupAccess';
import { buildResultTabQuery } from '../../../utils/pattern/resultTabParam';
import { useCompanyId } from '../../../hooks/useCompanyId';
import { useMagnifierEnabled } from '../../../hooks/useMagnifierEnabled';
import { useDetailMode } from '../../../hooks/useDetailMode';
import { ProxyLoginContext } from '../../../contexts/ProxyLoginContext';
import {
  PROXY_BANNER_HEIGHT,
  PROXY_DISPLAY_NAME,
  MODEL_OPERATOR_CUSTOM_LABEL,
} from '../../../constants/labels';
import { convertToUserFriendlyMessage } from '../../../utils/errorHandler';
import { executeProvisionalOcrUsage } from '../../../utils/provisionalOcrUsage';
import { calculateOcrLimitInfo, getCreditRateByPlan } from '../../../utils/userUsageLimit';
import { getDisplayName } from '../../../utils/displayName';
import NoAccountIcon from '../../assets/Account circle.svg';
import HiStoryIcon from '../../assets/hi-story-icon.png';
import MetaTitle from '../../utils/MetaTitle';
import FileExportDialog from '../../uiParts/modal/FileExportDialog';
import PatternRuleImprovementModal, {
  ApprovedSuggestion,
} from '../../uiParts/modal/PatternRuleImprovementModal';
import {
  handleSuggestPatternRule,
  RuleSuggestion,
} from '../../../api/firebase/function/handleSuggestPatternRule';
import { getCreateHistory } from '../../../api/firebase/firestore/result/history';
import {
  collectChangedItemIds,
  collectRowCountChanges,
  filterSuggestableItems,
} from '../../../utils/pattern/changedItemIds';
import { getMastersByPatternId } from '../../../api/firebase/firestore/master/readByPattern';
import { saveMasterHistory } from '../../../api/firebase/function/handleMasterVerification';
import { MasterWithMappingType } from '../../../types/master';
import {
  MasterFilterCondition,
  MasterMatchResult,
  MatchFilterValue,
  ReconciliationTarget,
} from '../../../types/masterReconciliation';
import { resolveFilterKeys } from '../../../utils/master/matchKeys';
import MasterReconciliationModal from '../../uiParts/modal/MasterReconciliationModal';
import { getDictionariesByPatternId } from '../../../api/firebase/firestore/dictionary/readByPattern';
import { saveDictionaryHistory } from '../../../api/firebase/firestore/dictionary/history/create';
import {
  DictionaryWithMappingType,
  DictionaryMatchResult,
  DictionaryRow,
} from '../../../types/dictionary';
import DictionaryReconciliationModal from '../../uiParts/modal/DictionaryReconciliationModal';
import StatusChangeConfirmDialog from '../../uiParts/modal/StatusChangeConfirmDialog';
import VerificationTracePanel from './components/VerificationTracePanel';
import DetailTableRow from './components/DetailTableRow';
import MasterHistorySaveConfirmModal, {
  MasterHistoryItem,
} from '../../uiParts/modal/MasterHistorySaveConfirmModal';
import DictionaryHistorySaveConfirmModal, {
  DictionaryHistoryItem,
} from '../../uiParts/modal/DictionaryHistorySaveConfirmModal';
import {
  PatternType,
  ItemListType,
  ItemListChildType,
  ExportSettingsType,
} from '../../../types/pattern';
import { type ItemChoiceType, type ItemValidationType } from '../../../types/common';
import { convertItemListToSnakeCase } from '../../../utils/pattern/itemListConverter';
import {
  sanitizeFieldCode,
  fillMissingFieldCodes,
  renameFieldCodeReferences,
} from '../../../utils/pattern/fieldCode';
import { buildCalculationEditContext } from '../../../utils/pattern/calculationEditContext';
import { type ExportTemplate } from '../../../types/exportTemplate';
import { getExportTemplates } from '../../../api/firebase/firestore/exportTemplate';
import { generateExportTemplateFile } from '../../../api/firebase/function/exportTemplateFunction';
import {
  itemSourceBadgeLabels,
  resolveItemSourceBadge,
} from '../../../utils/pattern/resolveItemSourceBadge';
import { calcResultAccuracy, type ResultAccuracyResult } from '../../../utils/pattern/accuracyCalc';
import { selectBaselineResultData } from '../../../utils/pattern/latestOcrExecution';
import { itemValidationResultMessage, isChoiceDataType } from '../../../constants/itemValidation';
import {
  checkDateRange,
  dateRangeMessage,
  matchesCharType,
  matchesPattern,
  type DateRangeRule,
} from '../../../utils/pattern/itemValidationRules';
import ChoiceField from '../../uiParts/form/ChoiceField';
import {
  arrayCalculationCellKey,
  buildCalculationCellStatesFromResultData,
  containsCalculatedField,
  recalculateCalculatedFields,
  toFormDisplayValue,
  type CalculationCellStates,
} from '../../../utils/pattern/resultCalculation';
import {
  getRowId,
  arrayFieldErrorKey,
  masterSelectionKey,
  dictionarySelectionKey,
  clearedReasonItemKey,
  toRowPositionByRowId,
} from '../../../utils/pattern/detailRowKeys';
import { extractFieldCodes } from '../../../utils/calculation';
import {
  buildCalculationBreakdown,
  formatCalculationBreakdown,
  type BreakdownItem,
  type BreakdownValueSource,
} from '../../../utils/pattern/calculationBreakdown';
import { formatArithmeticBreakdown } from '../../../utils/pattern/calculationArithmeticBreakdown';
import { extractComparisons } from '../../../utils/pattern/calculationComparison';
import { OcrItemKeywords, normalizeOcrItemKeywords } from '../../../utils/search/ocrItemKeywords';
import { exportFile } from '../../../api/firebase/function/exportFileFunction';
import { createFileExportHistory } from '../../../api/firebase/firestore/fileExportHistory/create';
import {
  generatePatternExportData,
  generateExportColumns,
  buildExportBlob,
  generateFileName,
  parseDateStringSafely,
} from '../../../utils/pattern/fileExportUtils';
import { updatePatternItemList } from '../../../api/firebase/firestore/pattern/update';
import {
  createPatternHistory,
  hasPatternHistoryDiff,
} from '../../../api/firebase/firestore/pattern/history';
import { handleReOcrPatternResult } from '../../../api/firebase/function/handleReOcrPatternResult';
import {
  getResult,
  getPatternResultsPaginatedNonRealtime,
} from '../../../api/firebase/firestore/result/read';
import {
  PatternResultSearchConditions,
  RecentResultNavItem,
  WatchSearchItemsType,
} from '../../../types/result';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import {
  updatePatternResult,
  type ReviewMatchSettings,
  DynamicFieldValue,
  DynamicFormData,
  type SelectedMasterEntry,
} from '../../../api/firebase/firestore/result/update';
import { getPattern } from '../../../api/firebase/firestore/pattern/read';
import { doc, updateDoc, Timestamp, getDoc, onSnapshot } from 'firebase/firestore';
import { db } from '../../../api/firebase/config';
import { createResultHistory } from '../../../api/firebase/firestore/result/history';
import { ResultHistoryActionType } from '../../../types/resultHistory';
import {
  getOcrExecutions,
  OcrExecutionRecord,
} from '../../../api/firebase/firestore/result/ocrExecutions';
import { getUser } from '../../../api/firebase/firestore/user/read';

// ページ番号を持たないファイルのソート順。番号付きファイルより後ろへ回すための番兵値。
const PAGE_ORDER_LAST = Number.MAX_SAFE_INTEGER;

/**
 * 列レイアウトが見つからないときのフォールバック（#3275）。
 * ここで `[]` を書くと毎レンダー別の参照になり、
 * DetailTableRow の memo 化が効かなくなるため定数にする。
 */
const EMPTY_CHILDREN: ItemListChildType[] = [];

// 列幅設定に応じた最低幅を返すヘルパー関数
const getColumnMinWidth = (displayWidth?: number): number => {
  switch (displayWidth) {
    case displayWidthEnum.large:
      return 200; // 大: 200px
    case displayWidthEnum.small:
      return 80; // 小: 80px
    case displayWidthEnum.medium:
    default:
      return 140; // 中: 140px
  }
};

// 列幅設定に応じたflex比率を返すヘルパー関数
const getColumnFlex = (displayWidth?: number): number => {
  switch (displayWidth) {
    case displayWidthEnum.large:
      return 3; // 大: 3
    case displayWidthEnum.small:
      return 1; // 小: 1
    case displayWidthEnum.medium:
    default:
      return 2; // 中: 2
  }
};

/**
 * 明細テーブルの空行を作る（#3275）。
 * 行追加・行挿入の3箇所で同じ組み立てをしていたのを共通化した。
 * index には行の一意ID（nextRowIdCounter 由来）を入れる。
 */
const createEmptyRow = (children: ItemListChildType[], rowId: number): DynamicFieldValue => {
  const emptyRow: DynamicFieldValue = { index: rowId };
  children.forEach(field => {
    emptyRow[field.childValue] = field.type === 'number' ? null : '';
  });
  return emptyRow;
};

/**
 * 明細セル1つの値を差し替えた DynamicFormData を返す（#3275）。
 *
 * 以前は `{...dynamicFormData}` の浅いコピー後に
 * `newData[parentValue][itemIndex][childValue] = 値` としており、
 * 配列と行オブジェクトは元の参照のまま書き換えていた（React の不変性違反）。
 * 変更した行だけを新しいオブジェクトにし、他の行は参照を保つ。
 * 行コンポーネントを React.memo 化したとき、編集した行だけが再描画される。
 */
const updateCellValue = (
  formData: DynamicFormData,
  parentValue: string,
  rowIndex: number,
  childValue: string,
  value: string | number | Date | null,
): DynamicFormData => {
  const rows = formData[parentValue] ?? [];
  return {
    ...formData,
    [parentValue]: rows.map((row, index) =>
      index === rowIndex ? { ...row, [childValue]: value } : row,
    ),
  };
};

// Geminiモデルの表示名を取得するヘルパー関数
const getGeminiModelDisplayName = (modelValue?: number): string => {
  switch (modelValue) {
    case geminiModel.pro_2_5:
      return 'Gemini 2.5 Pro';
    case geminiModel.flash_lite_2_5:
      return 'Gemini 2.5 Flash-Lite';
    case geminiModel.flash_3_5:
      return 'Gemini 3.5 Flash';
    case geminiModel.flash_lite_3_1:
      return 'Gemini 3.1 Flash-Lite';
    case geminiModel.flash_3_6:
      return 'Gemini 3.6 Flash';
    case geminiModel.flash_lite_3_5:
      return 'Gemini 3.5 Flash-Lite';
    case geminiModel.pro_3:
      return 'Gemini 3.1 Pro';
    case geminiModel.flash_3:
      return 'Gemini 3 Flash';
    default:
      return 'Gemini 2.5 Flash';
  }
};

/**
 * 運営が検証UIで「Geminiモデル以外」の既定値を変えて実行した結果か（#3206）。
 *
 * OCRエンジン・構造化エンジン・sampling・座標付与を変えた結果は、ユーザーが
 * 同じ条件でアップロードしても再現できない。モデル名だけ出すと「自分でも出せる精度」と
 * 誤解されるため「事務局指定」と表示する。
 *
 * Gemini モデルの変更（Flash → Pro）はモデル選択可のパターンならユーザー自身も選べるため、
 * ここでは既定外とみなさない。
 * verification_settings は運営実行に限らず常に保存されるため、有無ではなく中身で判定する。
 */
const isOperatorCustomizedOcr = (tokenUsage?: DocumentData, geminiModelValue?: number): boolean => {
  if (!tokenUsage) return false;
  const vs = tokenUsage.verification_settings;
  if (!vs) return false;
  // OCRエンジン（既定は Azure Vision）
  const engine = tokenUsage.azure_vision?.engine;
  if (engine && engine !== 'azure-vision') return true;
  // 構造化エンジン（既定は Gemini）
  if (vs.structured_engine && vs.structured_engine !== 'gemini') return true;
  // sampling は運営が手動指定したときだけ値が入る（未指定は null）
  if (vs.temperature !== null && vs.temperature !== undefined) return true;
  if (vs.top_p !== null && vs.top_p !== undefined) return true;
  // 座標付与（既定は無効）
  if (vs.include_coordinates) return true;
  // 思考設定。既定はモデルで決まる（Pro は minimal / Flash は disabled）ので、
  // それと違う値なら運営が明示的に変えている
  const thinkingBudget = tokenUsage.gemini_ocr?.thinking_budget;
  if (thinkingBudget !== undefined && thinkingBudget !== null) {
    const defaultBudget = isProGeminiModel(geminiModelValue)
      ? geminiThinkingBudget.minimal
      : geminiThinkingBudget.disabled;
    if (thinkingBudget !== defaultBudget) return true;
  }
  // 思考レベルは運営が指定したときだけ入る（一般ユーザーは常に null）
  if (tokenUsage.gemini_ocr?.thinking_level) return true;
  return false;
};

// target_item_idsからitem_listを参照して項目名を取得
const resolveItemNames = (targetItemIds: string[], itemList?: ItemListType[]): string => {
  if (!itemList || targetItemIds.length === 0) return `${targetItemIds.length}項目`;

  const names: string[] = [];
  for (const targetId of targetItemIds) {
    // 親項目から検索
    const parentItem = itemList.find(item => item.id === targetId);
    if (parentItem) {
      names.push(parentItem.parentValue);
      continue;
    }
    // 子項目から検索
    for (const item of itemList) {
      const child = item.children?.find((c: ItemListChildType) => c.id === targetId);
      if (child) {
        names.push(child.childValue);
        break;
      }
    }
  }
  return names.length > 0 ? names.join(', ') : `${targetItemIds.length}項目`;
};

// OCR実行履歴のラベルを生成
const getExecutionLabel = (
  execution: OcrExecutionRecord,
  index: number,
  executions: OcrExecutionRecord[],
  itemList?: ItemListType[],
): string => {
  switch (execution.execution_type) {
    case ocrExecutionType.initial:
      return '初回読取';
    case ocrExecutionType.reclassification:
      return '仕分け変更';
    case ocrExecutionType.reOcrAll: {
      const reOcrFiltered = executions
        .filter(
          e =>
            e.execution_type === ocrExecutionType.reOcrAll ||
            e.execution_type === ocrExecutionType.reOcrSingle,
        )
        .reverse();
      const reOcrAllIndex = reOcrFiltered.indexOf(execution);
      return `再読取#${reOcrAllIndex + 1} (全項目)`;
    }
    case ocrExecutionType.reOcrSingle: {
      const reOcrFiltered = executions
        .filter(
          e =>
            e.execution_type === ocrExecutionType.reOcrAll ||
            e.execution_type === ocrExecutionType.reOcrSingle,
        )
        .reverse();
      const reOcrSingleIndex = reOcrFiltered.indexOf(execution);
      const itemNames = resolveItemNames(execution.target_item_ids || [], itemList);
      return `再読取#${reOcrSingleIndex + 1} (${itemNames})`;
    }
    default:
      return `実行#${executions.length - index}`;
  }
};

dayjs.extend(customParseFormat);

/**
 * 日付文字列をDateオブジェクトに変換（日本語形式を含む多形式対応）
 * #3144: 末尾が壊れた文字列（例: '2026-03-03 00:03:SS'）も日付部分を救済する
 */
const parseDateString = (value: string): Date | null => {
  const parsed = parseDateStringSafely(value);
  return parsed ? parsed.toDate() : null;
};

const styles = {
  backGround: {
    height: '100vh',
    width: '100%',
    display: 'flex',
  },

  leftContainer: {
    backgroundColor: gray5,
    '@media (min-width: 960px)': {
      overflowY: 'auto',
      height: '100vh',
    },
    '@media (max-width: 960px)': {
      height: 'auto',
      // 固定フッター(footerButtons)の実高分の余白。
      // 上padding16 + ボタン44 + gap12 + ボタン行44 + 下padding24 = 140px
      paddingBottom: '140px',
    },
  },

  rightContainer: {
    backgroundColor: gray100,
    height: '100vh',
    padding: '24px 16px',
    '@media (min-width: 960px)': {
      overflowY: 'auto',
    },
    '@media (max-width: 960px)': {
      height: 'auto',
      position: 'relative',
      paddingBottom: '72px',
      '&::after': {
        content: '""',
        display: 'block',
        position: 'absolute',
        top: 0,
        left: 0,
        height: 'inherit',
        zIndex: -1,
      },
    },
  },

  leftHeader: {
    backgroundColor: white,
    padding: '18px 24px',
    height: 'auto',
    margin: '0px 2px 16px 0px',
    position: 'sticky' as const,
    top: 0,
    zIndex: zIndexLayer.stickyPageHeader,
    '@media (max-width: 960px)': {
      padding: '10px 12px',
      margin: '0px 0px 8px 0px',
      // SPヘッダー（fixed・高さ spHeaderHeight）の下に貼り付ける。
      // top: 0 のままだとヘッダーと同じ位置に重なり、zIndex も同値で後勝ちになるため
      // スクロール中にヘッダーを覆い隠してしまう。
      top: spHeaderHeight,
      zIndex: zIndexLayer.stickyPageHeaderSp,
    },
  },

  KeyboardArrowLeftButton: {
    color: gray80,
    padding: '10px 12px 10px 4px',
    height: '28px',
    '@media (max-width: 960px)': {
      height: '16px',
      width: '16px',
    },
  },

  KeyboardArrowRightButton: {
    color: gray80,
    padding: '10px 4px 10px 12px',
    height: '28px',
    '@media (max-width: 960px)': {
      height: '16px',
      width: '16px',
    },
  },

  closeButton: {
    color: gray80,
    padding: '10px 4px',
    height: '28px',
    minWidth: '32px',
  },

  staffName: {
    justifyContent: 'flex-end',
    alignItems: 'center',
    margin: '0px 34px 16px 0px',
    gap: '4px',
    '@media (max-width: 960px)': {
      margin: '0px 16px 8px 0px',
    },
  },
  deliveryMainInfo: {
    backgroundColor: white,
    margin: '0px 16px 16px 16px',
    padding: '16px',
    '@media (max-width: 960px)': {
      margin: '0px 0px 8px 0px',
    },
  },
  deliveryProductInfo: {
    backgroundColor: white,
    margin: '0px 16px 40px 16px',
    padding: '16px',
    width: 'unset',
    boxShadow: 0,
    '@media (max-width: 960px)': {
      margin: '0px 0px 40px 0px',
    },
  },
  addButtom: {
    float: 'left',
    color: orange50,
  },
  addButton: {
    color: orange50,
    height: '40px',
    width: '24px',
    padding: '8px 4px',
  },
  removeButton: {
    color: orange50,
    height: '40px',
    width: '24px',
    padding: '8px 4px',
  },

  tableCellProductName: {
    padding: '0px 4px',
    height: '58px',
  },
  tableCellProductId: {
    padding: '0px 4px',
  },
  tableCellRemark: {
    minWidth: '120px',
    width: '100%',
    padding: '0px 4px',
    '@media (max-width: 960px)': {
      width: '120px',
    },
  },
  tableCell: {
    padding: '0px 4px',
  },
  tableCellRemoveButton: {
    padding: '0px',
    width: '24px',
  },

  rightHeader: {
    backgroundColor: gray100,
    width: '100%',
  },

  cardMediaBox: {
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    gap: '32px',
    overflowX: 'auto',
    '@media (max-width: 960px)': {
      margin: '16px 8px 32px',
    },
  },

  zoomControlBar: {
    width: '100%',
    display: 'flex',
    justifyContent: 'flex-end',
    alignItems: 'center',
    gap: '4px',
    padding: '4px 8px',
    backgroundColor: gray100,
  },

  zoomIconButton: {
    color: white,
    padding: '4px',
  },

  zoomPercentText: {
    color: white,
    minWidth: '44px',
    textAlign: 'center',
    fontSize: '0.75rem',
    userSelect: 'none',
  },

  openInNewButton: {
    color: white,
    minWidth: '32px',
  },

  downloadButton: {
    color: white,
    minWidth: '32px',
  },

  actionButtonsGrid: {
    '@media (max-width: 1148px)': {
      flexBasis: '100%',
      maxWidth: '100%',
    },
  },

  navButtonsGrid: {
    '@media (max-width: 1148px)': {
      flexBasis: '100%',
      maxWidth: '100%',
      justifyContent: 'flex-end',
      marginTop: '8px',
    },
    '@media (max-width: 960px)': {
      marginTop: '0px',
    },
  },

  buttonsDisplayNoneSp: {
    '@media (max-width: 960px)': {
      display: 'none',
    },
  },

  footerButtons: {
    backgroundColor: white_overlay80,
    padding: '16px 16px 24px',
    position: 'fixed',
    bottom: '0',
    left: '0',
    right: '0',
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
    boxSizing: 'border-box',
    // leftHeader より上に出してフッターのボタンが覆い隠されないようにする
    zIndex: zIndexLayer.fixedFooter,
    '@media (min-width: 960px)': {
      display: 'none',
    },
  },

  footerTwoButtons: {
    width: '100%',
    display: 'flex',
    gap: '12px',
  },

  leftHeaderIconSize: {
    fontSize: '24px',
    '@media (max-width: 960px)': {
      fontSize: '16px',
    },
  },
  textCss: {
    ...boldMdGray,
    '@media (max-width: 960px)': {
      ...regularSm,
    },
  },

  fileTitleTextCss: {
    ...regularLgWhite,
    flex: 1,
    minWidth: 0,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    '@media (max-width: 960px)': {
      ...regularMdWhite,
    },
  },

  rightHeaderIconSize: {
    fontSize: '32px',
    '@media (max-width: 960px)': {
      fontSize: '24px',
    },
  },
  draftButtonText: {
    fontSize: '14px',
    fontStyle: 'normal',
    fontWeight: '400',
    lineHeight: 'normal',
  },
  // 必須項目バッジ（#2815）項目名の右に表示する赤文字（項目名と同じ文字サイズ）
  // 値の出どころバッジと縦位置を揃えるため lineHeight を共通化する（#3130）
  requiredBadge: {
    fontSize: '12px',
    lineHeight: '18px',
    color: red50,
    flexShrink: 0,
  },
  // 項目の値の出どころを示すバッジ（#3130）。手入力／照合キー／照合反映／辞書キー／辞書変換共通。
  // 青・緑はリンク／成功のセマンティックカラーとして使われているため、
  // 状態ではなく属性を示すこのバッジはグレーにし、必須・エラーの赤を埋もれさせない。
  // 縦位置を項目名・必須と揃えるため lineHeight を固定し、上下 padding は持たせない。
  itemSourceBadge: {
    fontSize: '11px',
    lineHeight: '18px',
    padding: '0 6px',
    borderRadius: '4px',
    flexShrink: 0,
    whiteSpace: 'nowrap',
    color: gray80,
    backgroundColor: gray5,
  },
  dialog: {
    '& .MuiPaper-root': {
      border: '1px solid' + gray40,
      boxSizing: 'border-box',
      padding: '32px',
      margin: '0',
      width: '713px',
      '@media screen and (max-width:960px)': {
        padding: '32px 16px',
        width: '100%',
      },
    },
  },
  dialogCloseButton: {
    height: '32px',
    width: '32px',
    '@media screen and (max-width:960px)': {
      height: '24px',
      width: '24px',
    },
  },
  dialogHeader: {
    alignItems: 'center',
    display: 'flex',
    justifyContent: 'space-between',
    marginBottom: '12px',
    '@media screen and (max-width:960px)': {
      marginBottom: '16px',
    },
  },
  dialogTitle: {
    ...regularXl,
    '@media screen and (max-width:960px)': {
      ...regularLg,
    },
  },
  iconButton: {
    padding: 0,
  },
  tableContainer: {
    minWidth: '100%',
    overflowX: 'auto',
    margin: '0px',
    padding: '0px',
    border: '1px solid #E0E0E0',
  },
  tableHeader: {
    padding: '8px 10px 8px 0',
    color: gray100,
    fontSize: '14px',
    fontWeight: 400,
    minWidth: '35px',
  },
  modalTableCell: {
    color: gray100,
    fontSize: '14px',
    fontWeight: 400,
    padding: '8px 10px 8px 0',
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis',
    overflow: 'hidden',
    minWidth: '35px',
  },
  modalButtonCell: {
    color: gray100,
    fontSize: '14px',
    fontWeight: 400,
    padding: '8px 10px 8px 0',
    width: '44px',
  },
  buttonHeight: {
    display: 'flex',
    width: '44px',
    height: '32px',
    justifyContent: 'center',
    alignItems: 'center',
    marginLeft: '16px',
  },
  idRemoveButton: {
    display: 'flex',
    width: '94px',
    height: '32px',
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: '12px',
  },
  navigationButton: {
    color: gray80,
    padding: '10px 12px',
    height: '32px',
    '@media (max-width: 960px)': {
      height: '24px',
    },
  },
  reReadingOverlay: {
    position: 'relative' as const,
    '&::after': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(226, 232, 240, 0.7)',
      zIndex: 1,
    },
    '& .MuiInputBase-root': {
      color: 'rgba(10, 10, 10, 0.3)',
    },
    '& .MuiButton-root': {
      opacity: 0.3,
      pointerEvents: 'none' as const,
    },
    '& .MuiIconButton-root': {
      opacity: 0.3,
      pointerEvents: 'none' as const,
    },
  },
  reReadingLabel: {
    position: 'absolute' as const,
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    zIndex: 2,
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    color: '#45556C',
    fontSize: '13px',
    fontWeight: 500,
  },
  loadingIcon: {
    color: '#45556C',
    animation: 'spin 1s linear infinite',
    '@keyframes spin': {
      '0%': { transform: 'rotate(0deg)' },
      '100%': { transform: 'rotate(360deg)' },
    },
  },
};

// この画面はローカルテーマを持つため、App.tsx の共通設定を明示的に引き継ぐ必要がある。
// ThemeProvider はネストすると内側が優先され、palette / MuiSwitch を渡さないと
// ブランドのオレンジが失われてMUI既定の青に戻ってしまう（#3073）
const theme = createTheme({
  typography: baseTypography,
  palette: basePalette,
  components: {
    ...baseSwitchStyle,
    // プルダウンの項目はグレーで統一する（#3075）
    ...baseMenuItemStyle,
    // ボタン・アイコンボタンの既定ホバーはグレー（#3310）
    ...baseButtonHoverStyle,
    // 計算フィールドで disabled にしたラジオも選択色を残す（#3358）
    ...baseRadioStyle,
    MuiTableRow: {
      styleOverrides: {
        root: {
          minHeight: '56px',
        },
      },
    },
  },
  breakpoints: {
    values: {
      xs: 0,
      sm: 600,
      md: 960,
      lg: 1200,
      xl: 1536,
    },
  },
});

/**
 * focusItem を Context から読んで ImageOverlay に渡すラッパ（#3275）。
 *
 * 親（PatternAiOcrResult）が購読すると、入力欄をクリックするたびに
 * 7,000行のコンポーネント全体が再レンダーされ、明細の全セルが再評価される。
 * 購読をこの小さなコンポーネントに閉じ込めることで、フォーカスが変わっても
 * 再レンダーされるのはここと ImageOverlay だけになる。
 */
const FocusAwareImageOverlay: React.FC<
  Omit<React.ComponentProps<typeof ImageOverlay>, 'focusedField'>
> = props => {
  const focusItem = useFocusItem();
  return <ImageOverlay {...props} focusedField={focusItem} />;
};

/**
 * 要注意の判定に使う照合設定を組み立てる（#3309）。
 *
 * 照合キーの項目IDを渡さないと、column_mappings で値が入った項目すべてに
 * スコア判定が走り、同じ内容の要注意が反映先の列数だけ並んでしまう。
 *
 * マッピングは画面表示のために既に読み込み済みなので、判定のたびに
 * 取り直さずそれを使う。閾値はマスタ照合のみ（辞書には設定が無い）。
 *
 * 辞書の照合キーは別に持つ（#3340）。要注意の文言を「マスタ一致度 低」と
 * 「辞書一致度 低」で分けるのに使う。match_reason コードは candidate / no_match を
 * マスタと辞書で共有しており、コードだけではどちらの機能由来か区別できない。
 */
const buildReviewMatchSettingsFrom = (
  mastersBySearchItemId: Map<string, MasterWithMappingType>,
  dictionariesBySearchItemId: Map<string, DictionaryWithMappingType>,
): ReviewMatchSettings => {
  const searchItemIds = new Set<string>();
  mastersBySearchItemId.forEach((_, searchItemId) => searchItemIds.add(searchItemId));
  dictionariesBySearchItemId.forEach((_, searchItemId) => searchItemIds.add(searchItemId));
  const reviewThresholds = new Map<string, number>();
  mastersBySearchItemId.forEach((master, searchItemId) => {
    if (typeof master.review_threshold === 'number') {
      reviewThresholds.set(searchItemId, master.review_threshold);
    }
  });
  // 辞書変換の照合キーだけを別に持つ（#3340）。
  // 要注意の文言をマスタ由来／辞書由来で分けるのに使う
  const dictionarySearchItemIds = new Set(dictionariesBySearchItemId.keys());
  return { searchItemIds, reviewThresholds, dictionarySearchItemIds };
};

const PatternAiOcrResultInner: React.FC = () => {
  const { patternId, resultId } = useParams<{ patternId: string; resultId: string }>();
  const navigate = useNavigate();
  // 未認証ならログイン画面へ退避する（元URLは ?redirect= で引き継ぐ。#3203）
  useAuthRedirect();
  const location = useLocation();
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [snackbarText, setSnackbarText] = useState<string>('');
  const [errorText, setErrorText] = useState<string>('');
  const [nextResultId, setNextResultId] = useState<string | null>(null);
  const [prevResultId, setPrevResultId] = useState<string | null>(null);
  const [nextPatternId, setNextPatternId] = useState<string | null>(null);
  const [prevPatternId, setPrevPatternId] = useState<string | null>(null);
  const [recentResultsList, setRecentResultsList] = useState<
    RecentResultNavItem[] | null | undefined
  >(undefined);
  // 一覧画面から引き継いだ表示順の resultId 配列（次へ/前への並び順を一覧と一致させるために使用）
  const [navigationResultIds, setNavigationResultIds] = useState<string[] | null>(null);
  const [searchData, setSearchData] = useState<PatternResultSearchConditions>({
    fileName: null,
    responsiblePerson: null,
    assignee: null,
    startDate: null,
    endDate: null,
  });
  // OCR読取項目の検索キーワード（一覧画面に戻った際に復元するため引き継ぐ）
  // 一覧に戻る際に検索条件を復元するための引き回し用（この画面では検索には使わない）
  const [ocrItemKeywords, setOcrItemKeywords] = useState<OcrItemKeywords>({});

  // 検索条件をnavigation stateに渡すためのシリアライズ
  const serializeSearchData = (): Record<string, unknown> => ({
    ...searchData,
    startDate: searchData?.startDate ? searchData.startDate.toISOString() : null,
    endDate: searchData?.endDate ? searchData.endDate.toISOString() : null,
  });

  const [createUser, setCreateUser] = useState<string>('');
  const [icon, setIcon] = useState<string>('');
  const [files, setFiles] = useState<InputFileType[]>([]);
  const [originalFileUrl, setOriginalFileUrl] = useState<string>('');
  const [fileName, setFileName] = useState<string>('');
  // フォーカス項目は Context 管理（#3275）。ここで useState に持つと
  // 更新のたびにこの巨大なコンポーネント全体が再レンダーされる
  const setFocusItem = useSetFocusItem();
  const [isProcessingStatus, setIsProcessingStatus] = useState<number | null>(null);
  const { user, company, isLoading } = useContext(AuthContext);
  const { isProxyMode, companyId: effectiveCompanyId } = useCompanyId();
  const { operatorId, isProxyRestorePending } = useContext(ProxyLoginContext);
  const { isDetailView } = useDetailMode();
  const [baseRemainingCount, setBaseRemainingCount] = useState<number | null>(null);
  const [baseRemainingJpy, setBaseRemainingJpy] = useState<number | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [patternData, setPatternData] = useState<PatternType | null>(null);
  const [dynamicFormData, setDynamicFormData] = useState<DynamicFormData>({});
  // 保存済みの明細のスナップショット（#2967）。未保存判定に使う
  const savedDynamicFormDataRef = useRef<string>('');
  // 配列セルのバリデーションエラー: { "親項目名-行index-子項目名": "エラーメッセージ" }
  const [arrayFieldErrors, setArrayFieldErrors] = useState<Record<string, string>>({});
  // 計算セル（#3172）の表示状態。単一値は itemId、明細セルは arrayCalculationCellKey のキー
  const [calculationCellStates, setCalculationCellStates] = useState<CalculationCellStates>({});
  /**
   * 計算フィールドの現在値（itemId → 計算結果）。#3358
   *
   * 要注意判定と内訳表示に使う。getValues を直接読むと、再計算 effect が
   * setValue で値を書く**前**に memo が走ってしまい、1手前の値を見てしまう
   * （計算結果の表示だけ変わって要注意が追従しない原因）。
   * 再計算が出した値をそのまま state に持ち、それを唯一の参照元にする。
   */
  const [calculatedValuesByItemId, setCalculatedValuesByItemId] = useState<Map<string, string>>(
    new Map(),
  );

  // 計算フィールドの再計算予約トリガー（#3172）。参照元の編集確定・行追加/削除・照合反映で立て、
  // state 反映後の effect で実行する
  const [calculationRecalcTrigger, setCalculationRecalcTrigger] = useState<number>(0);
  const [resultRecord, setResultRecord] = useState<DocumentData | undefined>(undefined);
  const [nextRowIdCounter, setNextRowIdCounter] = useState<number>(0);
  const [isDataReady, setIsDataReady] = useState<boolean>(false);
  const [editModalOpen, setEditModalOpen] = useState<boolean>(false);
  const [editItemData, setEditItemData] = useState<EditItemData | null>(null);
  // 計算フィールド設定（#3145）のコンテキスト。モーダルを開くたびに対象項目に合わせて作り直す
  const [editItemCalcContext, setEditItemCalcContext] = useState<CalculationEditContext | null>(
    null,
  );
  // 項目編集モーダルを開いたときにフィールドコードを補完した item_list（#3145）。
  // 保存時にこれを基準に更新することで、開いて閉じただけでは変更が残らないようにする
  const editBaseItemListRef = useRef<ItemListType[] | null>(null);
  // 明細テーブルの縦スクロール枠（#3356）。行を追加したときに一番下まで送るために持つ。
  // 1画面に明細が複数あるため itemGroup.id をキーにする
  const detailScrollRefs = useRef<Map<string, HTMLDivElement>>(new Map());
  const [isReOcrProcessing, setIsReOcrProcessing] = useState<boolean>(false);
  const [reOcrProgress, setReOcrProgress] = useState<number>(0);
  // 一般ユーザー向け OCR枚数消費の確認モーダル
  const [creditConfirmOpen, setCreditConfirmOpen] = useState<boolean>(false);
  // 再読取設定モーダル（#2960 運営スタッフ限定）。開くときに1回目の設定を初期値にする。
  const [reOcrModalOpen, setReOcrModalOpen] = useState<boolean>(false);
  const [reOcrSettings, setReOcrSettings] = useState<ReOcrSettings>({
    geminiModel: GEMINI_MODEL_DEFAULT,
    structuredEngine: structuredEngineType.gemini,
    includeCoordinates: false,
    geminiTemperature: 0,
    geminiTopP: 0,
    thinkingBudget: null,
    thinkingLevel: null,
  });
  const [reReadingItemIds, setReReadingItemIds] = useState<string[]>([]);
  const [reclassifyModalOpen, setReclassifyModalOpen] = useState<boolean>(false);
  const [reclassifyLoading, setReclassifyLoading] = useState<boolean>(false);
  const [limitDialogOpen, setLimitDialogOpen] = useState<boolean>(false);
  const [ocrExecutions, setOcrExecutions] = useState<OcrExecutionRecord[]>([]);
  const [selectedExecutionIndex, setSelectedExecutionIndex] = useState<number>(-1); // -1 = 最新（result.token_usage）
  const [executionUserIcons, setExecutionUserIcons] = useState<Record<string, string>>({});
  const [zoomLevel, setZoomLevel] = useState<number>(PREVIEW_ZOOM.DEFAULT_LEVEL);
  // ルーペ（拡大表示）のON/OFF（読取パターンごとにlocalStorageへ永続化・デフォルトON #3079）
  const { magnifierEnabled, toggleMagnifier } = useMagnifierEnabled(patternId);
  // ルーペ（拡大表示）はマウス移動でしか位置が決まらず、タッチ操作では動かない。
  // SP では使えないコントロールを画面に残さないよう、トグル自体を非表示にする（#3224）。
  // localStorage の保存値は書き換えないため、PC に戻れば従来の設定がそのまま復活する。
  const isSpLayout = useMediaQuery({ query: '(max-width: 960px)' });
  const [fileExportDialogOpen, setFileExportDialogOpen] = useState<boolean>(false);
  // 指定フォーマット出力（#3070）。このパターンに登録済みのフォーマット
  const [exportTemplates, setExportTemplates] = useState<ExportTemplate[]>([]);
  const [hasEditHistory, setHasEditHistory] = useState<boolean>(false);
  // 読み取り精度（#2960 検証用・運営のみ表示）。初回OCR値と最終保存値の項目一致率。
  const [ocrAccuracy, setOcrAccuracy] = useState<ResultAccuracyResult | null>(null);
  // 検証トレースパネルの開閉（#3109 運営のみ）。OCR結果・プロンプト・値取得結果を表示する。
  const [verificationPanelOpen, setVerificationPanelOpen] = useState<boolean>(false);
  const [ruleImprovementOpen, setRuleImprovementOpen] = useState<boolean>(false);
  // 子パターンの作成（#2967）
  const [subPatternModalOpen, setSubPatternModalOpen] = useState<boolean>(false);
  // 編集中の差分（#2967）。null なら新規作成。
  // 取得ルールは帳票を見ないと書けないため、編集もこの画面で行う
  const [editingSubPattern, setEditingSubPattern] = useState<SubPatternType | null>(null);
  const [subPatternSubmitting, setSubPatternSubmitting] = useState<boolean>(false);
  // AI改善の提案から引き継ぐ初期値（項目IDごとの取得ルール）
  const [subPatternInitialOverrides, setSubPatternInitialOverrides] = useState<
    Record<string, string>
  >({});
  /**
   * 値を直した項目があるか（#2967）。
   *
   * AI提案は「直した項目だけ」を対象にするため、1件も無ければ提案しても何も返らない。
   * 押せてしまうとAIコールだけ発生して空の結果が返るので、ボタン自体を止める
   */
  const [hasChangedItems, setHasChangedItems] = useState<boolean>(false);
  /**
   * 値を直した項目の名前（#2967）。
   * 「どれに対して提案するのか」をモーダルに出すために使う
   */
  const [changedItemNames, setChangedItemNames] = useState<string[]>([]);
  // AI提案から引き継ぐ「この帳票での項目名」（#2967）。項目IDごとの印字見出し
  const [subPatternInitialItemNames, setSubPatternInitialItemNames] = useState<
    Record<string, string>
  >({});
  // 名称の重複判定に使う既存の子パターン
  const [existingSubPatterns, setExistingSubPatterns] = useState<SubPatternType[]>([]);
  // AI提案の取得中か（#2967）
  const [subPatternSuggesting, setSubPatternSuggesting] = useState<boolean>(false);
  // AI提案を受け取った結果のID（#2967）。
  //
  // boolean ではなく**結果IDを持つ**。結果画面は次/前の移動でURLだけが変わり
  // コンポーネントは再マウントされないため、boolean だと前の結果の提案が
  // 次の結果に残ってしまう（別の帳票なのに「提案し直す」と出る）。
  // 現在の resultId と一致するときだけ「提案済み」とみなす
  const [subPatternSuggestedResultId, setSubPatternSuggestedResultId] = useState<string | null>(
    null,
  );
  const subPatternSuggested =
    subPatternSuggestedResultId !== null && subPatternSuggestedResultId === resultId;
  // 別の結果へ移ったら提案の下書きを捨てる。前の帳票のルールを
  // 引き継いだまま子パターンを作ってしまわないようにする
  useEffect(() => {
    setSubPatternSuggestedResultId(prev => (prev === resultId ? prev : null));
    setSubPatternInitialOverrides(prev => (Object.keys(prev).length === 0 ? prev : {}));
    setSubPatternInitialItemNames(prev => (Object.keys(prev).length === 0 ? prev : {}));
  }, [resultId]);
  // 子パターンの変更（#2967）

  // 子パターンを作成できるか（#2967）。機能が有効な企業でのみ導線を出す
  // 子パターンは全ユーザーが使える。企業ごとの有効化フラグは廃止した（#2967）
  const canCreateSubPattern = true;
  // 上書きできる項目の候補。親パターンの item_list から組み立てる
  const subPatternCandidates = useMemo(
    () => buildSubPatternOverrideCandidates(patternData?.item_list ?? []),
    [patternData],
  );
  // 子パターンを変更できるか（#2967）。子パターンを持つパターンでのみ導線を出す。
  // 判定を誤ったときに選び直せるようにするため、子パターンが適用されていない結果でも変更できる
  const canChangeSubPattern = canCreateSubPattern && patternData?.has_sub_patterns === true;
  const [ruleImprovementLoading, setRuleImprovementLoading] = useState<boolean>(false);
  const [ruleSuggestions, setRuleSuggestions] = useState<RuleSuggestion[]>([]);
  const cachedRuleSuggestionsRef = useRef<RuleSuggestion[] | null>(null);
  const [actionMenuAnchor, setActionMenuAnchor] = useState<null | HTMLElement>(null);
  const [mastersBySearchItemId, setMastersBySearchItemId] = useState<
    Map<string, MasterWithMappingType>
  >(new Map());
  const [masterMappingTargetIds, setMasterMappingTargetIds] = useState<Set<string>>(new Set());
  const [masterModalOpen, setMasterModalOpen] = useState<boolean>(false);
  const [reconciliationTarget, setReconciliationTarget] = useState<ReconciliationTarget | null>(
    null,
  );
  // 辞書照合（マスタ照合と並んで独立して動作する）
  const [dictionariesBySearchItemId, setDictionariesBySearchItemId] = useState<
    Map<string, DictionaryWithMappingType>
  >(new Map());
  const [dictionaryMappingTargetIds, setDictionaryMappingTargetIds] = useState<Set<string>>(
    new Set(),
  );
  const [dictionaryModalOpen, setDictionaryModalOpen] = useState<boolean>(false);
  const [dictionaryReconciliationTarget, setDictionaryReconciliationTarget] = useState<{
    dictionary: DictionaryWithMappingType;
    rowIndex?: number;
    ocrValue?: string;
    // 入力欄の現在値。モーダルの初期検索クエリに使う（ユーザーが入力し直した値で再検索）。
    searchQuery?: string;
  } | null>(null);
  const [masterHistoryConfirmOpen, setMasterHistoryConfirmOpen] = useState<boolean>(false);
  // バリデーションエラー確認ダイアログ（#2815）。エラーがあっても操作を続行するか確認する
  const [validationConfirmOpen, setValidationConfirmOpen] = useState<boolean>(false);
  const [validationConfirmBody, setValidationConfirmBody] = useState<string>('');
  const pendingValidationActionRef = useRef<(() => void) | null>(null);
  // 項目設定の保存契機でバリデーションを再実行するためのトリガー（#2815）
  const [validationRevalidateTrigger, setValidationRevalidateTrigger] = useState<number>(0);
  const [masterHistoryItems, setMasterHistoryItems] = useState<MasterHistoryItem[]>([]);
  const [masterHistoryNextStatus, setMasterHistoryNextStatus] = useState<number>(
    processingStatus.completed,
  );
  // 辞書変換の変換履歴 保存確認モーダル
  const [dictionaryHistoryConfirmOpen, setDictionaryHistoryConfirmOpen] = useState<boolean>(false);
  const [dictionaryHistoryItems, setDictionaryHistoryItems] = useState<DictionaryHistoryItem[]>([]);
  const [dictionaryHistoryNextStatus, setDictionaryHistoryNextStatus] = useState<number>(
    processingStatus.completed,
  );
  // 手動で辞書変換した内容（保存確認モーダル・変換履歴保存に渡すため）
  // キー: `${dictionary_id}-${行ID}`（#3283）
  const [selectedDictionaryHistories, setSelectedDictionaryHistories] = useState<
    Map<
      string,
      {
        dictionary_id: string;
        query_text: string;
        matched_value: string;
        matched_row: DictionaryRow;
        search_item_id: string;
        search_item_label: string;
      }
    >
  >(new Map());
  // 照合で選択されたマスタ情報（保存確認モーダルに渡すため）
  const [selectedMasterIds, setSelectedMasterIds] = useState<
    Map<
      string,
      {
        ocr_value: string;
        matched_label: string;
        label: string;
        source_field_name: string;
        master_record_id?: number;
        match_reason_label?: string;
        // スコープ（取引先など）の値。設定時のみ。履歴保存・確認モーダル表示に使う。
        supplier_id?: string;
        // スコープ項目の名称（例：取引先コード）。確認モーダル表示用。
        supplier_label?: string;
        // 照合キー項目の item_id（保存時に result_data のどの項目へラベルを書くか）（#3078）。
        search_item_id?: string;
        // スコアラベル（例："85%"）。保存時に result_data へ書き込む（#3078）。
        score?: string;
        // 反映対象の行インデックス（配列項目の場合。単一値は 0）（#3078）。
        row_index?: number;
        // 選択で埋める値（column_mappings 由来）。保存時に result_data へ反映する（#3078）。
        applied_values?: { item_id: string; value: unknown }[];
        // 再照合で使った絞り込みの値。保存時に result_data へ書き込み、照合理由に出す（#3517）。
        match_filter_values?: MatchFilterValue[];
      }
    >
  >(new Map());
  // 紐付け解除（D-2）した照合キー（`${master_id}-${行ID}`）（#3283）。
  // Firestore 由来の match_reason_label をローカルで非表示にするために使う。
  // 保存ボタンを押すまで Firestore は更新しないため、画面上の打ち消し用。
  const [unlinkedMasterKeys, setUnlinkedMasterKeys] = useState<Set<string>>(new Set());
  // 紐付け解除で照合理由ラベル/スコアを消す対象の item_id（保存時に Firestore へ反映する）。
  // 単一値: `${itemId}`、配列子項目: `${childId}.${行ID}`（#3283）。
  const [clearedReasonItemKeys, setClearedReasonItemKeys] = useState<Set<string>>(new Set());
  // 担当者（#2642）
  const [assigneeOptions, setAssigneeOptions] = useState<AssigneeOption[]>([]);
  // 担当者「表示解決」用（#3095）: 共有範囲で絞らない会社の全アクティブ(finished)メンバー。
  // 割り当て候補(assigneeOptions)は共有範囲で絞る(#2927)が、既存担当者の表示は、
  // 共有範囲外になった現役メンバーを「退会済ユーザー」と誤表示しないよう絞らないリストで解決する。
  const [assigneeDisplayOptions, setAssigneeDisplayOptions] = useState<AssigneeOption[]>([]);
  const [assigneeDialogOpen, setAssigneeDialogOpen] = useState<boolean>(false);

  const handleZoomIn = (): void => {
    setZoomLevel(prev =>
      Math.min(
        Math.round((prev + PREVIEW_ZOOM.STEP) * PREVIEW_ZOOM.PERCENT_BASE) /
          PREVIEW_ZOOM.PERCENT_BASE,
        PREVIEW_ZOOM.MAX_LEVEL,
      ),
    );
  };

  const handleZoomOut = (): void => {
    setZoomLevel(prev =>
      Math.max(
        Math.round((prev - PREVIEW_ZOOM.STEP) * PREVIEW_ZOOM.PERCENT_BASE) /
          PREVIEW_ZOOM.PERCENT_BASE,
        PREVIEW_ZOOM.MIN_LEVEL,
      ),
    );
  };

  const handleZoomReset = (): void => {
    setZoomLevel(PREVIEW_ZOOM.DEFAULT_LEVEL);
  };

  // 編集履歴のアンマウント時保存用 ref
  // - 直近で履歴に保存した状態を保持。クリーンアップで現在値と比較し子パターンがあれば履歴1件追加
  // - 最新の patternData / user / patternId はクロージャがstaleにならないよう ref で保持
  const lastSavedStateRef = useRef<{
    prompt: string;
    useCustomPrompt: boolean;
    geminiModel: number | null;
    ocrEngine: number | null;
    itemList: ItemListType[];
  } | null>(null);
  const latestPatternDataRef = useRef<PatternType | null>(null);
  const latestUserRef = useRef<typeof user>(user);
  const latestPatternIdRef = useRef<string | null>(null);

  // 詳細モードOFF時は実行履歴選択を最新にリセット
  useEffect(() => {
    if (!isDetailView) {
      setSelectedExecutionIndex(-1);
      // 詳細モードをOFFにすると検証マーク（情報欄）自体が消えるため、
      // 開いていた検証ログパネルも閉じて表示の不整合をなくす（#3109）。
      setVerificationPanelOpen(false);
    }
  }, [isDetailView]);

  // 各 ref をレンダーごとに最新値に同期
  useEffect(() => {
    latestPatternDataRef.current = patternData;
    // 初回 patternData 取得時にベースラインを記録（編集履歴の子パターン判定用）
    if (patternData && lastSavedStateRef.current === null) {
      lastSavedStateRef.current = {
        prompt: patternData.prompt ?? '',
        useCustomPrompt: patternData.use_custom_prompt ?? false,
        geminiModel: patternData.gemini_model ?? null,
        ocrEngine: patternData.ocr_engine ?? null,
        itemList: patternData.item_list,
      };
    }
  }, [patternData]);
  useEffect(() => {
    latestUserRef.current = user;
  }, [user]);
  useEffect(() => {
    latestPatternIdRef.current = patternId ?? null;
  }, [patternId]);

  // 画面離脱時に prompt/モデル/項目リストの子パターンがあれば pattern_history に1件まとめて保存
  useEffect(() => {
    return (): void => {
      const pattern = latestPatternDataRef.current;
      const userSnap = latestUserRef.current;
      const pid = latestPatternIdRef.current;
      const baseline = lastSavedStateRef.current;
      if (!pattern || !userSnap || !pid || baseline === null) return;

      const currentSnapshot = {
        prompt: pattern.prompt ?? '',
        useCustomPrompt: pattern.use_custom_prompt ?? false,
        geminiModel: pattern.gemini_model ?? null,
        ocrEngine: pattern.ocr_engine ?? null,
        itemList: pattern.item_list,
      };
      if (!hasPatternHistoryDiff(baseline, currentSnapshot)) return;

      // クリーンアップは同期実行のため、Firestore 書き込みは fire-and-forget
      void createPatternHistory({
        patternId: pid,
        prompt: currentSnapshot.prompt,
        useCustomPrompt: currentSnapshot.useCustomPrompt,
        itemList: currentSnapshot.itemList,
        geminiModel: currentSnapshot.geminiModel,
        ocrEngine: currentSnapshot.ocrEngine,
        changedBy: userSnap.uid,
        changedByName: userSnap.name ?? '',
      }).catch(historyError => {
        Sentry.captureException(historyError);
      });
    };
  }, []);

  // 選択中の実行履歴に基づくtoken_usage（-1=最新のresult.token_usage）
  const displayTokenUsage =
    selectedExecutionIndex >= 0 && ocrExecutions[selectedExecutionIndex]
      ? (ocrExecutions[selectedExecutionIndex].token_usage as DocumentData)
      : resultRecord?.token_usage;

  // 子パターン判定のコスト（#2967）。
  // Functions は sub_pattern_classification で記録する。旧名称（variant_classification）で
  // 記録された結果も残っているため、どちらのキーでも表示できるようにする
  const subPatternClassificationCost =
    displayTokenUsage?.sub_pattern_classification ?? displayTokenUsage?.variant_classification;

  // 選択中の実行履歴が1項目再読取の場合、対象項目IDリスト（表示フィルタ用）
  const selectedExecutionTargetIds: string[] | null =
    selectedExecutionIndex >= 0 && ocrExecutions[selectedExecutionIndex]?.target_item_ids
      ? ocrExecutions[selectedExecutionIndex].target_item_ids
      : null;

  // 選択中の実行の検証トレース（#3109）。
  // 実行履歴を選択中はその実行、最新（-1）表示のときは最新実行（executed_at降順の先頭）を参照する。
  // 検証パラメータを指定せず実行した通常処理では null。
  const selectedVerificationTrace =
    (selectedExecutionIndex >= 0
      ? ocrExecutions[selectedExecutionIndex]?.verification_trace
      : ocrExecutions[0]?.verification_trace) ?? null;

  // 遷移元から検索条件または最近の処理履歴リストを取得
  // 企業の月間OCR残枚数をリアルタイム監視（仮消費用）
  useEffect(() => {
    if (!company?.company_id || isProxyMode) return;
    const companyDocRef = doc(db, 'companies', company.company_id);
    const unsubscribe = onSnapshot(
      companyDocRef,
      snapshot => {
        const data = snapshot.data();
        if (data) {
          const ocrMonthlyBudget = data.ocr_monthly_budget ?? company.ocr_monthly_budget;
          const creditRate = getCreditRateByPlan(data.plan ?? company.plan);
          const currentMonthOcrCost = data.current_month_ocr_cost ?? 0;
          const baseRemaining = Math.floor((ocrMonthlyBudget - currentMonthOcrCost) / creditRate);
          setBaseRemainingCount(Math.max(0, baseRemaining));
          setBaseRemainingJpy(Math.max(0, ocrMonthlyBudget - currentMonthOcrCost));
        }
      },
      error => {
        // ログアウト直後にリスナーが permission-denied を受け取るのは正常な動作
        if (error instanceof FirebaseError && error.code === 'permission-denied') {
          return;
        }
        Sentry.captureException(error);
      },
    );
    return (): void => unsubscribe();
  }, [company, isProxyMode]);

  // 結果の共有範囲（#2804）。担当者候補の絞り込みに使用（#2927）
  /**
   * 要注意（#3262）。保存済みの明細ではなく、表示時に判定し直す（#3328）。
   *
   * 以前は results 直下の review_issues を使っていたが、1MB 制限を避けるため
   * 200 件で打ち切って保存しており、201 件目以降がバッジもサマリーも出なかった。
   * 要注意が出ていない項目は確認しない運用では見落としにつながる。
   *
   * 判定材料（is_uncertain / match_reason / score と項目設定）はいずれも
   * result_data に全件残っているため、ここで判定し直せば件数の制限がなくなる。
   * バリデーションを yup に任せているのと同じく、判定材料は保存し
   * 判定結果は都度求める形に揃えている。
   *
   * 確認済・出力済のときはバッジを出さない（判定材料は保持したまま UI で出し分ける）
   */
  const savedReviewIssues = useMemo(() => {
    const resultData = resultRecord?.result_data as Record<string, unknown> | undefined;
    if (!resultData || !patternData?.item_list) return [];
    const { searchItemIds, reviewThresholds, dictionarySearchItemIds } =
      buildReviewMatchSettingsFrom(mastersBySearchItemId, dictionariesBySearchItemId);
    return evaluateReviewIssues(
      resultData,
      patternData.item_list,
      // 照合設定が取れないうちは絞り込まない（従来どおりのフォールバック）
      searchItemIds.size > 0 ? searchItemIds : undefined,
      undefined,
      reviewThresholds,
      // 照合設定が取れないうちはラベルの文字列判定に戻る（#3340）
      searchItemIds.size > 0 ? dictionarySearchItemIds : undefined,
    );
  }, [resultRecord, patternData, mastersBySearchItemId, dictionariesBySearchItemId]);

  /**
   * 明細セルの現在値（#3358）。要注意の判定に使う。
   *
   * dynamicFormData から都度作る。計算列は再計算で、選択肢の列は人の操作で
   * 値が変わるが、どちらも dynamicFormData に反映されるため、ここを見れば
   * 両方を取りこぼさない。
   */
  const calculatedRowValues = useMemo(() => {
    const values: LiveReviewChoiceRowValue[] = [];
    (patternData?.item_list ?? []).forEach((item: ItemListType) => {
      // 「要注意にする選択肢」が設定された列だけを見る
      const targets = (item.children ?? []).filter(
        (child: ItemListChildType) => (child.reviewChoiceValues?.length ?? 0) > 0,
      );
      if (targets.length === 0) return;
      const rows = dynamicFormData[item.parentValue];
      if (!Array.isArray(rows)) return;
      rows.forEach((row, rowIndex) => {
        targets.forEach((child: ItemListChildType) => {
          const raw = (row as Record<string, unknown>)?.[child.childValue];
          values.push({
            parentId: item.id,
            childId: child.id,
            // 要注意のパスは行ID で作る（#3283。行の挿入・削除でズレないようにする）
            rowIndex: getRowId(row, rowIndex),
            value: raw === null || raw === undefined ? '' : String(raw),
          });
        });
      });
    });
    return values;
  }, [patternData, dynamicFormData]);

  /**
   * 明細テーブルの列レイアウトを親項目ごとに1度だけ計算する（#3275）。
   *
   * activeChildren は `children.filter(...)` で作られるため、レンダーのたびに
   * 別の配列になる。これを DetailTableRow に props で渡していたので、
   * arePropsEqual が冒頭の参照比較で必ず false を返し、memo 化が
   * 効かないまま全行が再描画されていた。
   *
   * キーには item_list を作り直しても変わらない itemGroup.id を使う
   * （項目設定の保存時に `{ ...patternData, item_list }` で差し替わるため、
   * オブジェクト参照や配列の index はキーにできない）。
   */
  const detailTableLayouts = useMemo(() => {
    const layouts = new Map<
      string,
      {
        activeChildren: ItemListChildType[];
        totalMinWidth: number;
        totalFlex: number;
        hasTextareaChild: boolean;
      }
    >();

    (patternData?.item_list ?? []).forEach((itemGroup: ItemListType) => {
      // children が無い項目（単一値）も含めて全件登録する。
      // 描画側の分岐と条件がズレて取りこぼすのを避けるため
      const activeChildren = (itemGroup.children ?? []).filter(
        (c: ItemListChildType) => c.is_active === true,
      );
      // 各列の最低幅の合計 + ボタン列64px
      const totalMinWidth =
        activeChildren.reduce(
          (sum: number, c: ItemListChildType) => sum + getColumnMinWidth(c.displayWidth),
          0,
        ) + 64;
      const totalFlex = activeChildren.reduce(
        (sum: number, c: ItemListChildType) => sum + getColumnFlex(c.displayWidth),
        0,
      );
      const hasTextareaChild = activeChildren.some((c: ItemListChildType) => c.type === 'textarea');

      layouts.set(itemGroup.id, {
        activeChildren,
        totalMinWidth,
        totalFlex,
        hasTextareaChild,
      });
    });

    return layouts;
  }, [patternData?.item_list]);

  /** 要注意サマリーの項目名クリックで該当項目までスクロールする */
  const scrollToReviewIssue = useCallback((issue: ReviewIssue): void => {
    const target = document.querySelector(`[data-review-path="${CSS.escape(issue.path)}"]`);
    if (!target) return;
    target.scrollIntoView({ behavior: 'smooth', block: 'center' });
    // スクロール完了後に入力欄へフォーカスする（あらた版と同じ 600ms 待ち）
    window.setTimeout(() => {
      const input = target.querySelector('input, textarea');
      if (input instanceof HTMLElement) input.focus();
    }, 600);
  }, []);

  /**
   * 明細テーブルを一番下までスクロールする（#3356）。
   * 行を追加しても縦スクロール枠の外（下）に増えるだけで見えないため、
   * 追加した行が見える位置まで送る。
   * 枠を作っていないテーブル（行数がしきい値以下・SP）は ref が無いので何もしない。
   */
  const scrollDetailTableToBottom = useCallback((itemGroupId: string): void => {
    const container = detailScrollRefs.current.get(itemGroupId);
    if (!container) return;
    // requestAnimationFrame を二重にして、state 更新による再描画が終わった次のフレームで
    // スクロールする。1回だけだと追加行が描画される前に走り、scrollHeight が
    // 追加前の高さのままになって一番下まで届かないことがある
    window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => {
        container.scrollTo({ top: container.scrollHeight, behavior: 'smooth' });
      });
    });
  }, []);

  const resultShareScope = resultRecord?.share_scope;
  const resultSharedGroupIds = resultRecord?.shared_group_ids;
  const resultSharedUserIds = resultRecord?.shared_user_ids;
  const resultOwnerUserId = resultRecord?.owner_user_id;

  // 担当者候補リストをロード（#2642）
  // 結果の共有範囲に応じ、結果を閲覧できるユーザーのみ候補にする（#2927）
  useEffect(() => {
    if (isLoading || !user) return;
    const companyId = effectiveCompanyId || user.company_id;
    if (!companyId) {
      setAssigneeOptions([]);
      setAssigneeDisplayOptions([]);
      return;
    }
    const resource = {
      share_scope: resultShareScope,
      shared_group_ids: resultSharedGroupIds,
      shared_user_ids: resultSharedUserIds,
      owner_user_id: resultOwnerUserId,
    };
    void (async (): Promise<void> => {
      try {
        const users = await getUsersByCompanyId(companyId);
        const finishedUsers = users.filter(u => u.register_status === registerStatus.finished);
        const byName = (a: AssigneeOption, b: AssigneeOption): number =>
          a.name.localeCompare(b.name, 'ja');

        // 表示解決用（#3095）: 共有範囲で絞らない全アクティブメンバー。
        // 共有範囲外になった現役メンバーを「退会済」と誤表示しないため、割り当て候補とは別に保持する。
        setAssigneeDisplayOptions(
          finishedUsers
            .map(u => ({
              uid: u.uid as string,
              name: (u.name as string) ?? '',
              iconUrl: (u.icon_url as string | undefined) ?? null,
            }))
            .sort(byName),
        );

        // 割り当て候補用（#2927）: 共有範囲外で結果を閲覧できないユーザーは担当者候補から除外する
        setAssigneeOptions(
          finishedUsers
            .filter(u =>
              canViewSharedResource(
                {
                  uid: u.uid as string,
                  role: (u.role as number | null | undefined) ?? null,
                  group_ids: u.group_ids as string[] | undefined,
                },
                resource,
              ),
            )
            .map(u => ({
              uid: u.uid as string,
              name: (u.name as string) ?? '',
              iconUrl: (u.icon_url as string | undefined) ?? null,
            }))
            .sort(byName),
        );
      } catch {
        setAssigneeOptions([]);
        setAssigneeDisplayOptions([]);
      }
    })();
  }, [
    user,
    isLoading,
    effectiveCompanyId,
    resultShareScope,
    resultSharedGroupIds,
    resultSharedUserIds,
    resultOwnerUserId,
  ]);

  // 担当者変更成功時のハンドラ（#2642）
  const handleAssigneeChangeSuccess = (): void => {
    setSnackbarText(assigneeLabels.changeSuccess);
    void fetchData(false);
  };
  const handleAssigneeChangeError = (message: string): void => {
    setErrorText(message);
  };

  useEffect(() => {
    if (location.state && location.state.recentResults) {
      // ホーム画面からの遷移：最近の処理履歴リストを保持
      setRecentResultsList(location.state.recentResults as RecentResultNavItem[]);
      setNavigationResultIds(null);
      setSearchData({
        fileName: null,
        responsiblePerson: null,
        assignee: null,
        startDate: null,
        endDate: null,
      });
      setOcrItemKeywords({});
    } else if (location.state && location.state.searchData) {
      // 文字読み取り一覧画面からの遷移：検索条件を保持
      const searchData = location.state.searchData;
      setRecentResultsList(null);
      // 一覧の表示順（resultId 配列）も受け取る。あれば前後判定に使用する
      const navIds = location.state.navigationResultIds;
      setNavigationResultIds(Array.isArray(navIds) ? (navIds as string[]) : null);
      setSearchData({
        ...searchData,
        startDate: searchData.startDate ? dayjs(searchData.startDate) : null,
        endDate: searchData.endDate ? dayjs(searchData.endDate) : null,
      });
      setOcrItemKeywords(normalizeOcrItemKeywords(location.state.ocrItemKeywords));
    } else {
      setRecentResultsList(null);
      setNavigationResultIds(null);
      setSearchData({
        fileName: null,
        responsiblePerson: null,
        assignee: null,
        startDate: null,
        endDate: null,
      });
      setOcrItemKeywords({});
    }
  }, [location.state]);

  // ホーム画面からの遷移時：最近の処理履歴リストから前後のIDを取得
  const getPrevNextFromRecentResults = (currentResultId: string): void => {
    if (!recentResultsList) return;

    const currentIndex = recentResultsList.findIndex(item => item.id === currentResultId);
    if (currentIndex === -1) {
      setNextResultId(null);
      setPrevResultId(null);
      setNextPatternId(null);
      setPrevPatternId(null);
      return;
    }

    // リストは created_at 降順なので、次（下）はindex+1、前（上）はindex-1
    const prevItem = currentIndex > 0 ? recentResultsList[currentIndex - 1] : null;
    const nextItem =
      currentIndex < recentResultsList.length - 1 ? recentResultsList[currentIndex + 1] : null;

    setPrevResultId(prevItem?.id ?? null);
    setPrevPatternId(prevItem?.patternId ?? null);
    setNextResultId(nextItem?.id ?? null);
    setNextPatternId(nextItem?.patternId ?? null);
  };

  // 一覧画面からの遷移時：表示順の resultId 配列から前後のIDを取得
  // 一覧の並び順（Algolia の relevance/フリーワード・ファイル名・OCR項目検索を含む並び）を
  // そのまま「次へ/前へ」に反映するため、ナビゲーション側で再検索しない
  const getPrevNextFromNavigationIds = (currentResultId: string): void => {
    if (!navigationResultIds || navigationResultIds.length === 0) return;

    const currentIndex = navigationResultIds.findIndex(id => id === currentResultId);
    if (currentIndex === -1) {
      setNextResultId(null);
      setPrevResultId(null);
      return;
    }

    const prevId = currentIndex > 0 ? navigationResultIds[currentIndex - 1] : null;
    const nextId =
      currentIndex < navigationResultIds.length - 1 ? navigationResultIds[currentIndex + 1] : null;

    setPrevResultId(prevId);
    setNextResultId(nextId);
  };

  // 前後のレコードIDを取得する関数
  const getPrevNextResultId = async (currentDocSnap: DocumentSnapshot): Promise<void> => {
    if (!patternId) return;

    const result = currentDocSnap.data();
    const status = result?.processing_status;

    // API用にWatchSearchItemsType形式で渡す
    const apiSearchConditions: WatchSearchItemsType = {
      fileName: searchData?.fileName || null,
      responsiblePerson: searchData?.responsiblePerson || null,
      assignee: searchData?.assignee || null,
      startDate: searchData?.startDate || null,
      endDate: searchData?.endDate || null,
      freeWord: searchData?.freeWord || null,
    };

    try {
      // 次のresultIdを取得
      const { nextResultId, prevResultId } = await getPatternResultsPaginatedNonRealtime(
        patternId,
        status,
        currentDocSnap,
        apiSearchConditions,
        effectiveCompanyId || user?.company_id,
      );

      setNextResultId(nextResultId ?? null);
      setPrevResultId(prevResultId ?? null);
    } catch (error) {
      setNextResultId(null);
      setPrevResultId(null);
    }
  };

  // result_dataからフォームデータ（動的フィールド＋単一値フィールド）を構築・適用する
  const applyResultDataToForm = (
    resultData: Record<string, unknown> | undefined,
    itemList: ItemListType[] | undefined,
  ): void => {
    if (!itemList) return;

    // 動的フィールド（配列型）の構築
    const dynamicData: DynamicFormData = {};
    itemList
      .filter((itemGroup: ItemListType) => itemGroup.is_active === true)
      .forEach((itemGroup: ItemListType) => {
        const groupKey = itemGroup.parentValue;

        if (itemGroup.type === 'array' || itemGroup.children.length > 0) {
          dynamicData[groupKey] = [];

          if (resultData) {
            const groupData = resultData[itemGroup.id] || resultData[groupKey];

            if (Array.isArray(groupData)) {
              groupData.forEach((item: Record<string, unknown>, index: number) => {
                const fieldData: DynamicFieldValue = { index };

                itemGroup.children
                  .filter((childField: ItemListChildType) => childField.is_active === true)
                  .forEach((childField: ItemListChildType) => {
                    const fieldKey = childField.childValue;
                    const itemFieldValue = (item[childField.id] || item[fieldKey]) as
                      | { value?: unknown }
                      | unknown;
                    let value =
                      typeof itemFieldValue === 'object' &&
                      itemFieldValue !== null &&
                      'value' in itemFieldValue
                        ? (itemFieldValue.value as string | number | Date | null)
                        : (itemFieldValue as string | number | Date | null);

                    if (value === null || value === undefined) {
                      if (childField.type === 'number') {
                        value = null;
                      } else if (childField.type === 'date') {
                        value = null;
                      } else {
                        value = '';
                      }
                    } else if (childField.type === 'number') {
                      const numVal =
                        typeof value === 'string'
                          ? parseFloat(String(value).replace(/,/g, ''))
                          : Number(value);
                      value = isNaN(numVal) ? value : numVal.toLocaleString();
                    } else if (childField.type === 'date') {
                      if (typeof value === 'string') {
                        const parsed = parseDateString(value);
                        value = parsed;
                      } else if (value && typeof value === 'object' && 'seconds' in value) {
                        value = new Date((value as { seconds: number }).seconds * 1000);
                      }
                    }
                    fieldData[fieldKey] = value;
                  });

                dynamicData[groupKey].push(fieldData);
              });
            }
          }
        }
      });

    setDynamicFormData(dynamicData);
    // 保存済みの明細を控える（#2967）。明細は react-hook-form の管理外で
    // isDirty に載らないため、未保存かどうかを自前で判定するのに使う
    savedDynamicFormDataRef.current = JSON.stringify(dynamicData);

    let maxIndex = -1;
    Object.values(dynamicData).forEach(groupArray => {
      if (Array.isArray(groupArray)) {
        groupArray.forEach(item => {
          if (typeof item.index === 'number' && item.index > maxIndex) {
            maxIndex = item.index;
          }
        });
      }
    });
    setNextRowIdCounter(maxIndex + 1);

    // 単一値フィールドのフォームデータ構築
    const formData: Record<string, unknown> = {};
    itemList
      .filter((itemGroup: ItemListType) => itemGroup.is_active === true)
      .forEach((itemGroup: ItemListType) => {
        if (itemGroup.type !== 'array' && itemGroup.children.length === 0) {
          const fieldName = toFormFieldName(itemGroup.parentValue);
          if (resultData) {
            const fieldValue = resultData[itemGroup.id] || resultData[itemGroup.parentValue];

            if (fieldValue !== undefined) {
              let value =
                typeof fieldValue === 'object' && fieldValue !== null && 'value' in fieldValue
                  ? fieldValue.value
                  : fieldValue;

              if (value === null || value === undefined) {
                if (itemGroup.type === 'number') {
                  value = null;
                } else if (itemGroup.type === 'date') {
                  value = null;
                } else {
                  value = '';
                }
              } else if (itemGroup.type === 'number') {
                const numVal =
                  typeof value === 'string'
                    ? parseFloat(String(value).replace(/,/g, ''))
                    : Number(value);
                value = isNaN(numVal) ? value : numVal.toLocaleString();
              } else if (itemGroup.type === 'date') {
                if (typeof value === 'string') {
                  const parsed = parseDateString(value);
                  value = parsed;
                } else if (value && typeof value === 'object' && 'seconds' in value) {
                  value = new Date((value as { seconds: number }).seconds * 1000);
                }
              }
              formData[fieldName] = value;
            }
          }
        }
      });

    reset(formData);
    trigger();
    // 計算セル（#3172）の表示状態を保存済みデータの is_calculated / calculation_error から初期化
    setCalculationCellStates(buildCalculationCellStatesFromResultData(itemList, resultData));
    // 計算フィールドの現在値も保存済みデータで初期化する（#3358）。
    // 編集前（読み込み直後）から要注意・内訳を出すために必要
    setCalculatedValuesByItemId(() => {
      const initial = new Map<string, string>();
      itemList.forEach((item: ItemListType) => {
        // 計算フィールドに加え、「要注意にする選択肢」を持つ項目も対象にする（#3358）。
        // 読み取った選択肢は人が選び直すため、現在値で判定し直す必要がある
        const hasReviewChoice = (item.reviewChoiceValues?.length ?? 0) > 0;
        if (item.is_calculated !== true && !hasReviewChoice) return;
        const cell = resultData?.[item.id];
        if (!cell || typeof cell !== 'object' || Array.isArray(cell)) return;
        const value = (cell as { value?: unknown }).value;
        initial.set(item.id, value === null || value === undefined ? '' : String(value));
      });
      return initial;
    });

    // フォームデータ適用後、明細セル（array項目）も初期値で検証する（#2815）
    // setDynamicFormData 反映後の再レンダリングを待つためトリガー経由で実行
    setValidationRevalidateTrigger(prev => prev + 1);
  };

  const fetchData = async (isInitialLoad = true): Promise<void> => {
    if (!resultId || !patternId) return;

    // 該当なし（other）は実在する読取パターンが無く、このパターン結果画面では
    // 表示できない（getPattern が解決できず無限ローディングになる）。
    // メール取り込み等の導線から誤って遷移した場合の保険としてホームへ戻す（#3154）。
    if (patternId === PATTERN_ID_OTHER) {
      setErrorText('該当するパターンがありません');
      navigate('/home');
      return;
    }

    if (isInitialLoad) {
      setIsDataReady(false);
    }

    // resultレコードと読取パターンデータを取得。
    // 互いに独立した取得なので直列に待たず並列で投げる（#3275 初期表示の短縮）
    const [result, pattern] = await Promise.all([getResult(resultId), getPattern(patternId)]);
    setResultRecord(result);

    if (!pattern) {
      setErrorText('読取パターンが見つかりません');
      navigate('/pattern');
      return;
    }

    // 権限チェック: 運営スタッフ、または同じ会社のメンバー（作成者含む）のみアクセス可能
    const isOwner = pattern.created_by === user?.uid;
    const isSameCompany = user?.company_id && pattern.company_id === user.company_id;
    const isOperator = isOperatorStaff(user?.role);

    // 削除済み結果は運営スタッフのみアクセス可能
    if (result?.status === resultStatus.deleted && !isOperator) {
      if (recentResultsList) {
        navigate('/home');
      } else {
        navigate(`/ai-ocr/pattern/${patternId}`, {
          state: { searchData: serializeSearchData(), ocrItemKeywords },
        });
      }
      return;
    }

    // 削除済みパターンは運営スタッフのみアクセス可能
    if (pattern.status === patternStatus.deleted && !isOperator) {
      setErrorText('この読取パターンは削除されています');
      navigate('/pattern');
      return;
    }

    // 運営スタッフは全てアクセス可能（company_idチェック不要）
    // 企業管理者・一般メンバー共通: 作成者または同じ会社であること
    if (!isOperator && !isOwner && !isSameCompany) {
      setErrorText('この読取パターンにアクセスする権限がありません');
      navigate('/pattern');
      return;
    }

    setPatternData(pattern);

    // 処理ステータスを取得
    setIsProcessingStatus(result?.processing_status);

    // Pattern版：resultから直接データを取得（inputコレクション不要）
    // 作成者の名前とアイコンを取得（代理操作の場合は「事務局」とHi-Storyアイコンを表示）
    setCreateUser(getDisplayName(result?.user_name, result?.is_proxy));
    setIcon(result?.is_proxy ? HiStoryIcon : (result?.user_icon_url ?? ''));

    // オリジナルファイルのURLを取得
    const originalFile = result?.originalFiles?.[0];
    let originalFileUrl = originalFile?.file_url;
    if (originalFile && !originalFile.file_url) {
      originalFileUrl = await getFileUrl(originalFile);
    }
    setOriginalFileUrl(originalFileUrl || '');

    // ファイル名を取得
    const fileName = result?.originalFiles?.[0]?.file_name;
    setFileName(fileName);

    // 画像URL解決とOCR実行履歴の取得は互いに独立しているので並列で待つ。
    // 直列だと実行履歴の取得が全ページ画像の読み込み完了まで待たされる（#3275）
    const filesPromise =
      result?.files && result.files.length > 0
        ? // filesが存在する場合のみ更新（画像変換後のファイル情報）
          updateFilesWithUrls(result.files)
        : Promise.resolve();

    // OCR実行履歴を取得
    const executionsPromise = getOcrExecutions(resultId)
      .then(executions => {
        setOcrExecutions(executions);
        setSelectedExecutionIndex(-1); // 最新を選択
      })
      .catch(() => {
        // 実行履歴は補助情報なので、取得できなくても画面表示は継続する
      });

    // 前後のレコードIDの取得は検索データ設定後に実行

    // 動的フィールドデータの構築
    applyResultDataToForm(result?.result_data, pattern?.item_list);

    await Promise.all([filesPromise, executionsPromise]);

    // 全てのデータが設定されたことを示すフラグを立てる
    setIsDataReady(true);
  };

  useEffect(() => {
    if (isLoading) return;
    // 未認証が確定したらローディングを解除する（#3203）。
    // 解除せずに抜けると、useAuthRedirect のリダイレクトが走るまでの間
    // スピナーが残り続け、共有リンクを未ログインで開いた際に固まって見える。
    if (!user) {
      setIsDataReady(true);
      return;
    }
    // 代理モードの復元待ちの間は取得しない（#3184）。
    // 代理が確立する前に取得すると、他社データへのアクセスとなり権限エラーになる。
    if (isProxyRestorePending) return;

    void (async (): Promise<void> => {
      setLoading(true);
      try {
        await fetchData();
      } catch (error) {
        setErrorText(convertToUserFriendlyMessage(error));
        // 他社データへのアクセス等で権限エラーになった場合、この画面には何も表示できない。
        // 留まるとローディング表示のまま操作不能になるためホームへ退避させる（#3184）。
        navigate('/home', { replace: true });
      } finally {
        setLoading(false);
        // 早期return・例外のいずれで抜けてもローディング表示を解除する。
        // 解除しないと isDataReady が false のままスピナーが残り続ける（#3184）。
        setIsDataReady(true);
      }
    })();
  }, [resultId, patternId, user, isLoading, isProxyRestorePending]);

  // パターンに紐づくマスタ設定を取得し、search_item_id をキーにMapを構築
  // D-6: マスタ照合が企業設定で OFF（enable_master=false）の場合は、マッピングが
  // 残っていても照合UI（照合ボタン・背景色・ラベル）を出さないよう Map を空にする。
  // company.enable_master は ProxyLoginContext 経由で対象企業の値が反映される。
  useEffect(() => {
    if (!patternId || !effectiveCompanyId) return;
    void (async (): Promise<void> => {
      if (!company?.enable_master) {
        setMastersBySearchItemId(new Map());
        setMasterMappingTargetIds(new Set());
        return;
      }
      const masters = await getMastersByPatternId(effectiveCompanyId, patternId);
      const map = new Map<string, MasterWithMappingType>();
      const targetIds = new Set<string>();
      masters.forEach(m => {
        map.set(m.search_item_id, m);
        m.column_mappings?.forEach(mapping => targetIds.add(mapping.item_id));
      });
      setMastersBySearchItemId(map);
      setMasterMappingTargetIds(targetIds);
    })();
  }, [patternId, effectiveCompanyId, company?.enable_master]);

  /**
   * マスタ照合に関わる項目のID（#3314）。
   *
   * 照合の検索キー・絞り込みキー・紐づけ単位は、照合先のマスタを引くための
   * 入力であり、反映先は照合結果で値が上書きされる。いずれも取得ルールを
   * 直して精度を上げる対象ではないため、AI改善提案から外す。
   */
  const masterRelatedItemIds = useMemo(() => {
    const ids = new Set<string>();
    mastersBySearchItemId.forEach(master => {
      ids.add(master.search_item_id);
      if (master.scope_item_id) ids.add(master.scope_item_id);
      master.filter_keys?.forEach(key => {
        if (key.item_id) ids.add(key.item_id);
      });
    });
    // 照合結果の反映先。照合で値が入るため、こちらも提案の対象外にする
    masterMappingTargetIds.forEach(id => ids.add(id));
    return ids;
  }, [mastersBySearchItemId, masterMappingTargetIds]);

  // パターンに紐づく辞書設定を取得し、search_item_id をキーにMapを構築（マスタ照合と独立）。
  // 辞書変換が企業設定で OFF（enable_dictionary=false）の場合は、辞書・マッピングを
  // 削除しなくても結果画面から辞書変換 UI を消すため、空 Map にする（マスタの D-6 と同じ）。
  // company.enable_dictionary は ProxyLoginContext 経由で対象企業の値が反映される。
  useEffect(() => {
    if (!patternId || !effectiveCompanyId) return;
    if (!company?.enable_dictionary) {
      setDictionariesBySearchItemId(new Map());
      setDictionaryMappingTargetIds(new Set());
      return;
    }
    void (async (): Promise<void> => {
      const dictionaries = await getDictionariesByPatternId(effectiveCompanyId, patternId);
      const map = new Map<string, DictionaryWithMappingType>();
      const targetIds = new Set<string>();
      dictionaries.forEach(d => {
        map.set(d.search_item_id, d);
        d.column_mappings?.forEach(mapping => targetIds.add(mapping.item_id));
      });
      setDictionariesBySearchItemId(map);
      setDictionaryMappingTargetIds(targetIds);
    })();
  }, [patternId, effectiveCompanyId, company?.enable_dictionary]);

  // 値の出どころバッジ（#3130）。単一値項目・テーブル列ヘッダーで同じ判定・見た目を使う。
  // 1項目に複数の役割があってもバッジは1つだけ表示する（優先順位は resolveItemSourceBadge 参照）。
  const renderItemSourceBadge = (
    itemId: string,
    isOcrTarget?: boolean,
    fixedValue?: string,
    isCalculated?: boolean,
  ): React.ReactElement | null => {
    const badge = resolveItemSourceBadge({
      itemId,
      isOcrTarget,
      fixedValue,
      isCalculated,
      masterSearchItemIds: new Set(mastersBySearchItemId.keys()),
      dictionarySearchItemIds: new Set(dictionariesBySearchItemId.keys()),
      masterMappingTargetIds,
      dictionaryMappingTargetIds,
    });
    if (!badge) return null;
    return (
      <Typography component="span" sx={styles.itemSourceBadge}>
        {itemSourceBadgeLabels[badge]}
      </Typography>
    );
  };

  // 実行履歴の実行者アイコンを取得
  useEffect(() => {
    if (ocrExecutions.length === 0) return;
    const uniqueUserIds = Array.from(
      new Set(ocrExecutions.map(e => e.executed_by).filter(Boolean)),
    );
    if (uniqueUserIds.length === 0) return;

    void (async (): Promise<void> => {
      const iconMap: Record<string, string> = {};
      await Promise.all(
        uniqueUserIds.map(async uid => {
          const userData = await getUser(uid);
          if (userData?.icon_url) {
            iconMap[uid] = userData.icon_url;
          }
        }),
      );
      setExecutionUserIcons(iconMap);
    })();
  }, [ocrExecutions]);

  // ナビゲーションIDを取得（ホーム画面からの遷移 or 一覧画面からの遷移で分岐）
  useEffect(() => {
    if (!resultId) return;

    // location.stateの処理がまだ完了していない場合はスキップ
    if (recentResultsList === undefined) return;

    // ホーム画面からの遷移：最近の処理履歴リストから前後を算出
    if (recentResultsList) {
      getPrevNextFromRecentResults(resultId);
      return;
    }

    // 一覧画面からの遷移：一覧の表示順をそのまま使って前後を算出（並び順を一覧と一致させる）
    if (navigationResultIds) {
      getPrevNextFromNavigationIds(resultId);
      return;
    }

    // URL直接アクセスなど navigationResultIds がない場合のみ：Firestoreクエリで前後を取得
    if (!searchData) return;

    const setupNavigation = async (): Promise<void> => {
      try {
        const currentDocRef = doc(db, 'results', resultId);
        const currentDocSnap = await getDoc(currentDocRef);

        if (currentDocSnap.exists()) {
          await getPrevNextResultId(currentDocSnap);
        }
      } catch (error) {
        // エラーは静かに処理
      }
    };

    void setupNavigation();
  }, [resultId, searchData, recentResultsList, navigationResultIds]);

  /**
   * 読み取り精度（OCR値 vs 最終保存値の文字数一致率）を算出して state に入れる（#3354）。
   *
   * 比較の起点は「最新の全項目実行」を使う（#3318）。再読み取りは result_data を
   * 上書きするが CREATE 履歴は更新しないため、CREATE 履歴を基準にすると
   * 人が直していない再読み取り分の変化まで不一致として計上され精度が低く出る。
   *
   * 実行ログが無い過去データのみ CREATE 履歴にフォールバックする。
   * CREATE 履歴の changed_fields は pagePerResult モードでは空になり
   * （OCR 結果は後続の EDIT 履歴に入る）、これを前提にすると精度が出ないため、
   * 精度の算出は CREATE 履歴の有無に依存させない。
   */
  const calcAndSetOcrAccuracy = async (
    currentResultData: Record<string, unknown> | undefined,
  ): Promise<void> => {
    if (!resultId || !patternData || !currentResultData) {
      setOcrAccuracy(null);
      return;
    }
    try {
      const executions = await getOcrExecutions(resultId, OCR_EXECUTION_FETCH_LIMIT).catch(
        () => [],
      );
      let baseData = selectBaselineResultData(
        executions.map(execution => ({
          execution_type: execution.execution_type,
          result_data: execution.result_data,
          executed_at_ms: execution.executed_at.getTime(),
        })),
      );

      // 実行ログが無い過去データは CREATE 履歴の初回OCR値にフォールバックする
      if (!baseData) {
        const createHistory = await getCreateHistory(resultId);
        baseData = ((createHistory?.changed_fields as Record<string, unknown>)?.result_data ??
          null) as Record<string, unknown> | null;
      }
      if (!baseData) {
        setOcrAccuracy(null);
        return;
      }

      // 正規化は accuracyCalc 内の専用ロジックを使う（変更検知用 normalizeFieldValue は
      // 数字列を日付に誤変換するため精度計算には使わない）。
      setOcrAccuracy(calcResultAccuracy(baseData, currentResultData, patternData.item_list));
    } catch {
      setOcrAccuracy(null);
    }
  };

  // 初回OCR値から変化があるかを確認（AIボタンの有効/無効に使用）
  const checkEditHistory = async (): Promise<void> => {
    if (!resultId || !patternData) return;
    try {
      const currentResultData = resultRecord?.result_data as Record<string, unknown> | undefined;

      // 読み取り精度（OCR値 vs 最終保存値の文字数一致率）を先に算出する（#3354）。
      // 精度の比較起点は ocr_executions（#3318）なので CREATE 履歴には依存しない。
      // pagePerResult モードでは CREATE 履歴の changed_fields が空で、OCR 結果は
      // 後続の EDIT 履歴に入る。CREATE 履歴を前提にすると精度が出ない（#3354）。
      await calcAndSetOcrAccuracy(currentResultData);

      const createHistory = await getCreateHistory(resultId);
      if (!createHistory) {
        setHasEditHistory(false);
        return;
      }
      const initialResultData = ((createHistory.changed_fields as Record<string, unknown>)
        ?.result_data ?? null) as Record<string, unknown> | null;
      if (!initialResultData || !currentResultData) {
        setHasEditHistory(false);
        return;
      }
      const normalizeValue = (val: unknown): string => {
        if (val === null || val === undefined) return '';
        if (typeof val === 'object' && 'seconds' in (val as object)) {
          return dayjs(new Date((val as { seconds: number }).seconds * 1000)).format('YYYY/MM/DD');
        }
        if (typeof val === 'string') {
          const parsed = parseDateString(val);
          if (parsed) return dayjs(parsed).format('YYYY/MM/DD');
          // 数値として解釈できる場合はparseFloatで正規化（2630.0 と 2630 を同一視、26.30 と 2630 は別扱い）
          const numVal = parseFloat(val.replace(/,/g, ''));
          if (!isNaN(numVal) && /^-?[\d,]+\.?\d*$/.test(val.trim())) {
            return String(numVal);
          }
        }
        if (typeof val === 'number') return String(val);
        return String(val);
      };

      const normalizeFieldValue = (fieldData: unknown): string => {
        if (fieldData === null || fieldData === undefined) return '';
        if (typeof fieldData === 'object' && 'value' in (fieldData as object)) {
          return normalizeValue((fieldData as Record<string, unknown>).value);
        }
        return normalizeValue(fieldData);
      };

      const hasChanges = patternData.item_list.some(item => {
        const isArrayField = item.children && item.children.length > 0;
        if (isArrayField) {
          const initialRows =
            (initialResultData[item.id] as Array<Record<string, unknown>> | undefined) ?? [];
          const currentRows =
            (currentResultData[item.id] as Array<Record<string, unknown>> | undefined) ?? [];
          if (initialRows.length !== currentRows.length) return true;
          return (item.children ?? []).some(child =>
            initialRows.some(
              (row, i) =>
                normalizeFieldValue(row[child.id]) !==
                normalizeFieldValue(currentRows[i]?.[child.id]),
            ),
          );
        }
        const initialVal = normalizeFieldValue(initialResultData[item.id]);
        const currentVal = normalizeFieldValue(currentResultData[item.id]);
        return initialVal !== currentVal;
      });
      setHasEditHistory(hasChanges);
    } catch {
      setHasEditHistory(false);
    }
  };

  useEffect(() => {
    void checkEditHistory();
  }, [resultId, patternData, resultRecord]);

  /**
   * 日付範囲（#3357）の基準日。OCR を実行した日（結果の created_at）を使う。
   * 閲覧日にすると、サーバで判定・保存される要注意と画面表示が食い違うため。
   * 結果がまだ読めていない場合は undefined（日付範囲は判定しない）
   */
  // ⚠️ useMemo で参照を固定する。毎レンダーで新しい Date を作ると
  // validateArrayField（useCallback）が作り直され、DetailTableRow の React.memo が
  // 効かなくなって明細の全行が再描画される（#3275 と同じ問題）
  const createdAtSeconds = resultRecord?.created_at?.seconds;

  const dateRangeBaseDate = useMemo(
    () => (createdAtSeconds !== undefined ? new Date(createdAtSeconds * 1000) : undefined),
    [createdAtSeconds],
  );

  // 読取パターンフィールド用の動的バリデーションスキーマを作成
  const createDynamicSchema = useCallback((): yup.ObjectSchema<Record<string, unknown>> => {
    const patternSchema = createPatternResultSchema(patternData, dateRangeBaseDate);

    // 固定フィールドと動的フィールドを結合
    const baseSchema = aiOcrResultMainInfoSchema.shape;
    return yup.object().shape({
      ...baseSchema,
      ...(patternSchema as yup.ObjectSchema<Record<string, unknown>>).fields,
    });
  }, [patternData, dateRangeBaseDate]);

  /**
   * 最新のスキーマを保持する ref（#3357）。
   *
   * useForm の resolver は初期化時に1度だけ評価されるため、
   * 結果画面の項目設定モーダルで制約を変えても古いスキーマのまま検証され、
   * その場では新しい制約が効かなかった（保存して開き直すまで反映されない）。
   * resolver から ref 経由で参照することで、trigger() のたびに最新の制約で検証する。
   */
  const dynamicSchemaRef = useRef(createDynamicSchema());
  useEffect(() => {
    dynamicSchemaRef.current = createDynamicSchema();
  }, [createDynamicSchema]);

  const {
    control,
    watch,
    reset,
    setValue,
    getValues,
    trigger,
    formState: { errors, isDirty },
  } = useForm<Record<string, unknown>>({
    // ref 経由で毎回最新のスキーマを使う（項目設定の変更をその場で反映するため）
    resolver: async (values, context, options) =>
      yupResolver(dynamicSchemaRef.current)(values, context, options),
    mode: 'all',
    reValidateMode: 'onChange',
  });

  /**
   * 保存されていない編集があるか（#2967）。
   *
   * **isDirty だけでは足りない。** 明細（array項目）は dynamicFormData という
   * 別の state で持っており、react-hook-form の管理外のため isDirty に載らない。
   * 明細だけを直した場合に未保存と判定されず、保存前のままAI提案を実行できてしまう。
   */
  const hasUnsavedResultEdits =
    isDirty || JSON.stringify(dynamicFormData) !== savedDynamicFormDataRef.current;

  /**
   * 現在値のバリデーションエラー（#3358）。サマリーに流す。
   *
   * 既存の検証結果を詰め替えるだけにする。判定を別に持つと、
   * 入力欄の下の赤字とサマリーで食い違うため。
   * - 単一項目: yup の errors
   * - 明細セル: arrayFieldErrors（validateArrayField の結果。行IDがキー）
   *
   * キーは `親項目名-行ID-子項目名` だが、項目名に "-" が含まれると
   * 分解できないため、item_list 側からキーを組み立てて引き当てる。
   */
  const liveValidationIssues = useMemo(() => {
    const itemList = patternData?.item_list ?? [];

    const singleErrors = new Map<string, string>();
    const cellErrors: LiveValidationCellError[] = [];

    itemList.forEach((item: ItemListType) => {
      if (item.is_active !== true) return;

      if (item.type === 'array' || (item.children?.length ?? 0) > 0) {
        const rows = dynamicFormData[item.parentValue];
        if (!Array.isArray(rows)) return;
        rows.forEach((row, rowIndex) => {
          const rowId = getRowId(row, rowIndex);
          item.children
            ?.filter((child: ItemListChildType) => child.is_active === true)
            .forEach((child: ItemListChildType) => {
              const message =
                arrayFieldErrors[arrayFieldErrorKey(item.parentValue, rowId, child.childValue)];
              if (!message) return;
              cellErrors.push({
                parentId: item.id,
                childId: child.id,
                rowIndex: rowId,
                label: child.childValue,
                message,
              });
            });
        });
        return;
      }

      const message = errors[toFormFieldName(item.parentValue)]?.message;
      if (typeof message === 'string' && message !== '') singleErrors.set(item.id, message);
    });

    return buildLiveValidationIssues(itemList, singleErrors, cellErrors);
  }, [patternData, dynamicFormData, arrayFieldErrors, errors]);

  /**
   * サマリー・バッジに使う要注意（#3358）。
   *
   * 計算フィールドの「要注意にする選択肢」だけは保存値ではなく**現在値**で判定する。
   * 金額を直して「一致」に変わったらサマリーからも消え、「不一致」に変わったら出る。
   * セル下部の赤字と同じ判定結果（buildLiveReviewChoiceIssues）を使うので、
   * 「セルには出ているのにサマリーに無い」という食い違いが起きない。
   *
   * 他のバリデーション（必須・文字数など）は従来どおり保存値のまま。
   * yup 側のライブ判定と出所が二重になるため、統一は別途対応とする。
   */
  const reviewIssues = useMemo(() => {
    const itemList = patternData?.item_list ?? [];
    return [
      // バリデーション由来は保存値を捨てて現在値で作り直す（#3358）。
      // 入力を直したらその場でサマリーからも消える
      ...withoutStaleValidationIssues(withoutStaleReviewChoices(savedReviewIssues, itemList)),
      ...buildLiveReviewChoiceIssues(itemList, calculatedValuesByItemId, calculatedRowValues),
      ...liveValidationIssues,
    ];
  }, [
    savedReviewIssues,
    patternData,
    calculatedValuesByItemId,
    calculatedRowValues,
    liveValidationIssues,
  ]);

  // 確認ダイアログの「要注意の項目が n 件」は、フォームの現在値で数え直す
  // （#3309 / countReviewItemsForConfirm）。ここの判定は保存済みの result_data
  // を見るため、入力中の値はまだ反映されていない
  const showReviewIssues =
    reviewIssues.length > 0 &&
    (isProcessingStatus === processingStatus.draft ||
      isProcessingStatus === processingStatus.processing);
  /**
   * 項目ごとの要注意を引くためのマップ（path → その項目の理由一覧）。
   * 単一項目のバッジで使う。バリデーション由来も含める。
   *
   * 単一項目のバリデーションエラーは入力欄の下に yup が出し、要注意バッジは
   * 項目名の横に出るため、位置が違って重複にならない。
   */
  const reviewIssuesByPath = useMemo(() => {
    const map = new Map<string, ReviewIssue[]>();
    if (!showReviewIssues) return map;
    for (const issue of reviewIssues) {
      const existing = map.get(issue.path);
      if (existing) {
        existing.push(issue);
      } else {
        map.set(issue.path, [issue]);
      }
    }
    return map;
  }, [reviewIssues, showReviewIssues]);

  /**
   * 再照合で選び直して、もう要注意にする必要が無くなった項目のパス（#3309）。
   *
   * 照合由来の要注意は result_data の match_reason / score から判定するため、
   * 再照合しても保存するまで消えない。選び直した直後に古い「マスタ一致度 低」が
   * 残っていると、直したのに消えないように見える。
   *
   * 選択内容は Firestore ではなく selectedMasterIds（UI状態）に持つため、
   * ここも表示だけを抑える。保存しなければリロードで元に戻る（#3078 と同じ方式）。
   *
   * 選んだら無条件に消すのではなく、新しいスコアが閾値以上のときだけ消す。
   * 低いスコアの候補を選んだ場合に消えてしまうと、かえって見落とすため。
   */
  const resolvedMatchingPaths = useMemo(() => {
    const paths = new Set<string>();
    const itemList = patternData?.item_list;
    if (!itemList) return paths;

    const thresholds = new Map<string, number>();
    mastersBySearchItemId.forEach((master, searchItemId) => {
      if (typeof master.review_threshold === 'number') {
        thresholds.set(searchItemId, master.review_threshold);
      }
    });

    selectedMasterIds.forEach(selected => {
      const itemId = selected.search_item_id;
      if (!itemId) return;

      const score = parseScore(selected.score);
      const threshold = thresholds.get(itemId) ?? MASTER_MATCH_REVIEW_THRESHOLD;
      // スコアが取れない／閾値未満なら要注意のままにする
      if (score === null || score < threshold) return;

      // 明細の列は `親ID[行index].子ID`、単一項目は項目IDがパスになる
      const parent = itemList.find((item: ItemListType) =>
        item.children?.some((child: ItemListChildType) => child.id === itemId),
      );
      paths.add(parent ? `${parent.id}[${selected.row_index ?? 0}].${itemId}` : itemId);
    });

    return paths;
  }, [selectedMasterIds, mastersBySearchItemId, patternData]);

  /**
   * 項目・セルの表示に使う要注意マップ。**バリデーション由来は除く**。
   *
   * バリデーション（必須・文字数・数値範囲など）は、入力に応じて
   * リアルタイムに再検証される仕組み（react-hook-form / arrayFieldErrors）が
   * すでにあり、そちらが同じ内容を出している。
   *
   * 一方こちらは表示時に result_data から判定した結果で、入力中の値は
   * まだ反映されていない（#3328）。両方出すと、値を直しても要注意側が
   * 消えず「エラーが消えない」ように見えてしまう。
   *
   * 確信度・照合スコア由来は入力で変わる性質のものではないため、
   * そのまま出してよい。
   *
   * なお一覧バッジとサマリーには validation 由来も含める（上記 reviewIssuesByPath）。
   */
  const cellReviewIssuesByPath = useMemo(() => {
    const map = new Map<string, ReviewIssue[]>();
    reviewIssuesByPath.forEach((issues, path) => {
      const nonValidation = issues.filter(
        (issue: ReviewIssue) =>
          // バリデーション由来は原則として出さない（同じ内容を yup 側が出すため）。
          // ただし「要注意にする選択肢」（#3357）は yup に相当する検証が無い。
          // エラー（直すべき入力）ではなく「人が見た方がよい」印であり、
          // 赤字の入力エラーとして出すのは意味が合わないため yup には足していない。
          // 除外したままだとセルに何の印も出ないので、ここだけ通す
          (issue.source !== reviewIssueSource.validation ||
            issue.code === reviewIssueCode.reviewChoice) &&
          // 再照合で解消した照合由来は出さない（#3309）
          !(issue.source === reviewIssueSource.matching && resolvedMatchingPaths.has(path)),
      );
      if (nonValidation.length > 0) map.set(path, nonValidation);
    });
    return map;
  }, [reviewIssuesByPath, resolvedMatchingPaths]);

  /**
   * 計算フィールドの「計算に使った値」を項目IDごとに持つ（#3358）。
   *
   * IF(SUM({明細.金額}) = {合計金額}, "OK", "不一致") で「不一致」と出ても、
   * 式を読まないと**なぜ**不一致なのか分からない。参照した項目と値を
   * セルの下に添えて、確認の手がかりにする。
   */
  const calculationBreakdownByItemId = useMemo(() => {
    const map = new Map<string, string>();
    const itemList = patternData?.item_list;
    const resultData = resultRecord?.result_data as Record<string, unknown> | undefined;
    if (!itemList || !resultData) return map;

    // フィールドコード → 項目（明細の列は parentId を持たせる）
    const breakdownItems: BreakdownItem[] = [];
    itemList.forEach((item: ItemListType) => {
      if (item.field_code) {
        breakdownItems.push({ fieldCode: item.field_code, itemName: item.parentValue });
      }
      item.children?.forEach((child: ItemListChildType) => {
        if (child.field_code) {
          breakdownItems.push({
            fieldCode: child.field_code,
            itemName: child.childValue,
            parentId: item.id,
          });
        }
      });
    });

    const cellValue = (cell: unknown): string => {
      if (!cell || typeof cell !== 'object' || Array.isArray(cell)) return '';
      const value = (cell as { value?: unknown }).value;
      return value === null || value === undefined ? '' : String(value);
    };
    const byCode = new Map(breakdownItems.map(i => [i.fieldCode, i]));
    const itemIdByCode = new Map<string, string>();
    itemList.forEach((item: ItemListType) => {
      if (item.field_code) itemIdByCode.set(item.field_code, item.id);
      item.children?.forEach((child: ItemListChildType) => {
        if (child.field_code) itemIdByCode.set(child.field_code, child.id);
      });
    });

    // 画面で編集中の値を優先して読む（#3358）。
    // result_data は OCR 時点のスナップショットなので、金額を直しても
    // 内訳が古い値のままになり「直したのに一致しない」ように見えてしまう。
    // 単一項目はフォーム、明細は dynamicFormData（どちらも表示名がキー）を見る
    const liveSingleValue = (itemId: string): string | undefined => {
      const item = itemList.find((i: ItemListType) => i.id === itemId);
      if (!item) return undefined;
      const value = getValues(toFormFieldName(item.parentValue));
      return value === null || value === undefined ? undefined : String(value);
    };

    const source: BreakdownValueSource = {
      getSingleValue: code => {
        const itemId = itemIdByCode.get(code);
        if (!itemId) return undefined;
        return liveSingleValue(itemId) ?? cellValue(resultData[itemId]);
      },
      getColumnValues: code => {
        const item = byCode.get(code);
        const childId = itemIdByCode.get(code);
        if (!item?.parentId || !childId) return undefined;
        const parent = itemList.find((i: ItemListType) => i.id === item.parentId);
        const child = parent?.children?.find((c: ItemListChildType) => c.id === childId);
        // 明細は表示名キーの dynamicFormData に編集中の値が入る
        const liveRows = parent ? dynamicFormData[parent.parentValue] : undefined;
        if (liveRows !== undefined && child) {
          return liveRows.map(row => {
            const value = row[child.childValue];
            return value === null || value === undefined ? '' : String(value);
          });
        }
        const rows = resultData[item.parentId];
        if (!Array.isArray(rows)) return undefined;
        return rows.map(row =>
          row && typeof row === 'object' && !Array.isArray(row)
            ? cellValue((row as Record<string, unknown>)[childId])
            : '',
        );
      },
    };
    itemList.forEach((item: ItemListType) => {
      if (item.is_calculated !== true || !item.formula) return;
      // 四則演算の式は、項目を値に置き換えた式の形で出す（#3412）
      const arithmetic = formatArithmeticBreakdown(item.formula, breakdownItems, source);
      if (arithmetic !== undefined) {
        map.set(item.id, arithmetic);
        return;
      }
      const entries = buildCalculationBreakdown(
        item.formula,
        extractFieldCodes(item.formula),
        breakdownItems,
        source,
      );
      if (entries.length > 0) {
        map.set(item.id, formatCalculationBreakdown(entries, extractComparisons(item.formula)));
      }
    });
    return map;
    // calculatedValuesByItemId は再計算のたびに更新される state なので、
    // これを依存に置くと再計算後の再レンダリングで内訳も作り直される。
    // （getValues 自体は値の変化を通知しないため、依存に入れても再評価されない）
  }, [patternData, resultRecord, dynamicFormData, calculatedValuesByItemId, getValues]);

  // 配列セルのバリデーション（明細行の子項目）。制約（#2815）も反映する
  // 外部stateに依存しないため useCallback で安定化し、再検証 effect の依存を安定させる
  const validateArrayField = useCallback(
    (
      parentValue: string,
      itemIndex: number,
      childValue: string,
      value: string | number | Date | null,
      child: ItemListChildType,
    ): string | undefined => {
      const childType = child.type;
      const strValue = value !== null && value !== undefined ? String(value) : '';

      // 「0 を空欄として扱う」（#3262）が ON の列では 0 も未入力とみなす。
      // 要注意判定（utils/pattern/reviewIssues.ts の isEmptyValue）と揃える。
      // 明細セルはバリデーション由来の要注意を表示しない（cellReviewIssuesByPath で
      // 除外し、代わりにこの赤字で伝える）方針のため、ここが 0 を見落とすと
      // 要注意は立っているのに画面には何も出ない状態になる（#3309）
      const isZeroAsEmpty =
        child.treatZeroAsEmpty === true &&
        strValue !== '' &&
        Number(strValue.replace(/,/g, '')) === 0 &&
        Number.isFinite(Number(strValue.replace(/,/g, '')));

      // 必須チェック（空値時に発火）
      if (!strValue || isZeroAsEmpty) {
        if (child.required) return itemValidationResultMessage.required;
        return undefined; // 空値かつ必須でなければバリデーションなし
      }

      if (childType === 'textarea') {
        if (strValue.length > 5000) return '5000文字以内で入力してください';
      } else if (childType === 'number') {
        const stripped = strValue.replace(/,/g, '');
        if (stripped !== '' && isNaN(Number(stripped))) return '数値を入力してください';
        // 数値範囲制約
        const num = Number(stripped);
        if (!isNaN(num)) {
          if (child.minValue !== undefined && num < child.minValue) {
            return itemValidationResultMessage.minValue(child.minValue);
          }
          if (child.maxValue !== undefined && num > child.maxValue) {
            return itemValidationResultMessage.maxValue(child.maxValue);
          }
        }
      } else if (childType !== 'date' && !isChoiceDataType(childType)) {
        // string型。選択肢型（#3075）は値が候補で規定されるため文字数制約の対象外
        if (strValue.length > 250) return '250文字以内で入力してください';
      }

      // 文字数制約（string / textarea のみ。日付は値が Date のため文字数対象外＝必須のみ）
      if (childType === 'string' || childType === 'textarea') {
        if (child.minLength !== undefined && strValue.length < child.minLength) {
          return itemValidationResultMessage.minLength(child.minLength);
        }
        if (child.maxLength !== undefined && strValue.length > child.maxLength) {
          return itemValidationResultMessage.maxLength(child.maxLength);
        }
        // 文字種・入力パターン（#3357）
        if (child.charType !== undefined && !matchesCharType(strValue, child.charType)) {
          return itemValidationResultMessage.charType(child.charType);
        }
        if (!matchesPattern(strValue, child.pattern)) {
          return child.patternMessage !== undefined && child.patternMessage !== ''
            ? child.patternMessage
            : itemValidationResultMessage.pattern;
        }
      }

      // 日付範囲（#3357）。基準日は OCR を実行した日（結果の created_at）
      if (childType === 'date' && dateRangeBaseDate !== undefined) {
        const dateRule: DateRangeRule = {
          noPast: child.dateNoPast,
          noFuture: child.dateNoFuture,
          pastLimit: child.datePastLimit,
          pastUnit: child.datePastUnit,
          futureLimit: child.dateFutureLimit,
          futureUnit: child.dateFutureUnit,
        };
        const violation = checkDateRange(value, dateRule, dateRangeBaseDate);
        if (violation !== null) {
          return dateRangeMessage(violation, dateRule);
        }
      }

      return undefined;
    },
    [dateRangeBaseDate],
  );

  // DetailTableRow の React.memo を効かせるため参照を固定する（#3275）。
  // setter しか使わないので依存は空でよい
  const updateArrayFieldError = useCallback(
    (
      parentValue: string,
      itemIndex: number,
      childValue: string,
      errorMessage: string | undefined,
    ): void => {
      // itemIndex には行ID が入る（#3283）
      const key = arrayFieldErrorKey(parentValue, itemIndex, childValue);
      setArrayFieldErrors(prev => {
        const next = { ...prev };
        if (errorMessage) {
          next[key] = errorMessage;
        } else {
          delete next[key];
        }
        return next;
      });
    },
    [],
  );

  // 明細セル（array項目）を現在値で再検証し、エラーがあるか判定する（#2815）
  // arrayFieldErrors は入力変更時のみ更新されるため、操作時には初期値の制約違反も検出する
  const hasArrayValidationError = (): boolean => {
    if (!patternData?.item_list) return false;
    return patternData.item_list.some((item: ItemListType) => {
      if (item.is_active !== true || item.type !== 'array' || !item.children?.length) return false;
      const rows = dynamicFormData[item.parentValue];
      if (!Array.isArray(rows)) return false;
      return rows.some((row, rowIndex) =>
        // 非アクティブな子項目はフォーム・表示に存在しないため、バリデーション対象外
        item.children
          .filter((child: ItemListChildType) => child.is_active === true)
          .some((child: ItemListChildType) => {
            const value = (row as Record<string, unknown>)?.[child.childValue];
            const err = validateArrayField(
              item.parentValue,
              rowIndex,
              child.childValue,
              value as string | number | Date | null,
              child,
            );
            return err !== undefined;
          }),
      );
    });
  };

  /**
   * 削除した明細行に紐づく画面状態を破棄する（#3283）。
   *
   * キーは行ID ベースなので、行を削除しても他の行の状態はズレない。
   * ただし削除した行のキーは残り続けるため、ここで取り除く。
   * 特に arrayFieldErrors は残ると hasAnyValidationError() が真のままになり、
   * 画面に出ていないエラーで保存時の確認ダイアログが出てしまう。
   */
  const discardRowState = useCallback(
    (itemGroup: ItemListType, rowId: number): void => {
      const children = itemGroup.children ?? [];

      setArrayFieldErrors(prev => {
        const next = { ...prev };
        let changed = false;
        children.forEach((child: ItemListChildType) => {
          const key = arrayFieldErrorKey(itemGroup.parentValue, rowId, child.childValue);
          if (key in next) {
            delete next[key];
            changed = true;
          }
        });
        return changed ? next : prev;
      });

      setCalculationCellStates(prev => {
        const next = { ...prev };
        let changed = false;
        children.forEach((child: ItemListChildType) => {
          const key = arrayCalculationCellKey(itemGroup.id, rowId, child.id);
          if (key in next) {
            delete next[key];
            changed = true;
          }
        });
        return changed ? next : prev;
      });

      setClearedReasonItemKeys(prev => {
        if (prev.size === 0) return prev;
        const next = new Set(prev);
        children.forEach((child: ItemListChildType) => {
          next.delete(clearedReasonItemKey(child.id, rowId));
        });
        return next.size === prev.size ? prev : next;
      });

      // 照合・辞書の選択状態も、その行の分だけ取り除く
      const masterKeys = children
        .map((child: ItemListChildType) => mastersBySearchItemId.get(child.id))
        .filter((master): master is MasterWithMappingType => !!master)
        .map(master => masterSelectionKey(master.master_id, rowId));
      const dictionaryKeys = children
        .map((child: ItemListChildType) => dictionariesBySearchItemId.get(child.id))
        .filter((dictionary): dictionary is DictionaryWithMappingType => !!dictionary)
        .map(dictionary => dictionarySelectionKey(dictionary.dictionary_id, rowId));

      if (masterKeys.length > 0) {
        setSelectedMasterIds(prev => {
          const next = new Map(prev);
          masterKeys.forEach(key => next.delete(key));
          return next.size === prev.size ? prev : next;
        });
        setUnlinkedMasterKeys(prev => {
          if (prev.size === 0) return prev;
          const next = new Set(prev);
          masterKeys.forEach(key => next.delete(key));
          return next.size === prev.size ? prev : next;
        });
      }

      if (dictionaryKeys.length > 0) {
        setSelectedDictionaryHistories(prev => {
          const next = new Map(prev);
          dictionaryKeys.forEach(key => next.delete(key));
          return next.size === prev.size ? prev : next;
        });
      }
    },
    [mastersBySearchItemId, dictionariesBySearchItemId],
  );

  // 明細セル（array項目）を全走査し、現在値で arrayFieldErrors を再構築する（#2815）
  // 項目設定の保存直後など、入力以外の契機でバリデーションを反映するために使う
  const revalidateAllArrayFields = useCallback((): void => {
    if (!patternData?.item_list) return;
    const nextErrors: Record<string, string> = {};
    patternData.item_list.forEach((item: ItemListType) => {
      if (item.is_active !== true || item.type !== 'array' || !item.children?.length) return;
      const rows = dynamicFormData[item.parentValue];
      if (!Array.isArray(rows)) return;
      rows.forEach((row, rowIndex) => {
        // 非アクティブな子項目はフォーム・表示に存在しないため、バリデーション対象外
        item.children
          .filter((child: ItemListChildType) => child.is_active === true)
          .forEach((child: ItemListChildType) => {
            const value = (row as Record<string, unknown>)?.[child.childValue];
            const err = validateArrayField(
              item.parentValue,
              rowIndex,
              child.childValue,
              value as string | number | Date | null,
              child,
            );
            if (err) {
              // 行ID ベースでキーを組む（#3283）。行の挿入・削除でズレないようにする
              nextErrors[
                arrayFieldErrorKey(item.parentValue, getRowId(row, rowIndex), child.childValue)
              ] = err;
            }
          });
      });
    });
    setArrayFieldErrors(nextErrors);
  }, [patternData, dynamicFormData, validateArrayField]);

  /** 保存時の要注意の再判定に使う照合設定（#3309） */
  const buildReviewMatchSettings = (): ReviewMatchSettings =>
    buildReviewMatchSettingsFrom(mastersBySearchItemId, dictionariesBySearchItemId);

  /**
   * 確認ダイアログに出す要注意の件数を、画面の現在値で数える（#3309）。
   *
   * review_item_count は OCR 時点の件数が Firestore に保存されたもので、
   * 画面で値を直しても保存するまで変わらない。そのため入力エラーを解消しても
   * 「要注意の項目が 3 件あります」が減らず、何を直せばよいか分からなかった。
   *
   * ここでは系統ごとに出所を変える。
   *   - バリデーション由来: 画面の現在値で数え直す（直せば減り、壊せば増える）
   *   - 確信度・照合由来  : 保存済みの判定をそのまま使う。入力では変わらないため
   *
   * review_item_count と同じく「同じ項目に複数の理由があっても 1」と数えるため、
   * 項目パスの集合で持つ。パスの形は evaluateReviewIssues と揃える
   * （単一項目は itemId、明細セルは `親ID[行index].子ID`）。
   */
  const countReviewItemsForConfirm = (): number => {
    const paths = new Set<string>();

    // 確信度・照合由来（保存済み）。validation 由来はここでは数えない。
    // 再照合で解消した照合由来も除く（#3309）。セル表示と件数を一致させる
    reviewIssuesByPath.forEach((issues, path) => {
      const counted = issues.some(
        (issue: ReviewIssue) =>
          // 「要注意にする選択肢」（#3357/#3358）は validation 由来だが、
          // yup に相当する検証が無いため下の現在値ループでは拾えない。
          // ここで数えないと、セルとサマリーに出ているのに件数に入らない
          (issue.source !== reviewIssueSource.validation ||
            issue.code === reviewIssueCode.reviewChoice) &&
          !(issue.source === reviewIssueSource.matching && resolvedMatchingPaths.has(path)),
      );
      if (counted) paths.add(path);
    });

    if (!patternData?.item_list) return paths.size;

    patternData.item_list.forEach((item: ItemListType) => {
      if (item.is_active !== true) return;

      // 明細（array）
      if (item.type === 'array') {
        const rows = dynamicFormData[item.parentValue];
        const rowList = Array.isArray(rows) ? rows : [];

        // 明細が1行も無い（#3309）。行ループでは拾えないため個別に見る
        if (item.required === true && rowList.length === 0) {
          paths.add(item.id);
          return;
        }

        if (!item.children?.length) return;
        rowList.forEach((row, rowIndex) => {
          item.children
            .filter((child: ItemListChildType) => child.is_active === true)
            .forEach((child: ItemListChildType) => {
              const value = (row as Record<string, unknown>)?.[child.childValue];
              const err = validateArrayField(
                item.parentValue,
                rowIndex,
                child.childValue,
                value as string | number | Date | null,
                child,
              );
              if (err) paths.add(`${item.id}[${rowIndex}].${child.id}`);
            });
        });
        return;
      }

      // 単一項目。yup の検証結果（errors）をそのまま使う
      if (item.children?.length) return;
      if (errors[toFormFieldName(item.parentValue)]) paths.add(item.id);
    });

    return paths.size;
  };

  // 結果全体にバリデーションエラーがあるか（確認ダイアログの要否判定用）
  const hasAnyValidationError = (): boolean =>
    Object.keys(errors).length > 0 ||
    Object.keys(arrayFieldErrors).length > 0 ||
    hasArrayValidationError();

  // エラーまたは要注意がある場合は確認ダイアログを表示し、OKで action を実行する。
  // どちらも無ければそのまま action を実行する。（#2815 / #3262）
  //
  // 要注意があってもブロックはしない。要注意は「特に見た方がよい」という
  // 優先度の提示であり、確認済にすること自体を止める理由にならないため。
  const runWithValidationConfirm = (actionLabel: string, action: () => void): void => {
    const hasError = hasAnyValidationError();
    // 画面の現在値で数え直す（#3309）。保存済みの review_item_count は
    // OCR 時点の件数なので、入力エラーを直しても減らなかった
    const reviewCount = showReviewIssues ? countReviewItemsForConfirm() : 0;

    if (hasError || reviewCount > 0) {
      const reasons: string[] = [];
      if (hasError) reasons.push('入力内容に不備があります。');
      if (reviewCount > 0) reasons.push(`要注意の項目が ${reviewCount} 件あります。`);
      setValidationConfirmBody(`${reasons.join('')}${actionLabel}してよろしいですか？`);
      pendingValidationActionRef.current = action;
      setValidationConfirmOpen(true);
      return;
    }
    action();
  };

  const handleValidationConfirm = (): void => {
    setValidationConfirmOpen(false);
    const action = pendingValidationActionRef.current;
    pendingValidationActionRef.current = null;
    if (action) action();
  };

  const handleValidationConfirmClose = (): void => {
    setValidationConfirmOpen(false);
    pendingValidationActionRef.current = null;
  };

  // 項目設定の保存後、新しい制約でその場でバリデーションを再実行する（#2815）
  // patternData（スキーマ）更新後の再レンダリングを待ってから検証するため useEffect で実行
  useEffect(() => {
    if (validationRevalidateTrigger === 0) return;
    void trigger(); // 親項目・単一値フィールドを新スキーマで再検証
    revalidateAllArrayFields(); // 明細セルを再検証
  }, [validationRevalidateTrigger, trigger, revalidateAllArrayFields]);

  // ---- 画像プレビュー（#3275 パフォーマンス最適化）----

  // ページ番号順に並べたプレビュー用ファイル。
  // 以前は JSX 内で files.sort() を直接呼んでおり、state 配列を破壊的に
  // 変更していた（React の不変性違反）。コピーしてからソートする。
  // あわせて毎レンダーでの新規配列生成を避け、ImageOverlay の再描画を減らす。
  const sortedFiles = useMemo<InputFileType[]>(
    () =>
      [...files].sort((a: InputFileType, b: InputFileType) => {
        // page番号でソート（page番号がない場合は後ろに配置）
        const pageA = a.page || PAGE_ORDER_LAST;
        const pageB = b.page || PAGE_ORDER_LAST;
        return pageA - pageB;
      }),
    [files],
  );

  // ImageOverlay に渡す有効項目リスト。
  // JSX 内で filter すると毎レンダーで新しい配列になり、ImageOverlay を
  // React.memo 化しても props 比較が必ず不一致になって再描画が止まらない。
  const activeItemListForOverlay = useMemo<ItemListType[]>(
    () => patternData?.item_list?.filter((item: ItemListType) => item.is_active === true) ?? [],
    [patternData],
  );

  // ---- 計算フィールド（#3145 / #3172）の再計算 ----

  // パターンに計算フィールドがあるか（無ければ再計算処理は一切動かさない）
  const hasCalculatedFields = useMemo<boolean>(() => {
    if (!patternData?.item_list) return false;
    return containsCalculatedField(patternData.item_list);
  }, [patternData]);

  // 計算式が参照している項目（親・子）の item_id 群。
  // この項目の編集確定時だけ再計算する（無関係な項目の編集では動かさない）
  const calculationSourceItemIds = useMemo<Set<string>>(() => {
    const ids = new Set<string>();
    if (!patternData?.item_list) return ids;
    const referencedCodes = new Set<string>();
    const collectCodes = (field: {
      is_active?: boolean;
      is_calculated?: boolean;
      formula?: string;
    }): void => {
      if (field.is_active !== true || field.is_calculated !== true || !field.formula) return;
      extractFieldCodes(field.formula).forEach(code => referencedCodes.add(code));
    };
    patternData.item_list.forEach((item: ItemListType) => {
      collectCodes(item);
      item.children?.forEach((child: ItemListChildType) => collectCodes(child));
    });
    patternData.item_list.forEach((item: ItemListType) => {
      if (item.is_active !== true) return;
      if (item.field_code && referencedCodes.has(item.field_code)) ids.add(item.id);
      item.children?.forEach((child: ItemListChildType) => {
        if (child.is_active !== true) return;
        if (child.field_code && referencedCodes.has(child.field_code)) ids.add(child.id);
      });
    });
    return ids;
  }, [patternData]);

  // 再計算を予約する。値の state 反映後（次のレンダリング後）の effect で実行される
  // DetailTableRow の React.memo を効かせるため参照を固定する（#3275）
  const requestRecalculation = useCallback((): void => {
    if (!hasCalculatedFields) return;
    setCalculationRecalcTrigger(prev => prev + 1);
  }, [hasCalculatedFields]);

  // 参照元項目の編集確定時に呼ぶ。計算式から参照されていない項目なら何もしない
  const requestRecalculationForItem = useCallback(
    (itemId: string): void => {
      if (!calculationSourceItemIds.has(itemId)) return;
      requestRecalculation();
    },
    [calculationSourceItemIds, requestRecalculation],
  );

  // 再計算 effect が常に最新の state を読めるようにする ref（トリガー以外を effect の依存に
  // 持たせないため。dynamicFormData を依存にすると再計算自身の更新で無限ループする）
  const latestDynamicFormDataRef = useRef<DynamicFormData>({});
  const latestResultRecordRef = useRef<DocumentData | undefined>(undefined);
  useEffect(() => {
    latestDynamicFormDataRef.current = dynamicFormData;
    latestResultRecordRef.current = resultRecord;
  });

  useEffect(() => {
    if (calculationRecalcTrigger === 0) return;
    const itemList = latestPatternDataRef.current?.item_list;
    if (!itemList) return;

    // 単一値フィールドの現在値を itemId → 値 の形で集める
    const singleValues: Record<string, unknown> = {};
    itemList.forEach((itemGroup: ItemListType) => {
      if (itemGroup.is_active !== true) return;
      if (itemGroup.type === 'array' || itemGroup.children.length > 0) return;
      const fieldName = toFormFieldName(itemGroup.parentValue);
      singleValues[itemGroup.id] = getValues(fieldName);
    });

    const result = recalculateCalculatedFields({
      itemList,
      singleValues,
      dynamicFormData: latestDynamicFormDataRef.current,
      resultData: latestResultRecordRef.current?.result_data as Record<string, unknown> | undefined,
    });
    if (!result.hasCalculatedFields) return;

    result.singleUpdates.forEach(update => {
      const itemGroup = itemList.find((item: ItemListType) => item.id === update.itemId);
      if (!itemGroup) return;
      const fieldName = toFormFieldName(itemGroup.parentValue);
      setValue(fieldName, toFormDisplayValue(update.value, update.type));
    });

    if (result.arrayUpdates.length > 0) {
      setDynamicFormData(prev => {
        const next: DynamicFormData = { ...prev };
        result.arrayUpdates.forEach(update => {
          const rows = next[update.parentValue];
          if (!rows?.[update.rowIndex]) return;
          // 同じ親の初回更新時だけ行配列をコピーする（immutable 更新）
          if (rows === prev[update.parentValue]) next[update.parentValue] = [...rows];
          next[update.parentValue][update.rowIndex] = {
            ...next[update.parentValue][update.rowIndex],
            [update.childValue]: toFormDisplayValue(update.value, update.type),
          };
        });
        return next;
      });
    }

    // 要注意・内訳が参照する現在値を記録する（#3358）。
    // setValue は再レンダリングを起こさないため、ここで state に持たせる
    setCalculatedValuesByItemId(prev => {
      const next = new Map(prev);
      result.singleUpdates.forEach(update => next.set(update.itemId, update.value));
      return next;
    });

    setCalculationCellStates(result.cellStates);
    // 計算で値が変わったため、バリデーションも再評価する
    setValidationRevalidateTrigger(prev => prev + 1);
  }, [calculationRecalcTrigger, getValues, setValue]);

  // 前のレコードに移動
  const handlePrevResult = (): void => {
    if (!prevResultId) return;

    // ホーム画面からの遷移：recentResultsリストを引き継いでパターン横断ナビゲーション
    if (recentResultsList) {
      const targetPatternId = prevPatternId || patternId;
      navigate(`/ai-ocr/pattern/${targetPatternId}/result/${prevResultId}`, {
        state: { recentResults: recentResultsList },
      });
      return;
    }

    // 一覧画面からの遷移：検索条件＋一覧の表示順を引き継いで同一パターン内ナビゲーション
    if (patternId) {
      navigate(`/ai-ocr/pattern/${patternId}/result/${prevResultId}`, {
        state: {
          searchData: serializeSearchData(),
          ...(navigationResultIds ? { navigationResultIds } : {}),
          ocrItemKeywords,
        },
      });
    }
  };

  // 次のレコードに移動
  const handleNextResult = (): void => {
    if (!nextResultId) return;

    // ホーム画面からの遷移：recentResultsリストを引き継いでパターン横断ナビゲーション
    if (recentResultsList) {
      const targetPatternId = nextPatternId || patternId;
      navigate(`/ai-ocr/pattern/${targetPatternId}/result/${nextResultId}`, {
        state: { recentResults: recentResultsList },
      });
      return;
    }

    // 一覧画面からの遷移：検索条件＋一覧の表示順を引き継いで同一パターン内ナビゲーション
    if (patternId) {
      navigate(`/ai-ocr/pattern/${patternId}/result/${nextResultId}`, {
        state: {
          searchData: serializeSearchData(),
          ...(navigationResultIds ? { navigationResultIds } : {}),
          ocrItemKeywords,
        },
      });
    }
  };

  // 更新系処理
  const handleResults = async (nextStatus: number, deleteFlg?: boolean): Promise<void> => {
    // 保存（またはステータス変更）が実際に完了したか。
    // finally で成功メッセージを出すかの判定に使う（#3275）。
    // 例外だけでなく、早期 return や条件不成立で保存を実行しなかった場合も
    // 成功メッセージを出さないようにするため、完了時に true を立てる
    let hasSaved = false;
    try {
      setLoading(true);
      if (!resultId) return;

      if (deleteFlg) {
        // 削除の場合は、削除ステータスに変更して画面遷移
        const currentResult = await getResult(resultId);
        if (!currentResult) {
          throw new Error('削除対象のデータが見つかりません');
        }

        const resultRef = doc(db, 'results', resultId);
        await updateDoc(resultRef, {
          status: resultStatus.deleted,
          updated_at: Timestamp.now(),
          updated_by: user?.uid || '',
        });

        // 履歴記録: 削除（ステータス変更として記録）（失敗してもメイン処理は継続、ユーザーには通知しない）
        try {
          await createResultHistory({
            result_id: resultId,
            action_type: ResultHistoryActionType.DELETE,
            changed_fields: { status: resultStatus.deleted },
            previous_values: { status: currentResult.status },
            user_id: user?.uid || '',
            user_name: user?.name || currentResult.user_name || 'Unknown',
          });
        } catch (historyError) {
          // 履歴記録の失敗はメイン処理を止めない
        }

        setIsModalOpen(false);
        if (recentResultsList) {
          navigate('/home');
        } else {
          navigate(`/ai-ocr/pattern/${patternId}`, {
            state: { searchData: serializeSearchData(), ocrItemKeywords },
          });
        }
        return;
      } else {
        // 処理ステータスを変更
        if (resultId && patternData) {
          // マスタ再照合で手動選択した照合結果を保存時に確定する（#3078）。
          // 選択時点では Firestore を更新しないため、ここで selectedMasterIds を渡して反映する。
          const selectedMasterEntries: SelectedMasterEntry[] = Array.from(
            selectedMasterIds.values(),
          )
            .filter(v => !!v.match_reason_label)
            .map(v => ({
              searchItemId: v.search_item_id,
              rowIndex: v.row_index ?? 0,
              matchReasonLabel: v.match_reason_label ?? '',
              score: v.score,
              // 照合キーの上書き前の読取値（#3274）。選択時に退避した値をそのまま渡す。
              searchItemOcrValue: v.ocr_value,
              appliedValues: (v.applied_values ?? []).map(a => ({
                itemId: a.item_id,
                value: a.value,
              })),
              matchFilterValues: v.match_filter_values,
            }));
          await updatePatternResult(
            resultId,
            patternData,
            watch,
            dynamicFormData,
            nextStatus,
            user?.uid,
            clearedReasonItemKeys,
            selectedMasterEntries,
            calculationCellStates,
            buildReviewMatchSettings(),
          );
          hasSaved = true;
        }
      }
    } catch (error) {
      if (error instanceof FirebaseError) {
        switch (error.code) {
          case 'permission-denied':
            setErrorText('権限がありません。');
            break;
          case 'not-found':
            setErrorText('ドキュメントが見つかりません。');
            break;
          case 'invalid-argument':
            // Firestore の1ドキュメント1MB制限を超えた場合もここに来る。
            // 明細の行数・項目数が多い帳票で発生しうるため、原因が分かる文言にする
            setErrorText(
              error.message.includes('exceeds the maximum allowed size')
                ? 'データ量が上限を超えているため保存できません。明細の行数や項目数を減らしてください。'
                : '更新ができませんでした。',
            );
            break;
          default:
            setErrorText('更新ができませんでした。');
            break;
        }
      } else {
        setErrorText(convertToUserFriendlyMessage(error));
      }
    } finally {
      await fetchData(false);
      setLoading(false);
      // 下書き保存時はAI改善提案のキャッシュをクリア（次回は最新の差分で再取得）
      if (nextStatus === processingStatus.draft) {
        cachedRuleSuggestionsRef.current = null;
      }
      // スナックバーの表示。
      // 保存できていないのに成功と誤解させると、編集内容が消えたことに
      // 気づけずデータを失うため、完了した場合だけ出す
      if (hasSaved) {
        if (nextStatus === processingStatus.draft) {
          setSnackbarText('下書き保存しました。');
        } else if (nextStatus === processingStatus.completed) {
          setSnackbarText(`${processingStatusLabel[processingStatus.completed]}にしました。`);
        } else if (nextStatus === processingStatus.processing) {
          setSnackbarText(`${processingStatusLabel[processingStatus.processing]}にしました。`);
        }
      }
    }
  };

  const getImageDimensions = (url: string): Promise<{ width: number; height: number }> => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = (): void => resolve({ width: img.width, height: img.height });
      img.onerror = reject;
      img.src = url;
    });
  };

  const updateFilesWithUrls = async (files: InputFileType[]): Promise<void> => {
    const updatedFiles = await Promise.all(
      files.map(async (file: InputFileType) => {
        let fileUrl = file.file_url;
        if (!fileUrl) {
          fileUrl = await getFileUrl(file);
        }
        try {
          const { width, height } = await getImageDimensions(fileUrl);
          return { ...file, file_url: fileUrl, file_width: width, file_height: height };
        } catch (error) {
          return { ...file, file_url: fileUrl };
        }
      }),
    );
    setFiles(updatedFiles);
  };

  // 型の内部値を表示用ラベルに変換
  const convertTypeToDisplayLabel = (type: string | undefined): string => {
    switch (type) {
      case 'string':
        return 'テキスト';
      case 'number':
        return '数字';
      case 'date':
        return '日付';
      case 'textarea':
        return 'テキストエリア';
      case 'array':
        return '複数行データ';
      // 選択肢型（#3075）
      case 'radio':
        return 'ラジオボタン';
      case 'select':
        return 'プルダウン';
      default:
        return 'テキスト';
    }
  };

  // 表示用ラベルを内部値に変換
  const convertDisplayLabelToType = (label: string): string => {
    const dataTypeMap: Record<string, string> = {
      テキスト: 'string',
      テキストエリア: 'textarea',
      数字: 'number',
      日付: 'date',
      複数行データ: 'array',
      // 選択肢型（#3075）
      ラジオボタン: 'radio',
      プルダウン: 'select',
    };
    return dataTypeMap[label] || 'string';
  };

  // 項目編集ボタンのクリックハンドラ
  const handleEditClick = (
    id: string,
    itemName: string,
    dataType: string | undefined,
    displayWidth: number | undefined,
    extractionRule: string | undefined,
    outputFormat: string | undefined,
    isChild: boolean,
    parentId?: string,
    validation?: ItemValidationType,
  ): void => {
    // フィールドコード未設定の項目（導入前に作られたパターン）をここで自動採番する（#3145）。
    // 計算式の参照先コードをモーダルを開いた時点で確定させるため（パターン編集画面と同じ挙動）。
    // 補完結果は ref に持つだけで、Firestore へは項目設定の保存時に反映する
    // （開いて閉じただけで編集履歴が残らないよう、ここでは patternData を書き換えない）
    const itemList = patternData ? fillMissingFieldCodes(patternData.item_list) : [];
    editBaseItemListRef.current = patternData ? itemList : null;

    // 対象項目の is_ocr_target を patternData から引く（呼び出し側の引数を増やさないため。#2968）
    const currentIsOcrTarget = ((): boolean => {
      if (isChild && parentId) {
        const parent = itemList.find((i: ItemListType) => i.id === parentId);
        return parent?.children.find((c: ItemListChildType) => c.id === id)?.is_ocr_target ?? true;
      }
      return itemList.find((i: ItemListType) => i.id === id)?.is_ocr_target ?? true;
    })();
    // 対象項目のフィールドコードと、他項目が使用中のコードを patternData から引く（#3175）
    const currentFieldCode = ((): string => {
      if (isChild && parentId) {
        const parent = itemList.find((i: ItemListType) => i.id === parentId);
        return parent?.children.find((c: ItemListChildType) => c.id === id)?.field_code ?? '';
      }
      return itemList.find((i: ItemListType) => i.id === id)?.field_code ?? '';
    })();
    const usedFieldCodes = ((): string[] => {
      const codes: string[] = [];
      itemList.forEach((item: ItemListType) => {
        if (item.id !== id && item.field_code) codes.push(item.field_code);
        item.children?.forEach((child: ItemListChildType) => {
          if (child.id !== id && child.field_code) codes.push(child.field_code);
        });
      });
      return codes;
    })();
    // 子項目の未設定時の初期値を `親のコード.子の名前` にするため（#3175）
    const parentFieldCode = ((): string | undefined => {
      if (!isChild || !parentId) return undefined;
      const parent = itemList.find((i: ItemListType) => i.id === parentId);
      if (!parent) return undefined;
      return parent.field_code || sanitizeFieldCode(parent.parentValue);
    })();
    // 固定値も同様に patternData から引く（#3073）
    const currentFixedValue = ((): string => {
      if (isChild && parentId) {
        const parent = itemList.find((i: ItemListType) => i.id === parentId);
        return parent?.children.find((c: ItemListChildType) => c.id === id)?.fixedValue ?? '';
      }
      return itemList.find((i: ItemListType) => i.id === id)?.fixedValue ?? '';
    })();
    // 計算フィールド設定（#3145）。結果画面からも設定を確認・編集できるようにする
    const currentCalculation = ((): { isCalculated?: boolean; formula?: string } => {
      if (isChild && parentId) {
        const parent = itemList.find((i: ItemListType) => i.id === parentId);
        const child = parent?.children.find((c: ItemListChildType) => c.id === id);
        return { isCalculated: child?.is_calculated, formula: child?.formula };
      }
      const item = itemList.find((i: ItemListType) => i.id === id);
      return { isCalculated: item?.is_calculated, formula: item?.formula };
    })();
    // 参照できる項目（参照スコープ適用済み）を作る。対象項目の位置はIDから引く（#3145）
    const parentIndex = isChild && parentId ? itemList.findIndex(i => i.id === parentId) : -1;
    setEditItemCalcContext(
      isChild && parentId
        ? buildCalculationEditContext(itemList, {
            isChild: true,
            parentIndex,
            childIndex:
              itemList[parentIndex]?.children.findIndex((c: ItemListChildType) => c.id === id) ??
              -1,
          })
        : buildCalculationEditContext(itemList, {
            isChild: false,
            parentIndex: itemList.findIndex(i => i.id === id),
          }),
    );

    // 選択肢も同様に patternData から引く（#3075）。明細の列（プルダウン）も対象
    const currentChoices = ((): ItemChoiceType[] => {
      if (!patternData) return [];
      if (isChild && parentId) {
        const parent = patternData.item_list.find((i: ItemListType) => i.id === parentId);
        return parent?.children.find((c: ItemListChildType) => c.id === id)?.choices ?? [];
      }
      return patternData.item_list.find((i: ItemListType) => i.id === id)?.choices ?? [];
    })();
    setEditItemData({
      id,
      itemName,
      fieldCode: currentFieldCode,
      parentFieldCode,
      usedFieldCodes,
      dataType: convertTypeToDisplayLabel(dataType),
      displayWidth: displayWidth ?? displayWidthEnum.medium,
      isChild,
      parentId,
      isOcrTarget: currentIsOcrTarget,
      extractionRule: extractionRule || '',
      outputFormat: outputFormat || undefined,
      fixedValue: currentFixedValue,
      choices: currentChoices,
      required: validation?.required,
      minLength: validation?.minLength,
      maxLength: validation?.maxLength,
      minValue: validation?.minValue,
      maxValue: validation?.maxValue,
      treatZeroAsEmpty: validation?.treatZeroAsEmpty, // 0を空欄扱い（#3262）
      // 文字種・入力パターン・日付範囲（#3357）
      charType: validation?.charType,
      reviewChoiceValues: validation?.reviewChoiceValues, // 要注意にする選択肢（#3357）
      reviewLowConfidence: validation?.reviewLowConfidence, // 読み取り精度の要注意（#3384）
      pattern: validation?.pattern,
      patternMessage: validation?.patternMessage,
      dateNoPast: validation?.dateNoPast,
      dateNoFuture: validation?.dateNoFuture,
      datePastLimit: validation?.datePastLimit,
      datePastUnit: validation?.datePastUnit,
      dateFutureLimit: validation?.dateFutureLimit,
      dateFutureUnit: validation?.dateFutureUnit,
      isCalculated: currentCalculation.isCalculated,
      formula: currentCalculation.formula,
    });
    setEditModalOpen(true);
  };

  // 編集データからitemListを更新する共通処理
  const buildUpdatedItemList = (data: EditItemData): ItemListType[] => {
    if (!patternData) return [];
    const newType = convertDisplayLabelToType(data.dataType);
    // モーダルを開いたときにフィールドコードを補完した item_list を基準にする（#3145）。
    // 計算式が参照しているコードを、この保存で一緒に確定させるため
    const baseItemList = editBaseItemListRef.current ?? patternData.item_list;

    // フィールドコードが変更されたら、そのコードを参照している計算式を自動で書き換える（#3145）
    const applyFieldCodeRename = (
      list: ItemListType[],
      previousCode: string | undefined,
    ): ItemListType[] => {
      if (!previousCode || !data.fieldCode || previousCode === data.fieldCode) return list;
      return renameFieldCodeReferences(list, previousCode, data.fieldCode);
    };

    if (data.isChild && data.parentId) {
      const previousCode = baseItemList
        .find((i: ItemListType) => i.id === data.parentId)
        ?.children.find((c: ItemListChildType) => c.id === data.id)?.field_code;
      const newList = baseItemList.map((item: ItemListType) => {
        if (item.id === data.parentId) {
          const updatedChildren = item.children.map((child: ItemListChildType) => {
            if (child.id === data.id) {
              return {
                ...child,
                childValue: data.itemName,
                field_code: data.fieldCode, // #3175
                type: newType,
                displayWidth: data.displayWidth,
                is_ocr_target: data.isOcrTarget ?? true,
                extractionRule: data.extractionRule || '',
                outputFormat: data.outputFormat,
                fixedValue: data.fixedValue || '',
                // 選択肢（#3075）。選択肢型以外に変更した場合はモーダル側で空配列にクリアされている
                choices: data.choices ?? [],
                required: data.required,
                minLength: data.minLength,
                maxLength: data.maxLength,
                minValue: data.minValue,
                maxValue: data.maxValue,
                treatZeroAsEmpty: data.treatZeroAsEmpty, // 0を空欄扱い（#3262）
                // 文字種・入力パターン・日付範囲（#3357）
                charType: data.charType,
                reviewChoiceValues: data.reviewChoiceValues, // 要注意にする選択肢（#3357）
                reviewLowConfidence: data.reviewLowConfidence, // 読み取り精度の要注意（#3384）
                pattern: data.pattern,
                patternMessage: data.patternMessage,
                dateNoPast: data.dateNoPast,
                dateNoFuture: data.dateNoFuture,
                datePastLimit: data.datePastLimit,
                datePastUnit: data.datePastUnit,
                dateFutureLimit: data.dateFutureLimit,
                dateFutureUnit: data.dateFutureUnit,
                // 計算フィールド設定（#3145）
                is_calculated: data.isCalculated ?? false,
                formula: data.formula ?? '',
              };
            }
            return child;
          });
          return { ...item, children: updatedChildren };
        }
        return item;
      });
      return applyFieldCodeRename(newList, previousCode);
    }
    const previousCode = baseItemList.find((i: ItemListType) => i.id === data.id)?.field_code;
    const newList = baseItemList.map((item: ItemListType) => {
      if (item.id === data.id) {
        return {
          ...item,
          parentValue: data.itemName,
          field_code: data.fieldCode, // #3175
          type: newType,
          is_ocr_target: data.isOcrTarget ?? true,
          extractionRule: data.extractionRule || '',
          outputFormat: data.outputFormat,
          fixedValue: data.fixedValue || '',
          // 選択肢（#3075）。選択肢型以外に変更した場合はモーダル側で空配列にクリアされている
          choices: data.choices ?? [],
          required: data.required,
          minLength: data.minLength,
          maxLength: data.maxLength,
          minValue: data.minValue,
          maxValue: data.maxValue,
          treatZeroAsEmpty: data.treatZeroAsEmpty, // 0を空欄扱い（#3262）
          // 文字種・入力パターン・日付範囲（#3357）
          charType: data.charType,
          reviewChoiceValues: data.reviewChoiceValues, // 要注意にする選択肢（#3357）
          reviewLowConfidence: data.reviewLowConfidence, // 読み取り精度の要注意（#3384）
          pattern: data.pattern,
          patternMessage: data.patternMessage,
          dateNoPast: data.dateNoPast,
          dateNoFuture: data.dateNoFuture,
          datePastLimit: data.datePastLimit,
          datePastUnit: data.datePastUnit,
          dateFutureLimit: data.dateFutureLimit,
          dateFutureUnit: data.dateFutureUnit,
          // 計算フィールド設定（#3145）。テーブル親はモーダルに計算UIを出さないため既存値を保つ
          is_calculated:
            item.children?.length > 0 ? item.is_calculated : (data.isCalculated ?? false),
          formula: item.children?.length > 0 ? item.formula : (data.formula ?? ''),
        };
      }
      return item;
    });
    return applyFieldCodeRename(newList, previousCode);
  };

  // itemListをAPIリクエスト用のsnake_caseに変換（共通ユーティリティを利用）
  const convertItemListForApi = (itemList: ItemListType[]): Record<string, unknown>[] =>
    convertItemListToSnakeCase(itemList) as unknown as Record<string, unknown>[];

  const handleReclassifySelect = (
    newPatternId: string,
    itemList: Record<string, unknown>[],
  ): void => {
    if (!resultId) return;
    // 代理モード・パターンなし選択時はチェック不要
    if (!isProxyMode && newPatternId !== PATTERN_ID_OTHER && company) {
      const pageCount =
        resultRecord?.token_usage?.page_count || resultRecord?.original_files?.length || 1;
      const limitInfo = calculateOcrLimitInfo(company, baseRemainingCount);
      if (limitInfo.totalRemainingCount < pageCount) {
        setReclassifyModalOpen(false);
        setLimitDialogOpen(true);
        return;
      }
    }
    void executeReclassify(newPatternId, itemList);
  };

  const executeReclassify = async (
    newPatternId: string,
    itemList: Record<string, unknown>[],
  ): Promise<void> => {
    if (!resultId) return;
    setReclassifyModalOpen(false);
    setReclassifyLoading(true);

    // 擬似プログレス（即座にLoadingModal表示）
    setReOcrProgress(0);
    setIsReOcrProcessing(true);

    let feProvisionalCreditAmount = 0;
    let provisionalDeducted = false;
    // 仮加算（current_month_ocr_cost）に +15円 が乗ったか。BEの確定 delta 計算に渡す。
    let feProvisionalHasMasterMapping = false;

    try {
      // 仕分け先パターンのgemini_modelを取得（Pro単価の仮引き・OCR実行に使用）
      const reclassifyPattern =
        newPatternId !== PATTERN_ID_OTHER ? await getPattern(newPatternId).catch(() => null) : null;
      const reclassifyGeminiModel = reclassifyPattern?.gemini_model ?? GEMINI_MODEL_DEFAULT;
      const reclassifyHasMasterMapping = reclassifyPattern?.has_master_mapping === true;

      // パターンなし選択時はOCR不要なので仮消費しない
      if (newPatternId !== PATTERN_ID_OTHER && company) {
        // 仮消費実行（ページ数分 + マスタ照合が有効なら +15円）
        const pageCount =
          resultRecord?.token_usage?.page_count || resultRecord?.original_files?.length || 1;
        const provisionalResult = await executeProvisionalOcrUsage(
          company,
          pageCount,
          baseRemainingCount,
          isProxyMode,
          reclassifyGeminiModel,
          reclassifyHasMasterMapping,
          1,
          [pageCount],
          baseRemainingJpy,
        );
        feProvisionalCreditAmount = provisionalResult.feProvisionalCreditAmount;
        feProvisionalHasMasterMapping = provisionalResult.provisionalHasMasterMapping;
        provisionalDeducted = true;
      }
      const progressInterval = setInterval(() => {
        setReOcrProgress(prev => {
          if (prev >= 90) return prev;
          return prev + (90 - prev) * 0.08;
        });
      }, 500);

      try {
        const pageCount =
          resultRecord?.token_usage?.page_count || resultRecord?.original_files?.length || 1;
        await handleReOcrPatternResult({
          resultId,
          patternId: newPatternId,
          itemList,
          isReclassification: true,
          provisionalDeducted,
          feProvisionalCreditAmount,
          feProvisionalUsageCount: pageCount,
          provisionalHasMasterMapping: feProvisionalHasMasterMapping,
          isProxy: isProxyMode,
          operatorId: isProxyMode ? operatorId || undefined : undefined,
          geminiModel: reclassifyGeminiModel,
        });

        clearInterval(progressInterval);
        setReOcrProgress(100);

        setSnackbarText('仕分けを変更しました。');
        // プログレス100%表示後に遷移
        setTimeout(() => {
          setIsReOcrProcessing(false);
          setReOcrProgress(0);
          if (newPatternId === PATTERN_ID_OTHER) {
            // パターンなしはOCR結果が無く結果画面を表示できないため、ホーム画面に遷移する
            navigate('/home', { replace: true });
          } else {
            // patternIdが変わることでuseEffectが再実行され、新パターンのデータが自動取得される
            navigate(`/ai-ocr/pattern/${newPatternId}/result/${resultId}`, { replace: true });
          }
        }, 800);
      } catch (error) {
        clearInterval(progressInterval);
        setIsReOcrProcessing(false);
        setReOcrProgress(0);
        // エラーでもpattern_idはBE側で変更済みなので新パターンの一覧に遷移
        navigate(`/ai-ocr/pattern/${newPatternId}`, { replace: true });
        throw error;
      }
    } catch (error) {
      const message = convertToUserFriendlyMessage(error);
      setErrorText(message);
    } finally {
      setReclassifyLoading(false);
    }
  };

  // 項目編集の保存のみハンドラ
  const handleEditSave = async (data: EditItemData): Promise<void> => {
    setEditModalOpen(false);
    if (!patternData || !patternId || !user?.uid) return;

    const updatedItemList = buildUpdatedItemList(data);

    try {
      await updatePatternItemList(patternId, updatedItemList, user.uid);
      setPatternData({ ...patternData, item_list: updatedItemList });
      // 保存した制約でその場でバリデーションを再実行する（#2815）
      setValidationRevalidateTrigger(prev => prev + 1);
      // 計算設定を変更したときは、保存した式でその場で計算し直す（#3145）。
      // 変更前後のどちらかに計算フィールドがあれば動かす（計算OFFにした場合も状態を更新するため）
      if (hasCalculatedFields || containsCalculatedField(updatedItemList)) {
        setCalculationRecalcTrigger(prev => prev + 1);
      }
      setSnackbarText('項目設定を保存しました');
    } catch (error) {
      if (error instanceof FirebaseError) {
        setErrorText(convertToUserFriendlyMessage(error));
      } else {
        setErrorText('項目の保存に失敗しました');
      }
    }
    setEditItemData(null);
    setEditItemCalcContext(null);
    editBaseItemListRef.current = null;
  };

  // 項目編集の保存 + 再読取ハンドラ
  const handleEditSaveAndReOcr = async (data: EditItemData): Promise<void> => {
    setEditModalOpen(false);
    if (!patternData || !patternId || !user?.uid || !resultId) return;

    const updatedItemList = buildUpdatedItemList(data);

    // 対象項目のIDを収集（親項目の場合は子項目も含める）
    const targetIds = [data.id];
    const targetItem = updatedItemList.find(item => item.id === data.id);
    if (targetItem?.children) {
      targetItem.children.forEach(child => targetIds.push(child.id));
    }

    // モーダルを閉じた直後にグレーアウトを表示（既存の再読取中IDに追加）
    setReReadingItemIds(prev => Array.from(new Set(prev.concat(targetIds))));

    try {
      // パターンのitem_listを保存
      await updatePatternItemList(patternId, updatedItemList, user.uid);
      setPatternData({ ...patternData, item_list: updatedItemList });

      // 再OCR時はその時点のitemListをversionとして即時保存し、ocr_executionsから参照可能にする
      try {
        await createPatternHistory({
          patternId,
          prompt: patternData.prompt ?? '',
          useCustomPrompt: patternData.use_custom_prompt ?? false,
          itemList: updatedItemList,
          geminiModel: patternData.gemini_model ?? null,
          changedBy: user.uid,
          changedByName: user.name ?? '',
        });
        // 直近保存の参照値を更新（アンマウント子パターン判定用）
        lastSavedStateRef.current = {
          prompt: patternData.prompt ?? '',
          useCustomPrompt: patternData.use_custom_prompt ?? false,
          geminiModel: patternData.gemini_model ?? null,
          ocrEngine: patternData.ocr_engine ?? null,
          itemList: updatedItemList,
        };
      } catch (historyError) {
        Sentry.captureException(historyError);
      }

      setSnackbarText('項目設定を保存しました。再読取を実行中...');

      const reOcrResponse = await handleReOcrPatternResult({
        resultId,
        patternId,
        itemList: convertItemListForApi(updatedItemList),
        targetItemIds: [data.id],
        geminiModel: resultRecord?.gemini_model,
        thinkingBudget: resultRecord?.token_usage?.gemini_ocr?.thinking_budget,
        thinkingLevel: resultRecord?.token_usage?.gemini_ocr?.thinking_level,
        isProxy: isProxyMode,
        operatorId: isProxyMode ? operatorId || undefined : undefined,
      });

      if (reOcrResponse.success) {
        await fetchData(false);
        setSnackbarText('再読取が完了しました');
      } else {
        setSnackbarText('');
        setErrorText(reOcrResponse.error?.message || '再読取に失敗しました');
      }
    } catch (error) {
      setSnackbarText('');
      if (error instanceof FirebaseError) {
        setErrorText(convertToUserFriendlyMessage(error));
      } else if (error instanceof Error) {
        setErrorText(error.message);
      } else {
        setErrorText('項目の保存に失敗しました');
      }
    } finally {
      setReReadingItemIds(prev => prev.filter(id => !targetIds.includes(id)));
    }
    setEditItemData(null);
    setEditItemCalcContext(null);
    editBaseItemListRef.current = null;
  };

  // 全項目再読取（仮消費対応）
  const handleReOcrAll = (): void => {
    if (!patternData || !patternId || !user?.uid || !resultId) return;

    // OCR枚数の上限チェック（従来どおり）
    if (company && !isProxyMode) {
      const pageCount =
        resultRecord?.token_usage?.page_count || resultRecord?.original_files?.length || 1;
      const limitInfo = calculateOcrLimitInfo(company, baseRemainingCount);
      if (limitInfo.totalRemainingCount < pageCount) {
        setLimitDialogOpen(true);
        return;
      }
    }

    // 子パターンの選択肢を読み込む（#2967）。子パターンを持つパターンでのみ選択欄が出る。
    // 取得できなくても再読み取り自体は実行できるため、失敗しても止めない。
    // 運営スタッフの再読取設定モーダルでも使うため、分岐より前で読み込む（#3333）
    if (canChangeSubPattern && patternData) {
      getSubPatterns(patternData.patternId)
        .then(variants => setExistingSubPatterns(variants.filter(v => v.is_active)))
        .catch(() => setExistingSubPatterns([]));
    }

    // 運営スタッフ（詳細モードON時）は再読取設定モーダルを開く。
    // モーダルの初期値は1回目の設定（gemini_model / verification_settings）から復元する。
    if (isDetailView) {
      const vs = displayTokenUsage?.verification_settings;
      const structuredNameToEngine = (name?: string): number => {
        if (!name) return structuredEngineType.gemini;
        if (name.includes('gpt-4o')) return structuredEngineType.chatgpt;
        if (name.includes('3.5-flash-lite')) return structuredEngineType.geminiFlashLite35;
        // 3.1 Flash-Lite は既定（gemini）と同じモデルになったため、選択肢を統合した
        // 既定値に寄せる（#3375）。geminiFlashLite31 はプルダウンに出さない。
        return structuredEngineType.gemini;
      };
      setReOcrSettings({
        geminiModel: toAvailableGeminiModel(resultRecord?.gemini_model ?? GEMINI_MODEL_DEFAULT),
        structuredEngine: structuredNameToEngine(vs?.structured_model_name),
        includeCoordinates: vs?.include_coordinates ?? false,
        geminiTemperature: vs?.temperature ?? 0,
        geminiTopP: vs?.top_p ?? 0,
        thinkingBudget: displayTokenUsage?.gemini_ocr?.thinking_budget ?? null,
        thinkingLevel: displayTokenUsage?.gemini_ocr?.thinking_level ?? null,
      });
      setReOcrModalOpen(true);
      return;
    }

    // 一般ユーザーはOCR枚数消費の確認モーダルを表示（従来どおり）
    setCreditConfirmOpen(true);
  };

  /**
   * 全項目の再読み取り。
   * subPatternId を渡すと子パターンを切り替えて読み直す（#2967）。
   * クレジットの仮引き・進捗表示は通常の再読み取りと同じ経路をそのまま使う。
   */
  const executeReOcrAll = async (
    settings?: ReOcrSettings,
    subPatternId?: string,
  ): Promise<void> => {
    if (!patternData || !patternId || !user?.uid || !resultId) return;

    setReOcrModalOpen(false);
    // 即座にLoadingModal表示
    setReOcrProgress(0);
    setIsReOcrProcessing(true);

    let feProvisionalCreditAmount = 0;
    let provisionalDeducted = false;
    // 仮加算（current_month_ocr_cost）に +15円 が乗ったか。BEの確定 delta 計算に渡す。
    let feProvisionalHasMasterMapping = false;

    // 値取得モデル: 設定モーダルで選んだ値（運営）。未指定（一般）はパターン設定を使う。
    const reOcrGeminiModel =
      settings?.geminiModel ?? patternData.gemini_model ?? GEMINI_MODEL_DEFAULT;
    const reOcrHasMasterMapping = patternData.has_master_mapping === true;

    // 仮消費実行（ページ数分 + マスタ照合が有効なら +15円）
    if (company) {
      try {
        const pageCount =
          resultRecord?.token_usage?.page_count || resultRecord?.original_files?.length || 1;
        const provisionalResult = await executeProvisionalOcrUsage(
          company,
          pageCount,
          baseRemainingCount,
          isProxyMode,
          reOcrGeminiModel,
          reOcrHasMasterMapping,
          1,
          [pageCount],
          baseRemainingJpy,
        );
        feProvisionalCreditAmount = provisionalResult.feProvisionalCreditAmount;
        feProvisionalHasMasterMapping = provisionalResult.provisionalHasMasterMapping;
        provisionalDeducted = true;
      } catch (error) {
        setIsReOcrProcessing(false);
        const message = convertToUserFriendlyMessage(error);
        setErrorText(message);
        return;
      }
    }
    const progressInterval = setInterval(() => {
      setReOcrProgress(prev => {
        if (prev >= 90) return prev;
        return prev + (90 - prev) * 0.08;
      });
    }, 500);

    try {
      const reOcrPageCount =
        resultRecord?.token_usage?.page_count || resultRecord?.original_files?.length || 1;
      const reOcrResponse = await handleReOcrPatternResult({
        resultId,
        patternId,
        itemList: convertItemListForApi(patternData.item_list),
        geminiModel: reOcrGeminiModel,
        thinkingBudget:
          settings?.thinkingBudget ?? resultRecord?.token_usage?.gemini_ocr?.thinking_budget,
        thinkingLevel:
          settings?.thinkingLevel ?? resultRecord?.token_usage?.gemini_ocr?.thinking_level,
        // #2960 再読取モーダルで再設定した検証設定（運営のみ。一般は undefined）
        structuredEngine: settings?.structuredEngine,
        includeCoordinates: settings?.includeCoordinates,
        geminiTemperature: settings?.geminiTemperature,
        geminiTopP: settings?.geminiTopP,
        provisionalDeducted,
        feProvisionalCreditAmount,
        feProvisionalUsageCount: reOcrPageCount,
        provisionalHasMasterMapping: feProvisionalHasMasterMapping,
        isProxy: isProxyMode,
        operatorId: isProxyMode ? operatorId || undefined : undefined,
        // 子パターンの指定（#2967）。未指定なら現在の子パターンを維持する
        subPatternId,
      });

      clearInterval(progressInterval);
      setReOcrProgress(100);

      if (reOcrResponse.success) {
        await fetchData(false);
        setSnackbarText(subPatternId ? '子パターンを変更しました' : '再読取が完了しました');
      } else {
        setSnackbarText('');
        setErrorText(reOcrResponse.error?.message || '再読取に失敗しました');
      }
    } catch (error) {
      clearInterval(progressInterval);
      setSnackbarText('');
      if (error instanceof FirebaseError) {
        setErrorText(convertToUserFriendlyMessage(error));
      } else if (error instanceof Error) {
        setErrorText(error.message);
      } else {
        setErrorText('再読取に失敗しました');
      }
    } finally {
      setIsReOcrProcessing(false);
      setReOcrProgress(0);
    }
  };

  const saveButtonStyles = {
    buttonChange:
      isProcessingStatus === processingStatus.completed ||
      isProcessingStatus === processingStatus.outputted
        ? { ...secondaryButtonBackgroundColor }
        : isProcessingStatus === processingStatus.draft ||
            isProcessingStatus === processingStatus.processing
          ? { ...primaryButtonBackgroundColor }
          : {},
    textColorChange:
      isProcessingStatus === processingStatus.completed ||
      isProcessingStatus === processingStatus.outputted
        ? { ...boldMd }
        : isProcessingStatus === processingStatus.draft ||
            isProcessingStatus === processingStatus.processing
          ? { ...boldMdWhite }
          : {},
  };

  // ボタンのテキストをステータスで変更
  const buttonText = (): string => {
    if (
      isProcessingStatus === processingStatus.draft ||
      isProcessingStatus === processingStatus.processing
    ) {
      return `${processingStatusLabel[processingStatus.completed]}にする`;
    } else if (
      isProcessingStatus === processingStatus.completed ||
      isProcessingStatus === processingStatus.outputted
    ) {
      return `${processingStatusLabel[processingStatus.processing]}にする`;
    } else {
      return `${processingStatusLabel[processingStatus.completed]}にする`;
    }
  };

  // スコープ（取引先など）の値を OCR 結果から解決する。
  // master.scope_item_id が指す項目の「入力欄の現在値」を返す。未設定/空なら undefined。
  // OCR結果(ocr_value)ではなく、ユーザーが結果画面で修正した現在のフォーム値を使う。
  // スコープ項目が明細（配列）の子の場合は dynamicFormData[親][rowIndex][子] から取る。
  // DetailTableRow の React.memo を効かせるため参照を固定する（#3275）。
  // dynamicFormData は毎回変わるので依存に入れず、ref 経由で最新値を読む
  const resolveSupplierId = useCallback(
    (master: MasterWithMappingType, rowIndex?: number): string | undefined => {
      const scopeId = master.scope_item_id;
      if (!scopeId) return undefined;

      for (const itemGroup of patternData?.item_list ?? []) {
        // 単一値項目: フォーム値を getValues で取る
        if (
          itemGroup.id === scopeId &&
          itemGroup.type !== 'array' &&
          (!itemGroup.children || itemGroup.children.length === 0)
        ) {
          const fieldName = toFormFieldName(itemGroup.parentValue);
          const v = String(getValues(fieldName as never) ?? '').trim();
          return v || undefined;
        }
        // 明細（配列）の子: dynamicFormData の同じ行のセルから取る
        const childField = itemGroup.children?.find((c: ItemListChildType) => c.id === scopeId);
        if (childField) {
          // rowIndex は「照合対象の行番号」。スコープ項目が照合対象と同じグループにある
          // 場合のみ意味を持つ（例: 明細内の商品名を、同じ行の取引先コードで絞る）。
          // 別グループ（例: 明細の商品名に対し、単一の発注元の得意先コード）では
          // 行番号を引き継ぐと存在しない行を見てしまうため 0 行目を使う（#3072）。
          const isSameGroupAsSearchItem = itemGroup.children?.some(
            (c: ItemListChildType) => c.id === master.search_item_id,
          );
          // rowIndex には行ID が入るため、現在の配列位置へ変換して引く（#3283）
          const targetRowId = isSameGroupAsSearchItem ? (rowIndex ?? 0) : 0;
          const rowsForScope = latestDynamicFormDataRef.current[itemGroup.parentValue];
          const targetRow = toRowPositionByRowId(rowsForScope).get(targetRowId) ?? targetRowId;
          const row = rowsForScope?.[targetRow];
          const v = String(row?.[childField.childValue] ?? '').trim();
          return v || undefined;
        }
      }
      return undefined;
    },
    [patternData, getValues],
  );

  /**
   * 任意のパターン項目の「入力欄の現在値」を返す（#3180）。
   * resolveSupplierId と同じ探索だが、項目IDを外から受け取れるようにしたもの。
   */
  const resolveItemValue = (itemId: string, rowIndex?: number): string | undefined => {
    if (!itemId) return undefined;

    for (const itemGroup of patternData?.item_list ?? []) {
      // 単一値項目: フォーム値を getValues で取る
      if (
        itemGroup.id === itemId &&
        itemGroup.type !== 'array' &&
        (!itemGroup.children || itemGroup.children.length === 0)
      ) {
        const fieldName = toFormFieldName(itemGroup.parentValue);
        const value = String(getValues(fieldName as never) ?? '').trim();
        return value || undefined;
      }
      // 明細（配列）の子: dynamicFormData の同じ行のセルから取る
      const childField = itemGroup.children?.find((c: ItemListChildType) => c.id === itemId);
      if (childField) {
        // rowIndex には行ID が入るため、現在の配列位置へ変換して引く（#3283）
        const rows = dynamicFormData[itemGroup.parentValue];
        const row = rows?.[toRowPositionByRowId(rows).get(rowIndex ?? 0) ?? rowIndex ?? 0];
        const value = String(row?.[childField.childValue] ?? '').trim();
        return value || undefined;
      }
    }
    return undefined;
  };

  /**
   * 絞り込みキーの「項目ラベル・マスタ列・現在値」を解決する（#3180）。
   * 再照合モーダルで「どの条件で絞られているか」を表示するために使う。
   * 値が空のキーは照合時に条件から外れるため、ここでも除外する。
   */
  const resolveFilterConditions = (
    master: MasterWithMappingType,
    rowIndex?: number,
  ): MasterFilterCondition[] =>
    resolveFilterKeys(master)
      .map(key => ({
        itemLabel: key.item_label || key.item_id,
        csvColumn: key.csv_column,
        value: resolveItemValue(key.item_id, rowIndex) ?? '',
        filterMatchMode: key.filter_match_mode,
      }))
      // CSV更新で消えた列の条件は、自動照合と同じく外す（#3441）。残すとヒット0件になるため。
      // csv_columns が無い旧マスタは判定できないので外さない。
      .filter(
        condition =>
          !!condition.value &&
          (!master.csv_columns || master.csv_columns.includes(condition.csvColumn)),
      );

  /**
   * 行ID → 現在の配列位置 に変換する（#3283）。
   *
   * 照合・辞書のモーダルから返る rowIndex には行ID が入っている。
   * 画面状態（selectedMasterIds など）のキーは行ID のままでよいが、
   * dynamicFormData / result_data は配列位置で引くためここで変換する。
   * 対応する行が見つからないときは行ID をそのまま位置として扱う（従来動作）。
   */
  const toRowPosition = useCallback((parentValue: string, rowId: number): number => {
    const rows = latestDynamicFormDataRef.current[parentValue];
    return toRowPositionByRowId(rows).get(rowId) ?? rowId;
  }, []);

  /**
   * 再照合で選んだ候補の、照合時の絞り込みの値を作る（#3517）。
   *
   * 値はモーダルに渡した条件（モーダルを開いた時点の値）から取る。項目の現在値を
   * 表示に使うと、照合後に値を書き換えたとき「その値で照合した」ように見えるため。
   * 値が無く条件に入らなかったキーは「未適用」、モーダルで外したキーは「外して照合」にする。
   * 照合履歴タブから選んだ場合（excludedColumns 未設定）は絞り込んでいないため空にする。
   */
  const buildManualMatchFilterValues = (
    master: MasterWithMappingType,
    usedConditions: MasterFilterCondition[],
    excludedColumns: string[] | undefined,
  ): MatchFilterValue[] => {
    if (!excludedColumns) return [];
    return resolveFilterKeys(master).map(key => {
      const itemLabel = key.item_label || key.item_id;
      if (excludedColumns.includes(key.csv_column)) {
        return { item_label: itemLabel, filter_value: null, excluded: true };
      }
      const used = usedConditions.find(condition => condition.csvColumn === key.csv_column);
      return { item_label: itemLabel, filter_value: used ? used.value : null };
    });
  };

  // 照合選択時に selectedMasterIds を更新 + フォームフィールドに反映
  const handleMasterSelect = (result: MasterMatchResult): void => {
    if (!reconciliationTarget) return;
    const { master, rowIndex } = reconciliationTarget;
    const mapKey = `${master.master_id}-${rowIndex ?? 0}`;
    const matchReasonLabel = `再照合（照合結果：${result.matchedValue}）`;
    const scoreLabel = result.score ? `${result.score}%` : undefined;
    const matchFilterValues = buildManualMatchFilterValues(
      master,
      reconciliationTarget.filterConditions ?? [],
      result.excludedFilterColumns,
    );

    // フォーム反映対象 (item_id → value)。値を実際に書き換える項目のみを集める。
    // - 照合キー自体 (search_item_id): 選択したマスタレコードの照合先列の値（#3274）。
    //   従来は OCR 読取値を残していたが、手動で選び直した以上マスタの表記に揃えるのが正。
    //   上書き前の値は保存時に ocr_value へ退避され、「OCR結果：」表示は維持される。
    // - column_mappings: 各カラムの raw_data 値
    const updatesByItemId = new Map<string, unknown>();
    if (master.search_item_id && result.matchedValue) {
      updatesByItemId.set(master.search_item_id, result.matchedValue);
    }
    if (result.rowData && master.column_mappings?.length > 0) {
      master.column_mappings.forEach(({ csv_column, item_id }) => {
        const value = result.rowData[csv_column];
        if (value === undefined || !item_id) return;
        updatesByItemId.set(item_id, value);
      });
    }

    // 選択内容は UI 状態（selectedMasterIds）にのみ保持し、Firestore は保存ボタン／
    // 確認済みボタン押下時にまとめて確定する（#3078）。ラベル表示は selectedMasterIds を
    // 最優先で参照するため、選択した時点で画面にラベルが出る。
    // 保存時に result_data へ反映するためのラベル・スコア・値もここで一緒に保持する。
    const appliedValues = Array.from(updatesByItemId.entries()).map(([item_id, value]) => ({
      item_id,
      value,
    }));
    setSelectedMasterIds(prev => {
      const next = new Map(prev);
      next.set(mapKey, {
        ocr_value: reconciliationTarget.ocrValue ?? result.matchedValue,
        matched_label: result.matchedValue,
        label: master.search_item_label,
        source_field_name: master.search_item_label,
        master_record_id: result.masterRecordId,
        match_reason_label: matchReasonLabel,
        supplier_id: reconciliationTarget.supplierId,
        supplier_label: master.scope_item_label,
        search_item_id: master.search_item_id,
        score: scoreLabel,
        row_index: rowIndex ?? 0,
        applied_values: appliedValues,
        match_filter_values: matchFilterValues,
      });
      return next;
    });

    // 紐付け解除（clearedReasonItemKeys）で消していた項目を再選択したケースに備え、
    // 対象項目を解除対象から外す（保存時にラベルが消されないようにする）（#3078）。
    setClearedReasonItemKeys(prev => {
      if (prev.size === 0) return prev;
      // 解除キーは行ID ベース（#3283）。保存側（update.ts）の originalIndex と揃える
      const targetRowId = rowIndex ?? 0;
      const next = new Set(prev);
      if (master.search_item_id) next.delete(master.search_item_id);
      updatesByItemId.forEach((_value, itemId) => {
        next.delete(itemId);
        next.delete(clearedReasonItemKey(itemId, targetRowId));
      });
      return next.size === prev.size ? prev : next;
    });

    // 配列項目（行ごとに値を持つ）は setDynamicFormData で更新する必要があるため、
    // まず親キーごとにまとめてから1回の state 更新でまとめて反映する。
    const dynamicUpdates = new Map<string, Map<string, unknown>>(); // parentValue → (childValue → value)

    updatesByItemId.forEach((value, itemId) => {
      // item_list を走査:
      //   - トップレベル itemGroup.id === itemId かつ単一値項目 → setValue 反映
      //   - children 配下に id === itemId の childField があれば → dynamicFormData 反映
      //   - どちらにも該当しなければ無反映でスキップ
      for (const itemGroup of patternData?.item_list ?? []) {
        if (
          itemGroup.id === itemId &&
          itemGroup.type !== 'array' &&
          (!itemGroup.children || itemGroup.children.length === 0)
        ) {
          const fieldName = toFormFieldName(itemGroup.parentValue);
          setValue(fieldName as never, value as never);
          break;
        }
        const childField = itemGroup.children?.find((c: ItemListChildType) => c.id === itemId);
        if (childField) {
          const existing = dynamicUpdates.get(itemGroup.parentValue) ?? new Map();
          existing.set(childField.childValue, value);
          dynamicUpdates.set(itemGroup.parentValue, existing);
          break;
        }
      }
    });

    if (dynamicUpdates.size > 0) {
      setDynamicFormData(prev => {
        const next: DynamicFormData = { ...prev };
        const targetRowId = rowIndex ?? 0;
        dynamicUpdates.forEach((childUpdates, parentValue) => {
          const rows = [...(next[parentValue] ?? [])];
          // 行ID から現在の配列位置へ変換して書き込む（#3283）。
          // 行を挿入・削除した後でも、選択した行に正しく反映される。
          const targetRow = toRowPosition(parentValue, targetRowId);
          while (rows.length <= targetRow) {
            rows.push({ index: rows.length });
          }
          rows[targetRow] = { ...rows[targetRow] };
          childUpdates.forEach((v, childValue) => {
            rows[targetRow][childValue] = v as DynamicFieldValue[string];
          });
          next[parentValue] = rows;
        });
        return next;
      });
    }

    // ここでは Firestore を更新しない（#3078）。ラベル・スコア・値は selectedMasterIds に
    // 保持済みで、保存ボタン／確認済みボタン押下時に updatePatternResult 経由で確定する。

    // マスタ照合で値が埋まったので、明細セルのバリデーション（#2815）を再評価してエラー表示を更新する。
    setValidationRevalidateTrigger(prev => prev + 1);
    // 反映した値を参照している計算フィールドを再計算する（#3172）
    requestRecalculation();

    setMasterModalOpen(false);
    setReconciliationTarget(null);
  };

  // 紐付け解除（D-2）: マッピングだけ解除する。
  // - 照合キー本体 (search_item_id) の入力欄は触らない（値はそのまま維持）
  // - column_mappings で埋めた他カラム（得意先CD・住所等）だけ空にする
  // - selectedMasterIds から該当キーを削除し、照合理由ラベルをローカルで非表示にする
  // - 即時保存はしない。フォーム/ローカル state のみ変更し、保存ボタンで確定する。
  const handleMasterUnlink = (master: MasterWithMappingType, rowIndex?: number): void => {
    // rowIndex には行ID が入る（#3283）。画面状態のキーは行ID のまま使い、
    // result_data / dynamicFormData を引くときだけ現在の配列位置へ変換する。
    const rowId = rowIndex ?? 0;
    const mapKey = masterSelectionKey(master.master_id, rowId);

    setSelectedMasterIds(prev => {
      const next = new Map(prev);
      next.delete(mapKey);
      return next;
    });
    // Firestore 由来のラベルをローカルで打ち消すため、解除済みキーを記録する。
    setUnlinkedMasterKeys(prev => {
      const next = new Set(prev);
      next.add(mapKey);
      return next;
    });

    // 保存時にラベル/スコアを消す対象の item_id を記録する。
    // ラベルは照合キー本体 (search_item_id) に表示されるため、これも対象に含める。
    const reasonKeyFor = (itemId: string): string =>
      rowIndex === undefined ? itemId : clearedReasonItemKey(itemId, rowId);
    const reasonItemIds = new Set<string>();
    const searchItemId = master.search_item_id || '';
    if (searchItemId) reasonItemIds.add(searchItemId);

    // 解除対象の item_id（照合キー本体＋取得項目）。
    //   本体（search_item_id）      : 変換前の OCR 値（ocr_value）に戻す
    //   取得項目（column_mappings） : 照合で取得した値なので空にクリアする
    const targetItemIds = new Set<string>();
    if (searchItemId) targetItemIds.add(searchItemId);
    master.column_mappings?.forEach(({ item_id }) => {
      if (item_id) {
        targetItemIds.add(item_id);
        reasonItemIds.add(item_id);
      }
    });

    setClearedReasonItemKeys(prev => {
      const next = new Set(prev);
      reasonItemIds.forEach(id => next.add(reasonKeyFor(id)));
      return next;
    });

    // 戻す値を決める。本体は OCR 値に、取得項目は空に。
    // 照合キー本体は、格納先に指定していなければ照合時も値を書き換えていない（#2915）ため、
    // 現在の value が OCR 読取値そのもの。格納先に指定した場合（#3339）は上書きされるが、
    // その際 ocr_value に元の読取値が退避される。
    // よって ocr_value があればそれを優先し、無ければ現在の value を維持する。
    const currentResultData = (resultRecord?.result_data ?? {}) as Record<string, unknown>;
    const pickRestoreValue = (cell: unknown, itemId: string): string => {
      if (itemId !== searchItemId) return '';
      if (cell && typeof cell === 'object') {
        const c = cell as { ocr_value?: unknown; value?: unknown };
        if (typeof c.ocr_value === 'string' && c.ocr_value !== '') return c.ocr_value;
        if (typeof c.ocr_value === 'number') return String(c.ocr_value);
        if (typeof c.value === 'string') return c.value;
        if (typeof c.value === 'number') return String(c.value);
      }
      return '';
    };

    // フォーム反映（単一値項目 / 配列子項目）。即時保存はせずローカルのみ。
    const dynamicUpdates = new Map<string, Map<string, unknown>>();
    targetItemIds.forEach(itemId => {
      for (const itemGroup of patternData?.item_list ?? []) {
        if (
          itemGroup.id === itemId &&
          itemGroup.type !== 'array' &&
          (!itemGroup.children || itemGroup.children.length === 0)
        ) {
          const fieldName = toFormFieldName(itemGroup.parentValue);
          setValue(
            fieldName as never,
            pickRestoreValue(currentResultData[itemId], itemId) as never,
          );
          break;
        }
        const childField = itemGroup.children?.find((c: ItemListChildType) => c.id === itemId);
        if (childField) {
          const parentArr = currentResultData[itemGroup.id];
          // result_data は保存時点のスナップショットなので行ID がそのまま位置になる（#3283）
          const targetRow = rowId;
          const cell =
            Array.isArray(parentArr) && parentArr[targetRow]
              ? (parentArr[targetRow] as Record<string, unknown>)[itemId]
              : undefined;
          const existing = dynamicUpdates.get(itemGroup.parentValue) ?? new Map();
          existing.set(childField.childValue, pickRestoreValue(cell, itemId));
          dynamicUpdates.set(itemGroup.parentValue, existing);
          break;
        }
      }
    });

    if (dynamicUpdates.size > 0) {
      setDynamicFormData(prev => {
        const next: DynamicFormData = { ...prev };
        dynamicUpdates.forEach((childUpdates, parentValue) => {
          const rows = [...(next[parentValue] ?? [])];
          const targetRow = toRowPosition(parentValue, rowId);
          while (rows.length <= targetRow) {
            rows.push({ index: rows.length });
          }
          rows[targetRow] = { ...rows[targetRow] };
          childUpdates.forEach((v, childValue) => {
            rows[targetRow][childValue] = v as DynamicFieldValue[string];
          });
          next[parentValue] = rows;
        });
        return next;
      });
    }

    // バリデーション再評価（値が変わったため）
    setValidationRevalidateTrigger(prev => prev + 1);
    // 解除で戻した値を参照している計算フィールドを再計算する（#3172）
    requestRecalculation();
  };

  // 辞書変換モーダルで候補を選択したときの反映（マスタ照合の反映ロジックをミラーリング）
  const handleDictionarySelect = (result: DictionaryMatchResult): void => {
    if (!dictionaryReconciliationTarget) return;
    const { dictionary, rowIndex } = dictionaryReconciliationTarget;

    // 手動変換の内容を変換履歴の保存候補として保持する（ステータス確定時に確認モーダルを出す）。
    const dictQueryText = String(
      dictionaryReconciliationTarget.ocrValue ?? result.matchedValue ?? '',
    );
    if (dictQueryText) {
      // rowIndex には行ID が入る（#3283）
      const histKey = dictionarySelectionKey(dictionary.dictionary_id, rowIndex ?? 0);
      setSelectedDictionaryHistories(prev => {
        const next = new Map(prev);
        next.set(histKey, {
          dictionary_id: dictionary.dictionary_id,
          query_text: dictQueryText,
          matched_value: result.matchedValue,
          matched_row: result.rowData,
          search_item_id: dictionary.search_item_id,
          search_item_label: dictionary.search_item_label,
        });
        return next;
      });
    }

    // フォーム反映対象 (item_id → value)。値を実際に書き換える項目のみを集める。
    // - 変換キー自体 (search_item_id): 選択した辞書レコードの変換先列の値（#3274）。
    //   従来は OCR 読取値を残していたが、手動で選び直した以上辞書の表記に揃えるのが正。
    //   上書き前の値は pickOcrValue で ocr_value へ退避され、「OCR結果：」表示は維持される。
    // - column_mappings: 各カラムの rowData 値
    const updatesByItemId = new Map<string, unknown>();
    if (dictionary.search_item_id && result.matchedValue) {
      updatesByItemId.set(dictionary.search_item_id, result.matchedValue);
    }
    if (result.rowData && dictionary.column_mappings?.length > 0) {
      dictionary.column_mappings.forEach(({ csv_column, item_id }) => {
        const value = result.rowData[csv_column];
        if (value === undefined || !item_id) return;
        updatesByItemId.set(item_id, value);
      });
    }

    // 配列項目（行ごとに値を持つ）は setDynamicFormData で更新する必要があるため、
    // まず親キーごとにまとめてから1回の state 更新でまとめて反映する。
    const dynamicUpdates = new Map<string, Map<string, unknown>>(); // parentValue → (childValue → value)

    updatesByItemId.forEach((value, itemId) => {
      // item_list を走査:
      //   - トップレベル itemGroup.id === itemId かつ単一値項目 → setValue 反映
      //   - children 配下に id === itemId の childField があれば → dynamicFormData 反映
      //   - どちらにも該当しなければ無反映でスキップ
      for (const itemGroup of patternData?.item_list ?? []) {
        if (
          itemGroup.id === itemId &&
          itemGroup.type !== 'array' &&
          (!itemGroup.children || itemGroup.children.length === 0)
        ) {
          const fieldName = toFormFieldName(itemGroup.parentValue);
          setValue(fieldName as never, value as never);
          break;
        }
        const childField = itemGroup.children?.find((c: ItemListChildType) => c.id === itemId);
        if (childField) {
          const existing = dynamicUpdates.get(itemGroup.parentValue) ?? new Map();
          existing.set(childField.childValue, value);
          dynamicUpdates.set(itemGroup.parentValue, existing);
          break;
        }
      }
    });

    if (dynamicUpdates.size > 0) {
      setDynamicFormData(prev => {
        const next: DynamicFormData = { ...prev };
        const targetRowId = rowIndex ?? 0;
        dynamicUpdates.forEach((childUpdates, parentValue) => {
          const rows = [...(next[parentValue] ?? [])];
          // 行ID から現在の配列位置へ変換して書き込む（#3283）
          const targetRow = toRowPosition(parentValue, targetRowId);
          // 行が存在しない場合は最低限の枠を作成
          while (rows.length <= targetRow) {
            rows.push({ index: rows.length });
          }
          rows[targetRow] = { ...rows[targetRow] };
          childUpdates.forEach((v, childValue) => {
            rows[targetRow][childValue] = v as DynamicFieldValue[string];
          });
          next[parentValue] = rows;
        });
        return next;
      });
    }

    // Firestore の result_data に「辞書再変換」ラベルを書き込み、画面のラベル表示に反映する。
    // マスタ照合の手動再照合と同じく、自動変換と同経路（result_data の match_reason_label）で表示させる。
    const dictMatchReasonLabel = `辞書再変換（変換結果：${result.matchedValue}）`;
    // matchRate（文字数差を補正した一致度）を採用。モーダル表示・自動変換の confidence と揃える。
    const dictScoreLabel = `${Math.round(result.matchRate * 1000) / 10}%`;
    void (async (): Promise<void> => {
      if (!resultId) return;
      try {
        // rowIndex には行ID が入る（#3283）。result_data は保存時点のスナップショットなので
        // 行ID でそのまま引き、dynamicFormData（現在の並び）だけ位置へ変換する。
        const rowId = rowIndex ?? 0;
        const resultRef = doc(db, 'results', resultId);
        const snap = await getDoc(resultRef);
        const currentResultData = (snap.data()?.result_data ?? {}) as Record<string, unknown>;
        const updatePayload: DocumentData = {};
        // 画面表示用 result_data（resultRecord）にも同じ更新を反映するためのローカルコピー。
        // updateDoc は dotNotation 混在で setResultRecord にそのまま使えないため別途構築する。
        const nextLocalResultData: Record<string, unknown> = { ...currentResultData };

        // 既存フィールドに ocr_value が未保存なら、上書き前の value を ocr_value に保存しておく。
        const pickOcrValue = (existing: unknown): string | undefined => {
          if (!existing || typeof existing !== 'object') return undefined;
          const e = existing as { ocr_value?: unknown; value?: unknown };
          if (typeof e.ocr_value === 'string' && e.ocr_value !== '') return undefined;
          if (typeof e.value === 'string' && e.value !== '') return e.value;
          if (typeof e.value === 'number') return String(e.value);
          return undefined;
        };

        // 書き込み対象 (itemId → value)。value が undefined の項目は値を据え置き、
        // メタ（ラベル・スコア）のみ更新する。
        // 変換キー (search_item_id) はラベル/スコアの書き込み先として先に undefined で登録し、
        // updatesByItemId に同じ item_id の値があれば上書きされて値も反映される（#3274）。
        const metaUpdates = new Map<string, unknown>();
        if (dictionary.search_item_id) {
          metaUpdates.set(dictionary.search_item_id, undefined);
        }
        updatesByItemId.forEach((value, itemId) => {
          metaUpdates.set(itemId, value);
        });

        metaUpdates.forEach((value, itemId) => {
          // value 未指定なら値の差し替えは行わずメタのみ更新する。
          const writesValue = value !== undefined;
          for (const itemGroup of patternData?.item_list ?? []) {
            // 単一値項目: dotNotation でメタだけ更新
            if (
              itemGroup.id === itemId &&
              itemGroup.type !== 'array' &&
              (!itemGroup.children || itemGroup.children.length === 0)
            ) {
              const localCell = {
                ...((nextLocalResultData[itemId] ?? {}) as Record<string, unknown>),
              };
              if (writesValue) {
                const ocrValueToSave = pickOcrValue(currentResultData[itemId]);
                if (ocrValueToSave !== undefined) {
                  updatePayload[`result_data.${itemId}.ocr_value`] = ocrValueToSave;
                  localCell.ocr_value = ocrValueToSave;
                }
                updatePayload[`result_data.${itemId}.value`] = value;
                localCell.value = value;
              }
              updatePayload[`result_data.${itemId}.match_reason_label`] = dictMatchReasonLabel;
              updatePayload[`result_data.${itemId}.score`] = dictScoreLabel;
              // 変換理由コードも一緒に更新する（#3339）。
              // 判定は match_reason コードを最優先する（#3309）ため、ラベルだけ
              // 書き換えると OCR 時の古いコードが残り、再変換で選び直しても
              // 「辞書に該当なし」などの要注意が消えない。
              // 再変換は人が候補を見て確定させた選択なので確認済みとして扱う。
              updatePayload[`result_data.${itemId}.match_reason`] = matchReason.confirmed;
              // ローカル表示用にも同じメタを反映
              localCell.match_reason_label = dictMatchReasonLabel;
              localCell.score = dictScoreLabel;
              localCell.match_reason = matchReason.confirmed;
              nextLocalResultData[itemId] = localCell;
              return;
            }
            // 配列子項目: 配列全体を読み出して該当行のフィールドを差し替える
            const childField = itemGroup.children?.find((c: ItemListChildType) => c.id === itemId);
            if (childField) {
              const parentKey = itemGroup.id;
              const existingArr = Array.isArray(currentResultData[parentKey])
                ? [...(currentResultData[parentKey] as Record<string, unknown>[])]
                : [];
              // result_data は保存時点のスナップショットなので行ID がそのまま位置になる（#3283）
              const targetRow = rowId;
              while (existingArr.length <= targetRow) existingArr.push({});
              const row = { ...(existingArr[targetRow] ?? {}) } as Record<string, unknown>;
              const cell = { ...((row[itemId] ?? {}) as Record<string, unknown>) };
              if (writesValue) {
                const ocrValueToSave = pickOcrValue(cell);
                if (ocrValueToSave !== undefined) {
                  cell.ocr_value = ocrValueToSave;
                }
                cell.value = value;
              }
              cell.match_reason_label = dictMatchReasonLabel;
              cell.score = dictScoreLabel;
              // 単一項目と同じ理由で変換理由コードも更新する（#3339）
              cell.match_reason = matchReason.confirmed;
              row[itemId] = cell;
              existingArr[targetRow] = row;
              currentResultData[parentKey] = existingArr;
              updatePayload[`result_data.${parentKey}`] = existingArr;
              // ローカル表示用にも同じ配列を反映
              nextLocalResultData[parentKey] = existingArr;
              return;
            }
          }
        });

        if (Object.keys(updatePayload).length > 0) {
          await updateDoc(resultRef, updatePayload);
          // 保存成功後に画面表示用 state も同期し、照合理由ラベルを即時表示する。
          setResultRecord(prev => (prev ? { ...prev, result_data: nextLocalResultData } : prev));
        }
      } catch {
        // 反映失敗してもフォーム反映済みのUI状態は維持。ラベルが出ないだけなのでサイレントに無視。
      }
    })();

    // 辞書変換で値が埋まったので、明細セルのバリデーション（#2815）を再評価してエラー表示を更新する。
    setValidationRevalidateTrigger(prev => prev + 1);
    // 反映した値を参照している計算フィールドを再計算する（#3172）
    requestRecalculation();

    setDictionaryModalOpen(false);
    setDictionaryReconciliationTarget(null);
  };

  // 辞書変換を解除する（#2866）。辞書は OCR 読取値そのものを変換するため、
  // 解除時は変換した項目（search_item_id と column_mappings の item_id）を
  // 変換前の OCR 値（result_data の ocr_value）に戻し、ラベル/スコアを消す。
  // 反映は handleDictionarySelect と同じく即 Firestore に書き戻す。
  const handleDictionaryUnlink = (
    dictionary: DictionaryWithMappingType,
    rowIndex?: number,
  ): void => {
    // rowIndex には行ID が入る（#3283）。履歴キーは行ID、配列参照は位置へ変換して使う
    const rowId = rowIndex ?? 0;
    const histKey = dictionarySelectionKey(dictionary.dictionary_id, rowId);

    // 変換履歴の保存候補からも取り消す。
    setSelectedDictionaryHistories(prev => {
      const next = new Map(prev);
      next.delete(histKey);
      return next;
    });

    // 解除対象の item_id を2分類する。
    //   本体（search_item_id）        : 変換した読取値を OCR 値に戻す
    //   取得項目（column_mappings）   : 辞書から取得した値なので空にクリアする
    const targetItemIds = new Set<string>();
    const searchItemId = dictionary.search_item_id || '';
    if (searchItemId) targetItemIds.add(searchItemId);
    dictionary.column_mappings?.forEach(({ item_id }) => {
      if (item_id) targetItemIds.add(item_id);
    });
    if (targetItemIds.size === 0) return;

    void (async (): Promise<void> => {
      if (!resultId) return;
      try {
        const resultRef = doc(db, 'results', resultId);
        const snap = await getDoc(resultRef);
        const currentResultData = (snap.data()?.result_data ?? {}) as Record<string, unknown>;
        const updatePayload: DocumentData = {};
        const nextLocalResultData: Record<string, unknown> = { ...currentResultData };
        const dynamicUpdates = new Map<string, Map<string, unknown>>();

        // 戻す値を決める。
        //   本体（search_item_id）: OCR 値に戻す
        //   取得項目             : 空にクリアする
        // 本体は変換時に辞書の値で上書きしている（#3274）ため、退避済みの ocr_value を優先する。
        // 上書きしていなかった頃（#2915 以前）のデータは ocr_value を持たないので、
        // その場合は現在の value（= OCR 読取値そのもの）を維持する。
        const pickRestoreValue = (cell: unknown, itemId: string): string => {
          if (itemId !== searchItemId) return '';
          if (cell && typeof cell === 'object') {
            const c = cell as { ocr_value?: unknown; value?: unknown };
            if (typeof c.ocr_value === 'string' && c.ocr_value !== '') return c.ocr_value;
            if (typeof c.ocr_value === 'number') return String(c.ocr_value);
            if (typeof c.value === 'string') return c.value;
            if (typeof c.value === 'number') return String(c.value);
          }
          return '';
        };

        targetItemIds.forEach(itemId => {
          for (const itemGroup of patternData?.item_list ?? []) {
            // 単一値項目
            if (
              itemGroup.id === itemId &&
              itemGroup.type !== 'array' &&
              (!itemGroup.children || itemGroup.children.length === 0)
            ) {
              const restoreValue = pickRestoreValue(currentResultData[itemId], itemId);
              const fieldName = toFormFieldName(itemGroup.parentValue);
              setValue(fieldName as never, restoreValue as never);
              updatePayload[`result_data.${itemId}.value`] = restoreValue;
              updatePayload[`result_data.${itemId}.match_reason_label`] = '';
              updatePayload[`result_data.${itemId}.score`] = '';
              // ラベルと同時に変換理由コードも消す（#3339）。
              // 残すと OCR 時の no_match などが効き続け、解除したのに要注意が消えない。
              updatePayload[`result_data.${itemId}.match_reason`] = '';
              const localCell = {
                ...((nextLocalResultData[itemId] ?? {}) as Record<string, unknown>),
                value: restoreValue,
                match_reason_label: '',
                score: '',
                match_reason: '',
              };
              nextLocalResultData[itemId] = localCell;
              return;
            }
            // 配列子項目
            const childField = itemGroup.children?.find((c: ItemListChildType) => c.id === itemId);
            if (childField) {
              const parentKey = itemGroup.id;
              const existingArr = Array.isArray(currentResultData[parentKey])
                ? [...(currentResultData[parentKey] as Record<string, unknown>[])]
                : [];
              // result_data は保存時点のスナップショットなので行ID がそのまま位置になる（#3283）
              const targetRow = rowId;
              while (existingArr.length <= targetRow) existingArr.push({});
              const row = { ...(existingArr[targetRow] ?? {}) } as Record<string, unknown>;
              const cell = { ...((row[itemId] ?? {}) as Record<string, unknown>) };
              const restoreValue = pickRestoreValue(cell, itemId);
              cell.value = restoreValue;
              cell.match_reason_label = '';
              cell.score = '';
              // 単一項目と同じ理由で変換理由コードも消す（#3339）
              cell.match_reason = '';
              row[itemId] = cell;
              existingArr[targetRow] = row;
              currentResultData[parentKey] = existingArr;
              updatePayload[`result_data.${parentKey}`] = existingArr;
              nextLocalResultData[parentKey] = existingArr;
              // フォーム（dynamicFormData）も復元
              const existing = dynamicUpdates.get(itemGroup.parentValue) ?? new Map();
              existing.set(childField.childValue, restoreValue);
              dynamicUpdates.set(itemGroup.parentValue, existing);
              return;
            }
          }
        });

        if (dynamicUpdates.size > 0) {
          setDynamicFormData(prev => {
            const next: DynamicFormData = { ...prev };
            dynamicUpdates.forEach((childUpdates, parentValue) => {
              const rows = [...(next[parentValue] ?? [])];
              const targetRow = toRowPosition(parentValue, rowId);
              while (rows.length <= targetRow) {
                rows.push({ index: rows.length });
              }
              rows[targetRow] = { ...rows[targetRow] };
              childUpdates.forEach((v, childValue) => {
                rows[targetRow][childValue] = v as DynamicFieldValue[string];
              });
              next[parentValue] = rows;
            });
            return next;
          });
        }

        if (Object.keys(updatePayload).length > 0) {
          await updateDoc(resultRef, updatePayload);
          setResultRecord(prev => (prev ? { ...prev, result_data: nextLocalResultData } : prev));
        }
      } catch {
        // 反映失敗時はサイレントに無視（フォーム側の復元は反映済み）。
      }
      // 解除で戻した値を参照している計算フィールドを再計算する（#3172）
      // （復元値の反映が非同期のため、この async 内の最後で予約する）
      requestRecalculation();
    })();

    setValidationRevalidateTrigger(prev => prev + 1);
  };

  // 辞書変換の変換履歴がある場合は確認モーダルを表示。無ければそのまま完了処理に進む。
  // マスタ照合の確認の後に呼ばれることを想定（マスタ → 辞書 → handleResults のチェーン）。
  const openDictionaryHistoryConfirmOrFinish = (nextStatus: number): void => {
    if (selectedDictionaryHistories.size > 0) {
      const items: DictionaryHistoryItem[] = Array.from(selectedDictionaryHistories.values()).map(
        v => ({
          label: v.search_item_label,
          ocrValue: v.query_text,
          matchedName: v.matched_value,
        }),
      );
      setDictionaryHistoryItems(items);
      setDictionaryHistoryNextStatus(nextStatus);
      setDictionaryHistoryConfirmOpen(true);
    } else {
      handleResults(nextStatus);
    }
  };

  // 保存前の照合・変換確認モーダルを表示（マスタ照合を優先し、続けて辞書変換を確認する）
  const openHistoryConfirm = (nextStatus: number): void => {
    if (selectedMasterIds.size > 0) {
      const items: MasterHistoryItem[] = Array.from(selectedMasterIds.values()).map(v => ({
        label: v.label,
        ocrValue: v.ocr_value,
        matchedName: v.matched_label,
        // スコープ（取引先など）が設定されている場合のみ表示する。
        supplierValue: v.supplier_id,
        supplierLabel: v.supplier_label,
      }));
      setMasterHistoryItems(items);
      setMasterHistoryNextStatus(nextStatus);
      setMasterHistoryConfirmOpen(true);
    } else {
      // マスタ履歴が無ければ、辞書変換の変換履歴を確認する
      openDictionaryHistoryConfirmOrFinish(nextStatus);
    }
  };

  // マスタ照合の保存確認モーダルで「保存する」→ 選択された項目だけ保存し、辞書変換の確認へチェーン。
  // selectedIndexes はモーダルに渡した items（= selectedMasterIds の値配列）と同じ並びのインデックス。
  const handleMasterHistoryConfirm = (selectedIndexes: number[]): void => {
    setMasterHistoryConfirmOpen(false);
    const companyId = effectiveCompanyId ?? '';
    if (companyId) {
      const values = Array.from(selectedMasterIds.values());
      const selectedSet = new Set(selectedIndexes);
      values.forEach((v, index) => {
        if (!selectedSet.has(index)) return;
        if (v.master_record_id !== undefined) {
          void saveMasterHistory({
            gendrive_company_id: companyId,
            source_field_name: v.source_field_name,
            query_text: v.ocr_value,
            matched_master_record_id: v.master_record_id,
            // スコープ（取引先など）。設定時はそのスコープに紐づく履歴として保存。
            supplier_id: v.supplier_id,
          });
        }
      });
    }
    setSelectedMasterIds(new Map());
    openDictionaryHistoryConfirmOrFinish(masterHistoryNextStatus);
  };

  // 辞書変換の保存確認モーダルで「保存する」→ 変換履歴を Firestore に保存してから完了処理
  // 辞書変換の保存確認モーダルで「保存する」→ 選択された項目だけ変換履歴に保存する。
  // selectedIndexes はモーダルに渡した items（= selectedDictionaryHistories の値配列）と同じ並びのインデックス。
  const handleDictionaryHistoryConfirm = (selectedIndexes: number[]): void => {
    setDictionaryHistoryConfirmOpen(false);
    const values = Array.from(selectedDictionaryHistories.values());
    const selected = new Set(selectedIndexes);
    values.forEach((v, i) => {
      if (!selected.has(i)) return;
      void saveDictionaryHistory({
        dictionaryId: v.dictionary_id,
        queryText: v.query_text,
        matchedValue: v.matched_value,
        matchedRow: v.matched_row,
        searchItemId: v.search_item_id,
        searchItemLabel: v.search_item_label,
      });
    });
    setSelectedDictionaryHistories(new Map());
    handleResults(dictionaryHistoryNextStatus);
  };

  // ボタンの処理（#2815: 確認済み化のときはバリデーションエラー確認ダイアログを挟む）
  const handleProcessButton = (): void => {
    if (
      isProcessingStatus === processingStatus.draft ||
      isProcessingStatus === processingStatus.processing
    ) {
      // 確認済みにする操作：エラーがあれば確認ダイアログを表示
      runWithValidationConfirm('確認済みに', () => {
        openHistoryConfirm(processingStatus.completed);
      });
    } else if (
      isProcessingStatus === processingStatus.completed ||
      isProcessingStatus === processingStatus.outputted
    ) {
      // 編集に戻す操作：確認不要
      handleResults(processingStatus.processing);
    }
  };

  // ファイルのダウンロード
  const handleDownload = async (): Promise<void> => {
    try {
      const response = await fetch(originalFileUrl);
      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);

      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      window.URL.revokeObjectURL(downloadUrl);
    } catch (error) {
      setErrorText('ファイルのダウンロードに失敗しました。');
    }
  };

  // OCR結果1件のファイル出力
  // 指定フォーマット出力（#3070）: このパターンに登録済みのフォーマットを読み込む
  useEffect(() => {
    const load = async (): Promise<void> => {
      // 運営が代理ログインせずに見ているときは effectiveCompanyId が null になるため、
      // 自社（運営企業）のIDで引く。他の取得処理と揃える
      const companyId = effectiveCompanyId || user?.company_id;
      if (!patternId || !companyId) return;
      try {
        setExportTemplates(await getExportTemplates(patternId, companyId));
      } catch {
        // フォーマットが取れなくても通常の出力は使えるため、握りつぶして空のままにする
        setExportTemplates([]);
      }
    };
    void load();
  }, [patternId, effectiveCompanyId, user?.company_id]);

  /**
   * 指定フォーマット出力（#3070）を実行する。
   * 流し込みはサーバ側で行い、生成された .xlsx を受け取ってダウンロードする。
   */
  const handleTemplateExport = async (
    template: ExportTemplate,
    customFileName?: string,
    moveToOutputted?: boolean,
  ): Promise<void> => {
    if (!resultId || !template.id || !patternId || !patternData || !user || !resultRecord) return;
    try {
      const res = await generateExportTemplateFile({
        template_id: template.id,
        result_id: resultId,
      });

      // ユーザー指定のファイル名があればそれを優先する（拡張子は .xlsx 固定）
      const trimmedName = customFileName?.trim();
      const fileName = trimmedName ? `${trimmedName}.xlsx` : res.file_name;

      // 通常の出力と同じく履歴に残す。Storage への保存と履歴の更新は
      // 既存の exportFile（Functions）に任せる
      const historyId = await createFileExportHistory({
        pattern_id: patternId,
        pattern_name: patternData.pattern_name,
        company_id: effectiveCompanyId ?? user.company_id ?? '',
        file_name: fileName,
        file_type: 'xlsx',
        created_by: user.uid,
        created_by_name: isProxyMode ? PROXY_DISPLAY_NAME : user.name,
        created_by_icon_url: isProxyMode ? '' : user.icon || '',
        is_proxy: isProxyMode || false,
      });

      // Base64 の実バイト数（末尾の = を除いた長さから算出する）
      const base64Length = res.file_content.replace(/=+$/, '').length;
      const fileSize = Math.floor((base64Length * 3) / 4);

      await exportFile({
        history_id: historyId,
        file_content: res.file_content,
        file_size: fileSize,
        result_ids: [resultId],
        result_count: 1,
      });

      // ここでは手元にダウンロードしない。この後に遷移する出力履歴画面が
      // 「出力中 → 完了」を検知して自動ダウンロードするため、両方やると2重に落ちる。
      // 通常の CSV/Excel 出力と同じく履歴経由に揃える（#3207）

      // 出力済ステータスへの移行（通常の出力と同じ扱い）
      if (moveToOutputted) {
        try {
          await updateDoc(doc(db, 'results', resultId), {
            processing_status: processingStatus.outputted,
            updated_at: Timestamp.now(),
            updated_by: user.uid,
          });
        } catch {
          // ステータス更新の失敗はファイル出力自体を失敗にしない
        }
      }

      // あふれ・未解決プレースホルダは出力を止めずに知らせる（仕様書 §3.4）
      if (res.warnings.length > 0) {
        setSnackbarText(res.warnings.map(w => w.message).join(' / '));
      } else {
        setSnackbarText('指定フォーマットで出力しました');
      }

      // 通常の出力と同じく出力履歴の画面へ移す（#3207）
      const currentResultStatus =
        typeof resultRecord.processing_status === 'number'
          ? resultRecord.processing_status
          : processingStatus.completed;
      navigate(`/ai-ocr/pattern/${patternId}/exports${buildResultTabQuery(currentResultStatus)}`);
    } catch (error) {
      setErrorText(getUserErrorMessage(error, '指定フォーマットでの出力に失敗しました'));
      throw error;
    }
  };

  const handleFileExport = async (
    fileType: (typeof exportFileType)[keyof typeof exportFileType],
    encoding: (typeof exportEncoding)[keyof typeof exportEncoding],
    selectedFields: string[],
    sortedPattern?: PatternType,
    moveToOutputted?: boolean,
    saveSetting?: boolean,
    selectedFixedFields?: string[],
    sortedUnifiedOrder?: string[],
    customFileName?: string,
    customHeaders?: Record<string, string>,
    quoteMode?: (typeof exportQuoteMode)[keyof typeof exportQuoteMode],
  ): Promise<void> => {
    if (!patternId || !patternData || !user || !resultRecord || !resultId) {
      setErrorText('読取パターン情報が見つかりません');
      return;
    }

    // 設定保存処理
    if (saveSetting && patternData) {
      try {
        const itemListToUse = sortedPattern?.item_list || patternData.item_list;

        const sortedChildOrder: Record<string, string[]> = {};
        itemListToUse.forEach(item => {
          if (item.children?.length > 0) {
            sortedChildOrder[item.id] = item.children.map(child => child.id);
          }
        });

        const sortedItemOrder = sortedUnifiedOrder || itemListToUse.map(item => item.id);

        const exportSettings: ExportSettingsType = {
          move_to_outputted: moveToOutputted ?? true,
          file_type: fileType,
          encoding,
          selected_fields: selectedFields,
          selected_fixed_fields: selectedFixedFields,
          sorted_item_order: sortedItemOrder,
          sorted_child_order: sortedChildOrder,
          custom_headers: customHeaders ?? {},
          quote_mode: quoteMode ?? exportQuoteMode.minimal,
        };

        const patternRef = doc(db, 'patterns', patternData.patternId);
        await updateDoc(patternRef, {
          export_settings: exportSettings,
          updated_at: Timestamp.now(),
        });
        setPatternData(prev => (prev ? { ...prev, export_settings: exportSettings } : prev));
      } catch {
        // 設定保存失敗はエクスポート処理を止めない
      }
    }

    try {
      const patternToUse = sortedPattern || patternData;
      const currentResultStatus =
        typeof resultRecord.processing_status === 'number'
          ? resultRecord.processing_status
          : processingStatus.completed;

      // Firestoreに履歴を作成（出力中ステータス）。遷移先のonSnapshotで反映される。
      // ユーザー指定のファイル名があればそれを優先、なければ自動生成
      const ext = fileType === exportFileType.excel ? '.xlsx' : '.csv';
      const trimmedCustomName = customFileName?.trim();
      const tempFileName = trimmedCustomName
        ? `${trimmedCustomName}${ext}`
        : generateFileName(patternToUse, currentResultStatus, fileType, encoding);

      const historyId = await createFileExportHistory({
        pattern_id: patternId,
        pattern_name: patternData.pattern_name,
        company_id: effectiveCompanyId ?? user.company_id ?? '',
        file_name: tempFileName,
        file_type: fileType === exportFileType.excel ? 'xlsx' : 'csv',
        created_by: user.uid,
        created_by_name: isProxyMode ? PROXY_DISPLAY_NAME : user.name,
        created_by_icon_url: isProxyMode ? '' : user.icon || '',
        is_proxy: isProxyMode || false,
      });

      // 履歴作成成功後に画面遷移（出力中ステータスが即座に表示される）
      navigate(`/ai-ocr/pattern/${patternId}/exports${buildResultTabQuery(currentResultStatus)}`);

      // 重い処理（データ整形・ファイル生成・アップロード）はバックグラウンドで継続
      (async (): Promise<void> => {
        try {
          // 表示中の1件を出力対象とする
          const exportData = await generatePatternExportData(
            [resultRecord as Record<string, unknown>],
            patternToUse,
            selectedFields,
            selectedFixedFields,
          );
          const columns = generateExportColumns(
            patternToUse,
            selectedFields,
            selectedFixedFields,
            sortedUnifiedOrder,
            customHeaders,
          );

          // ファイル生成（Blob作成）
          const fileBlob = await buildExportBlob(exportData, columns, fileType, encoding, {
            quoteMode,
          });

          // BlobをBase64に変換
          const fileContent = await new Promise<string>((resolve, reject): void => {
            const reader = new FileReader();
            reader.onloadend = (): void => {
              const base64String = (reader.result as string).split(',')[1];
              resolve(base64String);
            };
            reader.onerror = reject;
            reader.readAsDataURL(fileBlob);
          });

          // Cloud Functionsでファイルアップロード
          // 単一 result の出力ページから呼ばれるため result_ids は当該 1 件
          await exportFile({
            history_id: historyId,
            file_content: fileContent,
            file_size: fileBlob.size,
            encoding: encoding === exportEncoding.shiftJis ? 'sjis' : 'utf8',
            result_ids: [resultId],
            result_count: 1,
          });

          // 出力済ステータスへの移行
          if (moveToOutputted) {
            try {
              const resultRef = doc(db, 'results', resultId);
              await updateDoc(resultRef, {
                processing_status: processingStatus.outputted,
                updated_at: Timestamp.now(),
                updated_by: user.uid,
              });
            } catch {
              // ステータス更新失敗はファイル出力自体を失敗にしない
            }
          }
        } catch (error) {
          Sentry.captureException(error);
          // エラーはCloud Function側でFirestoreのステータスを更新する
        }
      })();
    } catch (error) {
      Sentry.captureException(error);
      // 履歴作成までで失敗したケース。遷移していないのでソース画面でスナックバー通知。
      setErrorText('ファイル出力に失敗しました。');
      // 失敗をダイアログに伝播し、モーダルを開いたままリトライ可能にする
      throw error;
    }
  };

  const handleOpenRuleImprovement = async (): Promise<void> => {
    if (!resultId || !patternData) return;
    // キャッシュがあれば再利用（下書き保存でクリアされる）
    if (cachedRuleSuggestionsRef.current !== null) {
      setRuleSuggestions(cachedRuleSuggestionsRef.current);
      setRuleImprovementOpen(true);
      return;
    }
    setRuleImprovementOpen(true);
    setRuleImprovementLoading(true);
    setRuleSuggestions([]);
    try {
      const response = await handleSuggestPatternRule({
        resultId,
        patternId: patternData.patternId,
        // 照合に関わる項目は親・明細の列のどちらにもなりうる（#3314）。
        // 明細を持つ項目では照合の反映先が列IDになるため、children も絞る
        patternItemList: patternData.item_list
          .filter(item => !masterRelatedItemIds.has(item.id))
          .map(item => ({
            ...item,
            children: (item.children ?? []).filter(child => !masterRelatedItemIds.has(child.id)),
          })),
      });
      cachedRuleSuggestionsRef.current = response.suggestions;
      setRuleSuggestions(response.suggestions);
    } catch (error) {
      setRuleImprovementOpen(false);
      setErrorText(error instanceof Error ? error.message : 'AI改善提案の取得に失敗しました');
    } finally {
      setRuleImprovementLoading(false);
    }
  };

  const handleApplyRuleImprovements = async (approved: ApprovedSuggestion[]): Promise<void> => {
    if (!patternData || !user?.uid) return;
    const updatedItemList = patternData.item_list.map(item => {
      const match = approved.find(a => a.field_name === item.parentValue);
      if (match) {
        return { ...item, extractionRule: match.suggested_rule };
      }
      const updatedChildren = item.children.map(child => {
        const childMatch = approved.find(
          a => a.field_name === `${item.parentValue} > ${child.childValue}`,
        );
        if (childMatch) {
          return { ...child, extractionRule: childMatch.suggested_rule };
        }
        return child;
      });
      return { ...item, children: updatedChildren };
    });
    await updatePatternItemList(patternData.patternId, updatedItemList, user.uid);
    setPatternData({ ...patternData, item_list: updatedItemList });
    cachedRuleSuggestionsRef.current = null;
    setRuleImprovementOpen(false);
    setSnackbarText('取得ルールを更新しました');
  };

  /**
   * AI改善の提案を子パターンとして保存する（#2967）。
   *
   * 提案は field_name（親項目名、明細は「親 > 子」）で来るため、item_list を辿って
   * 項目IDに解決する。子パターンの上書きは項目IDで保持するため、項目名を変更しても追随できる。
   */
  const handleSaveSuggestionsAsSubPattern = (approved: ApprovedSuggestion[]): void => {
    if (!patternData) return;

    const initialOverrides: Record<string, string> = {};
    for (const item of patternData.item_list) {
      const parentMatch = approved.find(a => a.field_name === item.parentValue);
      if (parentMatch) initialOverrides[item.id] = parentMatch.suggested_rule;

      for (const child of item.children ?? []) {
        const childMatch = approved.find(
          a => a.field_name === `${item.parentValue} > ${child.childValue}`,
        );
        if (childMatch) initialOverrides[child.id] = childMatch.suggested_rule;
      }
    }

    setSubPatternInitialOverrides(initialOverrides);
    setRuleImprovementOpen(false);
    setSubPatternModalOpen(true);

    // 名称の重複判定に使うため既存子パターンを読み込む。
    // 取得できなくても作成自体は続けられるので、失敗しても画面は止めない
    getSubPatterns(patternData.patternId)
      .then(setExistingSubPatterns)
      .catch(() => setExistingSubPatterns([]));
  };

  /**
   * 子パターンの作成を開始する（#2967）。
   *
   * 帳票画像と項目定義をAIに渡して、この様式専用の取得ルールを提案させる。
   *
   * 対象は**ユーザーが値を直した項目だけ**に絞る。親パターンのルールを改善しても
   * 子パターンには伝播しないため、上書きが多いほど二重管理になり
   * メンテナンスが重くなる。直していない項目は現状のルールで正しく読めている。
   */
  const suggestSubPatternRulesForResult = async (): Promise<void> => {
    if (!patternData || !resultId) return;

    setSubPatternSuggesting(true);
    try {
      // 初回OCR値と現在値を比べて、直した項目だけを提案の対象にする
      const { changedIds, baselineResultData } = await collectSuggestionTargetsForResult(
        resultId,
        editingSubPattern,
      );
      const changedItemIds = new Set(changedIds);

      // 直した項目（明細は列単位）だけをAIへ渡す。
      // 明細は親ごと渡す必要があるため、変更された列がある明細だけを残して列を絞る
      const targetItemList = patternData.item_list
        .map(item => {
          const children = (item.children ?? []).filter(child => changedItemIds.has(child.id));
          if (children.length > 0) return { ...item, children };
          return changedItemIds.has(item.id) ? { ...item, children: [] } : null;
        })
        .filter((item): item is ItemListType => item !== null);

      // 行数を直した明細は、直す前に読み取っていた行も渡す（#3380）。
      // 何を明細として誤って拾っていたかが分からないと、その範囲を除外するルールを書けない
      const rowCountChanges = collectRowCountChanges(
        baselineResultData,
        resultRecord?.result_data as Record<string, unknown> | undefined,
        patternData.item_list.filter(item => changedItemIds.has(item.id)),
      );

      const response = await handleSuggestSubPatternRules(
        resultId,
        targetItemList,
        rowCountChanges,
      );

      // 提案は field_name（項目名）で来るため、item_list を辿って項目IDに解決する
      const initialOverrides: Record<string, string> = {};
      const initialItemNames: Record<string, string> = {};
      for (const item of targetItemList) {
        const parentMatch = response.suggestions.find(a => a.field_name === item.parentValue);
        if (parentMatch) {
          initialOverrides[item.id] = parentMatch.suggested_rule;
          // 親と同じ見出しなら入れない（上書きする意味がないため）
          const printed = (parentMatch.printed_item_name ?? '').trim();
          if (printed !== '' && printed !== item.parentValue) initialItemNames[item.id] = printed;
        }

        for (const child of item.children ?? []) {
          const childMatch = response.suggestions.find(
            a => a.field_name === `${item.parentValue} > ${child.childValue}`,
          );
          if (childMatch) {
            initialOverrides[child.id] = childMatch.suggested_rule;
            const printed = (childMatch.printed_item_name ?? '').trim();
            if (printed !== '' && printed !== child.childValue) {
              initialItemNames[child.id] = printed;
            }
          }
        }
      }

      setSubPatternInitialOverrides(initialOverrides);
      setSubPatternInitialItemNames(initialItemNames);
      // どの結果に対する提案かを覚えておく（別の結果へ移ったら無効になる）
      setSubPatternSuggestedResultId(resultId ?? null);
    } catch (error) {
      // 提案に失敗してもモーダルは開いたままにする。手入力で作成できるため
      setErrorText(error instanceof Error ? error.message : '取得ルールの提案に失敗しました');
    } finally {
      setSubPatternSuggesting(false);
    }
  };

  /**
   * 値を直した項目のIDを返す（#2967）。
   *
   * 初回OCR値（CREATE履歴）と現在の保存値を比べる。
   * 提案の対象を絞るのと、提案ボタンを押せるかの判定の両方で使う
   */
  /**
   * 編集中の子パターンで、すでに上書き設定がある項目ID（#2967）。
   *
   * 提案から除外する。手で書いたルールを提案で潰さないため、
   * 提案の対象は「まだ上書きしていない項目」だけにする
   */
  const overriddenItemIds = (variant: SubPatternType | null): Set<string> =>
    new Set((variant?.item_overrides ?? []).map(o => o.itemId));

  /**
   * 提案の対象になる項目IDと、比較の基準にした値（人が触る直前の状態）を返す。
   * 基準の値は、行数を直した明細の「直す前の行」をAIに渡すのにも使う（#3380）
   */
  const collectSuggestionTargetsForResult = async (
    targetResultId: string,
    variant: SubPatternType | null = null,
  ): Promise<{
    changedIds: string[];
    baselineResultData: Record<string, unknown> | null;
  }> => {
    if (!patternData) return { changedIds: [], baselineResultData: null };
    // 比較の左辺は「人が触る直前の状態」= 最新の全項目実行にする（#3318）。
    // CREATE履歴は初回OCR値のままで再読み取りを反映しないため、
    // 再読み取りで変わっただけの項目まで提案対象になってしまう。
    // 実行ログが無い過去データは従来どおり CREATE 履歴にフォールバックする。
    const executions = await getOcrExecutions(targetResultId, OCR_EXECUTION_FETCH_LIMIT).catch(
      () => [],
    );
    const baselineResultData = selectBaselineResultData(
      executions.map(execution => ({
        execution_type: execution.execution_type,
        result_data: execution.result_data,
        executed_at_ms: execution.executed_at.getTime(),
      })),
    );
    const createHistory = baselineResultData
      ? null
      : await getCreateHistory(targetResultId).catch(() => null);
    const initialResultData =
      baselineResultData ??
      (((createHistory?.changed_fields as Record<string, unknown>)?.result_data ?? null) as Record<
        string,
        unknown
      > | null);
    const changed = collectChangedItemIds(
      initialResultData,
      resultRecord?.result_data as Record<string, unknown> | undefined,
      // AI読取OFF・無効の項目は取得ルールが効かないため比較の対象にしない（#3314）
      filterSuggestableItems(patternData.item_list),
    );
    // すでに上書きしている項目は提案しない（手で書いたルールを潰さない）
    const alreadyOverridden = overriddenItemIds(variant);
    return {
      changedIds: changed.filter(id => !alreadyOverridden.has(id)),
      baselineResultData: initialResultData,
    };
  };

  const collectChangedItemIdsForResult = async (
    targetResultId: string,
    variant: SubPatternType | null = null,
  ): Promise<string[]> =>
    (await collectSuggestionTargetsForResult(targetResultId, variant)).changedIds;

  /**
   * 項目IDを画面に出す名前に変換する（#2967）。
   * 明細の列は「親項目名 > 子項目名」にして、どの列かが分かるようにする
   */
  const toChangedItemNames = (itemIds: string[]): string[] => {
    if (!patternData) return [];
    const ids = new Set(itemIds);
    const names: string[] = [];
    for (const item of patternData.item_list) {
      if (ids.has(item.id)) names.push(item.parentValue);
      for (const child of item.children ?? []) {
        if (ids.has(child.id)) names.push(`${item.parentValue} > ${child.childValue}`);
      }
    }
    return names;
  };

  const handleOpenSubPatternCreate = async (): Promise<void> => {
    if (!patternData || !resultId) return;

    setSubPatternModalOpen(true);

    // 名称の重複判定に使う既存の子パターン。取得できなくても作成は続けられる
    getSubPatterns(patternData.patternId)
      .then(setExistingSubPatterns)
      .catch(() => setExistingSubPatterns([]));

    // 提案の対象になる項目があるかを調べる（#2967）。
    // 取得に失敗した場合は「変更あり」に倒す。押せないより、押して空が返るほうが害が小さい
    void collectChangedItemIdsForResult(resultId)
      .then(ids => {
        setHasChangedItems(ids.length > 0);
        setChangedItemNames(toChangedItemNames(ids));
      })
      .catch(() => {
        setHasChangedItems(true);
        setChangedItemNames([]);
      });

    // 開いた時点では提案しない（#2967）。
    // AIは「ユーザーが直した正しい値」と帳票を見比べてルールを作るため、
    // 値を直す前に自動で走らせると精度が落ちる。モーダル内のボタンで明示的に実行する
  };

  /**
   * パターン一覧の「編集」から来たときは、最初から編集モーダルを開く（#2967）。
   *
   * 取得ルールは帳票を見ないと書けないため、一覧では編集させずこの画面へ送っている。
   * 遷移してきた本人は編集するつもりなので、もう一度クリックさせない。
   * 一度開いたら state を消す（再読み込みや戻る操作で開き直さないように）
   */
  useEffect(() => {
    const editSubPatternId = location.state?.editSubPatternId as string | undefined;
    if (!editSubPatternId || !patternData || subPatternModalOpen) return;

    // ?proxy= などのクエリは残す（消すと代理中に付け直しの遷移が余計に走るため）
    navigate(`${location.pathname}${location.search}`, { replace: true, state: {} });
    void openSubPatternEdit(editSubPatternId);
    // patternData の読み込みが終わってから開くため、両方を監視する
  }, [location.state, patternData]);

  /**
   * 適用中の子パターンを編集する（#2967）。
   *
   * 取得ルールは「帳票のどこから取るか」を書くものなので、帳票を見ながら編集する。
   * そのためパターン一覧では編集させず、この画面でモーダルを開く。
   */
  const openSubPatternEdit = async (targetSubPatternId?: string): Promise<void> => {
    if (!patternData) return;
    // 一覧から来た場合は子パターンIDが渡される。バッジから開いた場合は適用中の子パターンを使う
    const subPatternId =
      targetSubPatternId ?? (resultRecord?.sub_pattern_id as string | null | undefined);
    if (!subPatternId) return;
    try {
      const variants = await getSubPatterns(patternData.patternId);
      const target = variants.find(v => v.subPatternId === subPatternId);
      if (!target) {
        setErrorText('子パターンが見つかりません。削除された可能性があります');
        return;
      }
      setExistingSubPatterns(variants);
      setEditingSubPattern(target);
      setSubPatternModalOpen(true);

      // 編集時も、その時点で直した項目に対して提案できる（#2967）
      const targetResultId = resultId;
      if (targetResultId) {
        void collectChangedItemIdsForResult(targetResultId, target)
          .then(ids => {
            setHasChangedItems(ids.length > 0);
            setChangedItemNames(toChangedItemNames(ids));
          })
          .catch(() => {
            setHasChangedItems(true);
            setChangedItemNames([]);
          });
      }
    } catch {
      setErrorText('子パターンの取得に失敗しました');
    }
  };

  /**
   * 子パターンを選んで再読み取りする（#2967）。
   *
   * 未指定なら現在の子パターンを維持し、'base' なら標準で読み直す。
   */
  const handleReOcrWithSubPattern = (subPatternId?: string): void => {
    void executeReOcrAll(undefined, subPatternId);
  };

  /**
   * 子パターンを保存する（#2967）。編集中なら更新、そうでなければ新規作成に振り分ける。
   */
  const handleSubmitSubPattern = async (input: {
    subPatternName: string;
    description: string;
    itemOverrides: ItemOverrideType[];
  }): Promise<void> => {
    if (editingSubPattern) {
      await handleUpdateSubPattern(editingSubPattern, input);
      return;
    }
    await handleCreateSubPattern(input);
  };

  /** 既存の子パターンを更新する（#2967） */
  const handleUpdateSubPattern = async (
    target: SubPatternType,
    input: { subPatternName: string; description: string; itemOverrides: ItemOverrideType[] },
  ): Promise<void> => {
    if (!patternData || !user?.uid) return;
    try {
      setSubPatternSubmitting(true);
      await updateSubPattern({
        patternId: patternData.patternId,
        subPatternId: target.subPatternId,
        subPatternName: input.subPatternName,
        description: input.description,
        itemOverrides: input.itemOverrides,
        userId: user.uid,
        // 代理ログイン中は updated_by が運営スタッフのUIDになるため、
        // 一覧で「事務局」と出せるようフラグも残す（#3314）
        isProxy: isProxyMode,
        operatorId: isProxyMode ? operatorId || undefined : undefined,
      });

      // 結果に持っている子パターン名も更新する（名称を変えた場合にバッジがずれないように）
      if (resultId && resultRecord?.sub_pattern_id === target.subPatternId) {
        try {
          await updateDoc(doc(db, 'results', resultId), {
            sub_pattern_name: input.subPatternName,
            updated_at: Timestamp.now(),
          });
          setResultRecord(prev =>
            prev ? { ...prev, sub_pattern_name: input.subPatternName } : prev,
          );
        } catch {
          // バッジの名称が古いままになるだけなので、更新自体は成立させる
        }
      }

      setSubPatternModalOpen(false);
      setEditingSubPattern(null);
      // 保存のみで再読み取りはしない。読み直したい場合は再読み取りから実行できる
      setSnackbarText('子パターンを更新しました');
    } catch (error) {
      setErrorText(getUserErrorMessage(error, '子パターンの保存に失敗しました'));
    } finally {
      setSubPatternSubmitting(false);
    }
  };

  /** 子パターンを新規作成する（#2967） */
  const handleCreateSubPattern = async (input: {
    subPatternName: string;
    description: string;
    itemOverrides: ItemOverrideType[];
  }): Promise<void> => {
    if (!patternData || !user?.uid) return;
    try {
      setSubPatternSubmitting(true);
      const subPatternId = await createSubPattern({
        patternId: patternData.patternId,
        subPatternName: input.subPatternName,
        description: input.description,
        itemOverrides: input.itemOverrides,
        sourceResultId: resultId ?? null,
        userId: user.uid,
        // 代理ログイン中は created_by が運営スタッフのUIDになるため、
        // 一覧で「事務局」と出せるようフラグも残す（#3314）
        isProxy: isProxyMode,
        operatorId: isProxyMode ? operatorId || undefined : undefined,
      });

      // この帳票を子パターンに紐づけて指紋を記録する（#2967）。
      // ユーザーの「この帳票はこの子パターンだ」という判断が、
      // 座標による自動判定の教師データになる。失敗しても作成自体は成立する
      if (resultId) {
        // 元になった帳票にも子パターンのバッジを付ける（#2967）。
        // この結果は「その子パターンの見本」であり、以降この子パターンで読まれる帳票と
        // 同じ扱いにする。付けないと、一覧で子パターンの絞り込みをしたときに
        // 見本だけが漏れて「どれを見て作ったのか」が辿れなくなる。
        // 読み取り自体はやり直さない（取得ルールは正解データから導いたもので、
        // この帳票に対しては定義上正しいため）
        try {
          await updateDoc(doc(db, 'results', resultId), {
            sub_pattern_id: subPatternId,
            sub_pattern_name: input.subPatternName,
            updated_at: Timestamp.now(),
          });
          setResultRecord(prev =>
            prev
              ? { ...prev, sub_pattern_id: subPatternId, sub_pattern_name: input.subPatternName }
              : prev,
          );
        } catch {
          // バッジが付かないだけで子パターンの作成は成立している。ユーザーには見せない
        }
      }

      setSubPatternModalOpen(false);
      // 保存できた下書きは破棄する（#2967）。次に作るときは別の子パターンのはずで、
      // 保存済みの内容が残っていると重複した子パターンを作りかけてしまう。
      // 次回開いたときは提案から取り直す
      setSubPatternInitialOverrides({});
      setSubPatternInitialItemNames({});
      setSubPatternSuggestedResultId(null);
      // 保存のみで再読み取りはしない。取得ルールは手修正した正解データからAIが
      // 導いたものなので、その帳票に対しては定義上正しく、再読み取りはクレジットを
      // 消費するだけになる（確認したい場合は再読み取りを手動で実行できる）
      setSnackbarText('子パターンを保存しました');
    } catch (error) {
      setErrorText(getUserErrorMessage(error, '子パターンの保存に失敗しました'));
    } finally {
      setSubPatternSubmitting(false);
    }
  };

  // 指定URLを別タブで開く（拡張子に応じてPDFはビューア、画像は直接表示）
  const openUrlInNewTab = (url: string): void => {
    const fileName = url.split('/').pop()?.split('?')[0];
    const fileExtension = fileName?.split('.').pop()?.toLowerCase();

    switch (fileExtension) {
      case 'pdf':
        window.open(
          `https://docs.google.com/viewer?url=${encodeURIComponent(url)}&embedded=true`,
          '_blank',
        );
        break;
      case 'jpg':
      case 'jpeg':
      case 'png':
        window.open(url, '_blank');
        break;
      default:
        setErrorText('ファイルの表示に失敗しました。');
        break;
    }
  };

  // ファイルの表示（ヘッダーのボタン）
  const openFile = (): void => {
    // #2971 ハイブリッド方式:
    // - 単一ページ: プレビューと同じ「回転後の画像」(files[0]) を開く
    // - 複数ページ: 全ページを閲覧できるよう従来どおり元ファイル(originalFileUrl)を開く（回転前）
    const singlePageRotatedUrl = files.length === 1 ? files[0]?.file_url : undefined;
    const targetUrl = singlePageRotatedUrl || originalFileUrl;
    if (!targetUrl) {
      return;
    }
    openUrlInNewTab(targetUrl);
  };

  // データ取得中はローディング表示
  if (!isDataReady) {
    return <CustomLoading loading={true} />;
  }

  return (
    <ThemeProvider theme={theme}>
      <MetaTitle
        customTitle={
          patternData?.pattern_name ? `${patternData.pattern_name} AI読取結果` : undefined
        }
      />
      <SideBar pcIsNone />
      <CustomLoading loading={loading} />
      <LoadingModal
        open={isReOcrProcessing}
        progress={reOcrProgress}
        message={reclassifyLoading ? '仕分け変更しています...' : '再読取しています...'}
      />
      <Grid2
        container
        sx={{
          ...styles.backGround,
          ...(isProxyMode && {
            height: `calc(100vh - ${PROXY_BANNER_HEIGHT}px)`,
            marginTop: `${PROXY_BANNER_HEIGHT}px`,
          }),
        }}
      >
        {/* 画像領域（左側） */}
        <Grid2
          size={{ xs: 12, md: 5 }}
          sx={{
            ...styles.rightContainer,
            ...(isProxyMode && {
              height: `calc(100vh - ${PROXY_BANNER_HEIGHT}px)`,
            }),
          }}
          container
          flexDirection={'row'}
          spacing={'18px'}
          alignContent={'flex-start'}
        >
          <Grid2 container sx={styles.rightHeader} spacing={1}>
            <Grid2 size={10} display="flex" justifyContent="start" alignItems="center" gap={1}>
              <Typography sx={styles.fileTitleTextCss} textAlign="left">
                {fileName}
              </Typography>
              {resultRecord?.status === resultStatus.deleted && <DeletedBadge />}
            </Grid2>
            <Grid2 size={2} display="flex" justifyContent="end" alignItems="center">
              <Button sx={styles.openInNewButton} onClick={openFile}>
                <OpenInNewIcon sx={styles.rightHeaderIconSize} />
              </Button>
              <Button sx={styles.downloadButton} onClick={handleDownload}>
                <DownloadIcon sx={styles.rightHeaderIconSize} />
              </Button>
            </Grid2>
          </Grid2>
          {resultRecord?.result_data && files && files.length > 0 && (
            <>
              <Box sx={styles.zoomControlBar}>
                {/* ルーペ（拡大表示）のON/OFF切り替え（一番左に配置・共通コンポーネント #3079）。
                    SP では機能しないため表示しない（#3224） */}
                {!isSpLayout && (
                  <MagnifierToggle checked={magnifierEnabled} onChange={toggleMagnifier} />
                )}
                <Tooltip title="縮小">
                  <span>
                    <IconButton
                      sx={styles.zoomIconButton}
                      onClick={handleZoomOut}
                      disabled={zoomLevel <= PREVIEW_ZOOM.MIN_LEVEL}
                      size="small"
                    >
                      <ZoomOutIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
                <Typography sx={styles.zoomPercentText}>
                  {Math.round(zoomLevel * PREVIEW_ZOOM.PERCENT_BASE)}%
                </Typography>
                <Tooltip title="拡大">
                  <span>
                    <IconButton
                      sx={styles.zoomIconButton}
                      onClick={handleZoomIn}
                      disabled={zoomLevel >= PREVIEW_ZOOM.MAX_LEVEL}
                      size="small"
                    >
                      <ZoomInIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
                <Tooltip title="拡大率リセット">
                  <span>
                    <IconButton sx={styles.zoomIconButton} onClick={handleZoomReset} size="small">
                      <RestartAltIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
              </Box>
              <Box sx={styles.cardMediaBox}>
                {sortedFiles.map((file: InputFileType, index: number) => {
                  if (!file.file_url || !file.file_width || !file.file_height) return null;
                  const currentPage = file.page || index + 1;

                  return (
                    <FocusAwareImageOverlay
                      key={index}
                      imageUrl={file.file_url}
                      result={resultRecord.result_data}
                      imageWidth={file.file_width}
                      imageHeight={file.file_height}
                      forDemo={true}
                      currentPage={currentPage}
                      itemList={activeItemListForOverlay}
                      showConfidence={isOperatorStaff(user?.role)}
                      zoomLevel={zoomLevel}
                      magnifierEnabled={magnifierEnabled}
                    />
                  );
                })}
              </Box>
            </>
          )}
        </Grid2>

        {/* フィールド領域（右側） */}
        <Grid2
          size={{ xs: 12, md: 7 }}
          sx={{
            ...styles.leftContainer,
            ...(isProxyMode && {
              '@media (min-width: 960px)': {
                ...styles.leftContainer['@media (min-width: 960px)'],
                height: `calc(100vh - ${PROXY_BANNER_HEIGHT}px)`,
              },
            }),
          }}
        >
          <Grid2 container sx={styles.leftHeader} flexWrap="wrap">
            <Grid2
              size={{ xs: 0, md: 8 }}
              display="flex"
              justifyContent="start"
              alignItems="center"
              flexWrap="nowrap"
              sx={styles.actionButtonsGrid}
            >
              {patternData?.status === patternStatus.deleted ? (
                <DeletedBadge />
              ) : (
                <>
                  <Button
                    sx={{
                      ...saveButtonStyles.buttonChange,
                      mr: 1,
                      padding: '10px 16px',
                      height: '44px',
                      whiteSpace: 'nowrap',
                      flexShrink: 0,
                      ...styles.buttonsDisplayNoneSp,
                    }}
                    onClick={(): void => handleProcessButton()}
                  >
                    <Typography sx={saveButtonStyles.textColorChange}>{buttonText()}</Typography>
                  </Button>
                  <Button
                    sx={{
                      ...secondaryButtonBackgroundColor,
                      mr: 1,
                      padding: '10px 16px',
                      height: '44px',
                      whiteSpace: 'nowrap',
                      flexShrink: 0,
                      ...styles.buttonsDisplayNoneSp,
                    }}
                    onClick={(): void => {
                      if (!resultId) return;
                      runWithValidationConfirm('保存', () => {
                        openHistoryConfirm(processingStatus.draft);
                      });
                    }}
                  >
                    <Typography sx={styles.draftButtonText}>下書き保存</Typography>
                  </Button>
                  <Tooltip title="その他の操作">
                    <Button
                      sx={{
                        ...tertiaryButtonBackgroundColor,
                        padding: '10px 16px',
                        height: '44px',
                        minWidth: '32px',
                        flexShrink: 0,
                        ...styles.buttonsDisplayNoneSp,
                        '@media (min-width: 1601px)': { display: 'none' },
                      }}
                      onClick={(e): void => setActionMenuAnchor(e.currentTarget)}
                    >
                      <MoreVertIcon />
                    </Button>
                  </Tooltip>
                  <Box
                    sx={{
                      display: 'none',
                      '@media (min-width: 1601px)': { display: 'contents' },
                    }}
                  >
                    <Tooltip title="再読取">
                      <Button
                        sx={{
                          ...tertiaryButtonBackgroundColor,
                          mr: 1,
                          padding: '10px 16px',
                          height: '44px',
                          minWidth: '32px',
                          whiteSpace: 'nowrap',
                          flexShrink: 0,
                        }}
                        disabled={isReOcrProcessing}
                        onClick={handleReOcrAll}
                      >
                        <ReplayIcon />
                        <Typography
                          sx={{
                            ...boldMdGray,
                            display: 'none',
                            '@media (min-width: 2100px)': { display: 'inline' },
                          }}
                        >
                          再読取
                        </Typography>
                      </Button>
                    </Tooltip>
                    <Tooltip title="仕分け変更">
                      <Button
                        sx={{
                          ...tertiaryButtonBackgroundColor,
                          mr: 1,
                          padding: '10px 16px',
                          height: '44px',
                          minWidth: '32px',
                          whiteSpace: 'nowrap',
                          flexShrink: 0,
                        }}
                        disabled={reclassifyLoading}
                        onClick={(): void => setReclassifyModalOpen(true)}
                      >
                        <SwapHorizIcon />
                        <Typography
                          sx={{
                            ...boldMdGray,
                            display: 'none',
                            '@media (min-width: 2100px)': { display: 'inline' },
                          }}
                        >
                          {reclassifyLoading ? '変更中...' : '仕分け変更'}
                        </Typography>
                      </Button>
                    </Tooltip>
                    {hasEditHistory &&
                      patternData &&
                      (isOperatorStaff(user?.role) || isProxyMode) && (
                        <Tooltip title="AI改善">
                          <Button
                            sx={{
                              ...tertiaryButtonBackgroundColor,
                              mr: 1,
                              padding: '10px 16px',
                              height: '44px',
                              minWidth: '32px',
                              whiteSpace: 'nowrap',
                              flexShrink: 0,
                            }}
                            onClick={(): void => {
                              void handleOpenRuleImprovement();
                            }}
                          >
                            <AutoFixHighIcon />
                            <Typography
                              sx={{
                                ...boldMdGray,
                                display: 'none',
                                '@media (min-width: 2100px)': { display: 'inline' },
                              }}
                            >
                              AI改善
                            </Typography>
                          </Button>
                        </Tooltip>
                      )}
                    {/* 子パターンを作成（#2967）。AI改善と違い修正履歴を必要としない
                        （帳票を見て全項目の取得ルールを提案するため） */}
                    {canCreateSubPattern && patternData && (
                      <Tooltip title={subPatternLabel.create}>
                        <Button
                          sx={{
                            ...tertiaryButtonBackgroundColor,
                            mr: 1,
                            padding: '10px 16px',
                            height: '44px',
                            minWidth: '32px',
                            whiteSpace: 'nowrap',
                            flexShrink: 0,
                          }}
                          onClick={(): void => {
                            void handleOpenSubPatternCreate();
                          }}
                        >
                          <ViewQuiltIcon />
                          <Typography
                            sx={{
                              ...boldMdGray,
                              display: 'none',
                              '@media (min-width: 2100px)': { display: 'inline' },
                            }}
                          >
                            {subPatternLabel.feature}
                          </Typography>
                        </Button>
                      </Tooltip>
                    )}
                    <Tooltip title="項目設定">
                      <Button
                        sx={{
                          ...tertiaryButtonBackgroundColor,
                          mr: 1,
                          padding: '10px 16px',
                          height: '44px',
                          minWidth: '32px',
                          whiteSpace: 'nowrap',
                          flexShrink: 0,
                        }}
                        disabled={!patternId}
                        onClick={(): void => navigate(`/pattern/${patternId}?step=1`)}
                      >
                        <TuneIcon />
                        <Typography
                          sx={{
                            ...boldMdGray,
                            display: 'none',
                            '@media (min-width: 2100px)': { display: 'inline' },
                          }}
                        >
                          項目設定
                        </Typography>
                      </Button>
                    </Tooltip>
                    <Tooltip title="ファイル出力">
                      <Button
                        sx={{
                          ...tertiaryButtonBackgroundColor,
                          mr: 1,
                          padding: '10px 16px',
                          height: '44px',
                          minWidth: '32px',
                          whiteSpace: 'nowrap',
                          flexShrink: 0,
                        }}
                        disabled={!patternData || !resultRecord}
                        onClick={(): void =>
                          runWithValidationConfirm('出力', () => setFileExportDialogOpen(true))
                        }
                      >
                        <FileDownloadOutlinedIcon />
                        <Typography
                          sx={{
                            ...boldMdGray,
                            display: 'none',
                            '@media (min-width: 2100px)': { display: 'inline' },
                          }}
                        >
                          出力
                        </Typography>
                      </Button>
                    </Tooltip>
                    <Tooltip title="削除">
                      <Button
                        sx={{
                          ...tertiaryButtonBackgroundColor,
                          mr: 1,
                          padding: '10px 16px',
                          height: '44px',
                          minWidth: '32px',
                          whiteSpace: 'nowrap',
                          flexShrink: 0,
                        }}
                        onClick={(): void => setIsModalOpen(true)}
                      >
                        <DeleteOutlinedIcon />
                        <Typography
                          sx={{
                            ...boldMdGray,
                            display: 'none',
                            '@media (min-width: 2100px)': { display: 'inline' },
                          }}
                        >
                          削除
                        </Typography>
                      </Button>
                    </Tooltip>
                  </Box>
                  <Menu
                    anchorEl={actionMenuAnchor}
                    open={Boolean(actionMenuAnchor)}
                    onClose={(): void => setActionMenuAnchor(null)}
                  >
                    <MenuItem
                      disabled={isReOcrProcessing}
                      onClick={(): void => {
                        setActionMenuAnchor(null);
                        handleReOcrAll();
                      }}
                    >
                      <ReplayIcon sx={{ mr: 1, fontSize: '20px' }} />
                      再読取
                    </MenuItem>
                    <MenuItem
                      disabled={reclassifyLoading}
                      onClick={(): void => {
                        setActionMenuAnchor(null);
                        setReclassifyModalOpen(true);
                      }}
                    >
                      <SwapHorizIcon sx={{ mr: 1, fontSize: '20px' }} />
                      {reclassifyLoading ? '変更中...' : '仕分け変更'}
                    </MenuItem>
                    {hasEditHistory &&
                      patternData &&
                      (isOperatorStaff(user?.role) || isProxyMode) && (
                        <MenuItem
                          onClick={(): void => {
                            setActionMenuAnchor(null);
                            void handleOpenRuleImprovement();
                          }}
                        >
                          <AutoFixHighIcon sx={{ mr: 1, fontSize: '20px' }} />
                          AI改善
                        </MenuItem>
                      )}
                    {/* 子パターンを作成（#2967）。AI改善と違い修正履歴を必要としない
                        （帳票を見て全項目の取得ルールを提案するため） */}
                    {canCreateSubPattern && patternData && (
                      <MenuItem
                        onClick={(): void => {
                          setActionMenuAnchor(null);
                          void handleOpenSubPatternCreate();
                        }}
                      >
                        <ViewQuiltIcon sx={{ mr: 1, fontSize: '20px' }} />
                        {subPatternLabel.create}
                      </MenuItem>
                    )}
                    <MenuItem
                      disabled={!patternId}
                      onClick={(): void => {
                        setActionMenuAnchor(null);
                        navigate(`/pattern/${patternId}?step=1`);
                      }}
                    >
                      <TuneIcon sx={{ mr: 1, fontSize: '20px' }} />
                      項目設定
                    </MenuItem>
                    <MenuItem
                      disabled={!patternData || !resultRecord}
                      onClick={(): void => {
                        setActionMenuAnchor(null);
                        runWithValidationConfirm('出力', () => setFileExportDialogOpen(true));
                      }}
                    >
                      <FileDownloadOutlinedIcon sx={{ mr: 1, fontSize: '20px' }} />
                      ファイル出力
                    </MenuItem>
                    <MenuItem
                      onClick={(): void => {
                        setActionMenuAnchor(null);
                        setIsModalOpen(true);
                      }}
                    >
                      <DeleteOutlinedIcon sx={{ mr: 1, fontSize: '20px' }} />
                      削除
                    </MenuItem>
                  </Menu>
                </>
              )}
            </Grid2>
            <Grid2
              size={{ xs: 12, md: 4 }}
              display="flex"
              justifyContent="end"
              alignItems="center"
              sx={styles.navButtonsGrid}
            >
              {prevResultId && (
                <Button sx={styles.KeyboardArrowLeftButton} onClick={handlePrevResult}>
                  <KeyboardArrowLeftIcon sx={styles.leftHeaderIconSize} />
                  <Typography sx={styles.textCss}>前へ</Typography>
                </Button>
              )}
              {nextResultId && (
                <Button sx={styles.KeyboardArrowRightButton} onClick={handleNextResult}>
                  <Typography sx={styles.textCss}>次へ</Typography>
                  <KeyboardArrowRightIcon sx={styles.leftHeaderIconSize} />
                </Button>
              )}
              <Button
                sx={styles.closeButton}
                onClick={(): void => {
                  // 遷移元が戻り先を指定していれば優先する（例: メール取り込みの詳細から遷移）
                  const backTo = (location.state as { backTo?: string } | null)?.backTo;
                  if (backTo) {
                    navigate(backTo);
                  } else if (recentResultsList) {
                    navigate('/home');
                  } else {
                    navigate(`/ai-ocr/pattern/${patternId}`, {
                      state: { searchData: serializeSearchData(), ocrItemKeywords },
                    });
                  }
                }}
              >
                <CloseIcon sx={styles.leftHeaderIconSize} />
              </Button>
            </Grid2>
          </Grid2>
          <Stack
            direction="column"
            sx={{ ...styles.staffName, alignItems: 'flex-end' }}
            spacing={1}
          >
            {/* 1行目：全ユーザー向け情報 */}
            <Stack
              direction="row"
              spacing={3}
              sx={{ justifyContent: 'flex-end', alignItems: 'center', rowGap: '8px' }}
              flexWrap="wrap"
            >
              {/* Geminiモデル（一般ユーザー向け・思考/コストは非表示）。#3206
                  詳細モードONでは下段の運用情報に表示されるため重複させない。

                  1) 運営がOCRエンジン・構造化エンジン等を既定から変えて実行した結果は
                     「事務局指定」と出す。ユーザーが同じ条件で再現できないため、
                     モデル名を出すと「自分でも出せる精度」と誤解される。
                     Gemini モデルの変更（Flash→Pro）はユーザーも選べるため 2) で扱う。
                  2) モデルを選べるパターン（Pro 設定）は、実際に使ったモデルを出す。
                     Flash 実行にも出すことで、Pro と読み比べて精度を確認できる。
                     モデルを選べないパターンでは情報として意味がないため出さない。
                     企業プランを条件にすると、代理で Pro 実行した結果が Standard 企業では
                     隠れて「Flash で読まれた」と誤解されるため、プランは見ない。 */}
              {!isDetailView &&
                (isOperatorCustomizedOcr(displayTokenUsage, resultRecord?.gemini_model) ? (
                  <Typography sx={regularSm} textAlign="center">
                    モデル：{MODEL_OPERATOR_CUSTOM_LABEL}
                  </Typography>
                ) : (
                  isProGeminiModel(patternData?.gemini_model) && (
                    <Typography sx={regularSm} textAlign="center">
                      モデル：{getGeminiModelDisplayName(resultRecord?.gemini_model)}
                    </Typography>
                  )
                ))}
              {/* OCR文字数（全ユーザー向け） */}
              {displayTokenUsage?.total_char_count !== undefined && (
                <Typography sx={regularSm} textAlign="center">
                  OCR文字数：{displayTokenUsage.total_char_count.toLocaleString()}文字
                </Typography>
              )}
              {/* 消費枚数（全ユーザー向け・累計表示） */}
              {((): JSX.Element | null => {
                // 代理操作の実行は消費枚数に含めない
                const billableExecutions = ocrExecutions.filter(e => !e.is_proxy);
                const reOcrCount = billableExecutions.filter(e => {
                  if (e.execution_type === ocrExecutionType.initial) return false;
                  const usage =
                    (e.token_usage as Record<string, number>)?.calculated_usage_count ?? 0;
                  return usage > 0;
                }).length;
                const totalUsage =
                  billableExecutions.length === 0
                    ? 0
                    : reOcrCount > 0
                      ? billableExecutions.reduce((sum, e) => {
                          const usage =
                            (e.token_usage as Record<string, number>)?.calculated_usage_count ?? 0;
                          return sum + usage;
                        }, 0)
                      : displayTokenUsage?.calculated_usage_count;
                if (totalUsage === undefined) return null;
                return (
                  <Stack direction="row" spacing={0.5} alignItems="center" justifyContent="center">
                    <Typography sx={regularSm}>
                      消費枚数：{totalUsage}枚{reOcrCount > 0 && `（再読取${reOcrCount}回分含む）`}
                    </Typography>
                    <Tooltip title="消費枚数とは？" arrow placement="top">
                      <IconButton
                        size="small"
                        onClick={(): void => {
                          window.open(HELP_FAQ_CONSUMPTION_COUNT, '_blank');
                        }}
                        sx={{ padding: 0 }}
                      >
                        <HelpOutlineIcon sx={{ fontSize: 16, color: 'gray' }} />
                      </IconButton>
                    </Tooltip>
                  </Stack>
                );
              })()}
              {/* この結果に対して実際にマスタ照合を実行したかを示す（OCR実行時に記録）。 */}
              {resultRecord?.master_verification_executed === true && (
                <Typography sx={regularSm} textAlign="center">
                  マスタ照合：あり
                </Typography>
              )}
              <Typography sx={regularSm} textAlign="center">
                {selectedExecutionIndex >= 0 ? '実行日時' : 'アップロード日時'}：
                {((): string => {
                  if (selectedExecutionIndex >= 0 && ocrExecutions[selectedExecutionIndex]) {
                    return ocrExecutions[selectedExecutionIndex].executed_at.toLocaleString(
                      'ja-JP',
                    );
                  }
                  return resultRecord?.created_at
                    ? new Date(resultRecord.created_at.seconds * 1000).toLocaleString('ja-JP')
                    : '';
                })()}
              </Typography>
              <Stack direction="row" spacing={1} alignItems="center">
                <Typography sx={regularSm} textAlign="center">
                  {selectedExecutionIndex >= 0 ? '実行者' : '作成者'}：
                </Typography>
                <Avatar
                  alt={
                    selectedExecutionIndex >= 0
                      ? ocrExecutions[selectedExecutionIndex]?.executed_by_name || createUser
                      : createUser
                  }
                  src={
                    selectedExecutionIndex >= 0
                      ? ocrExecutions[selectedExecutionIndex]?.is_proxy
                        ? HiStoryIcon
                        : executionUserIcons[ocrExecutions[selectedExecutionIndex]?.executed_by] ||
                          NoAccountIcon
                      : icon || NoAccountIcon
                  }
                  sx={{ width: 24, height: 24 }}
                />
                <Typography sx={regularSm} textAlign="center">
                  {selectedExecutionIndex >= 0
                    ? ocrExecutions[selectedExecutionIndex]?.executed_by_name || createUser
                    : createUser}
                </Typography>
                {/* 担当者（#2642） */}
                {((): React.ReactElement => {
                  const { displayName, displayIconUrl } = resolveAssigneeDisplay(
                    resultRecord?.assignee_id as string | undefined,
                    resultRecord?.assignee_name as string | undefined,
                    resultRecord?.assignee_icon_url as string | null | undefined,
                    assigneeDisplayOptions,
                  );
                  return (
                    <>
                      <Typography sx={regularSm} textAlign="center" style={{ marginLeft: '16px' }}>
                        {assigneeLabels.label}：
                      </Typography>
                      <Avatar
                        alt={displayName}
                        src={displayIconUrl ?? NoAccountIcon}
                        sx={{ width: 24, height: 24 }}
                      />
                      <Typography sx={regularSm} textAlign="center">
                        {displayName}
                      </Typography>
                      <Tooltip title={assigneeLabels.changeAction}>
                        <IconButton
                          size="small"
                          onClick={(): void => setAssigneeDialogOpen(true)}
                          sx={{ padding: '2px' }}
                        >
                          <EditIcon sx={{ fontSize: '14px' }} />
                        </IconButton>
                      </Tooltip>
                    </>
                  );
                })()}
              </Stack>
              {/*
                子パターン（#2967）。どの取得ルールで読み取ったかを示す。
                作成者・担当者と同じ「この結果の属性」だが、同じ行に足すと横に長くなるため
                1行下ろす。鉛筆アイコンから取得ルールを編集できる
                （帳票を見ながらでないと編集できないため、この画面で開く）。
                読み直したい場合は再読み取りから子パターンを選ぶ。
              */}
              {resultRecord?.sub_pattern_name && (
                <Stack direction="row" spacing={1} alignItems="center" justifyContent="flex-end">
                  <Typography sx={regularSm} textAlign="center">
                    子パターン：
                  </Typography>
                  <SubPatternChip
                    subPatternName={resultRecord.sub_pattern_name}
                    onEdit={
                      canCreateSubPattern
                        ? (): void => {
                            void openSubPatternEdit();
                          }
                        : undefined
                    }
                  />
                </Stack>
              )}
            </Stack>
            {/* 2行目以降：運営スタッフかつ詳細モードON のときのみ表示。
                上段（OCRの流れ）と下段（精度・コスト・処理時間）を別 Stack に分けて
                必ず改行されるようにする（#3109）。 */}
            {isDetailView && (
              <>
                {/* 2行目：OCRの流れ（OCR → 値取得 → 構造化 → 検証マーク） */}
                <Stack
                  direction="row"
                  spacing={3}
                  sx={{ justifyContent: 'flex-end', alignItems: 'center', rowGap: '8px' }}
                  flexWrap="wrap"
                >
                  {/* OCRエンジン（#2960 運営スタッフのみ）。座標付与もここに併記する */}
                  <Typography sx={regularSm} textAlign="center">
                    OCR：{getOcrEngineDisplayName(displayTokenUsage?.azure_vision?.engine)}
                    {displayTokenUsage?.verification_settings?.include_coordinates ? ' + 座標' : ''}
                  </Typography>
                  {/* 値取得（1回目Gemini）：モデル + パラメータ（思考/temp/topP）をまとめて表示。
                    座標は OCR行に移したのでここには出さない。
                    運営が使った結果（verification_settings あり）は思考/temp/topPを常に表示する。 */}
                  <Typography sx={regularSm} textAlign="center">
                    値取得：{getGeminiModelDisplayName(resultRecord?.gemini_model)}
                    {((): string => {
                      const parts: string[] = [];
                      const vs = displayTokenUsage?.verification_settings;
                      // 思考設定（「思考：無効」等のラベル。設定があれば表示）
                      const thinkingDisplay = getThinkingConfigDisplayName(
                        displayTokenUsage?.gemini_ocr?.thinking_budget,
                        displayTokenUsage?.gemini_ocr?.thinking_level,
                      );
                      if (thinkingDisplay) {
                        parts.push(thinkingDisplay);
                      }
                      // temp / topP は運営が使った結果（verification_settings あり）では常に表示
                      if (vs) {
                        parts.push(`temp: ${vs.temperature ?? 0}`);
                        parts.push(`topP: ${vs.top_p ?? 0}`);
                      }
                      return parts.length > 0 ? `（${parts.join(' / ')}）` : '';
                    })()}
                  </Typography>
                  {/* 構造化エンジン（#2960 運営スタッフのみ。verification_settings がある結果のみ）
                    保存済みの structured_model_name を人が読める形に整形して表示する。
                    過去データ（model_name なし）は structured_engine から判定する。 */}
                  {displayTokenUsage?.verification_settings && (
                    <Typography sx={regularSm} textAlign="center">
                      構造化：
                      {((): string => {
                        const vs = displayTokenUsage.verification_settings;
                        const modelName: string | undefined = vs.structured_model_name;
                        // モデル名の整形はフリーOCRと共通のヘルパーを使う（#3375）
                        if (modelName) return getModelNameDisplay(modelName);
                        // 過去データ：エンジン種別のみ
                        return vs.structured_engine === 'chatgpt' ? 'ChatGPT' : 'Gemini';
                      })()}
                    </Typography>
                  )}
                  {/* 検証ログマーク（#3109 運営のみ・検証トレースがある実行のみ）。
                    構造化の右に置き、目立たない灰色で表示する。
                    クリックで左の帳票画像に被らない右側パネルにOCR結果/プロンプト/値取得結果を表示する。 */}
                  {selectedVerificationTrace && selectedVerificationTrace.pages.length > 0 && (
                    <Tooltip title="検証ログを表示">
                      <IconButton
                        size="small"
                        onClick={(): void => setVerificationPanelOpen(true)}
                        sx={{ color: gray80, padding: '2px' }}
                      >
                        <FactCheckOutlinedIcon sx={{ fontSize: '18px' }} />
                      </IconButton>
                    </Tooltip>
                  )}
                </Stack>
                {/* 3行目：結果系（読み取り精度 → コスト → 処理時間 → 読取履歴セレクト） */}
                <Stack
                  direction="row"
                  spacing={3}
                  sx={{ justifyContent: 'flex-end', alignItems: 'center', rowGap: '8px' }}
                  flexWrap="wrap"
                >
                  {/* 読み取り精度（#2960 運営スタッフのみ・OCR値 vs 最終保存値の文字数一致率）
                    コストの左に配置する。ユーザーが確認保存（完了/出力済み）するまでは
                    正解が未確定のため「- %」を表示する。
                    #3354 で項目単位の一致率から文字数ベースの一致率に変更した */}
                  {((): React.ReactElement => {
                    const isReviewed =
                      isProcessingStatus === processingStatus.completed ||
                      isProcessingStatus === processingStatus.outputted;
                    const showPercent =
                      isReviewed && ocrAccuracy !== null && ocrAccuracy.percent !== null;
                    // 行数差（OCR行数 − 最終行数）。0以外のとき過剰行/行落ちとして表示する
                    const rowDiffText =
                      showPercent && ocrAccuracy?.rowDiff
                        ? `（行数差${ocrAccuracy.rowDiff > 0 ? '+' : ''}${ocrAccuracy.rowDiff}）`
                        : '';
                    const charText = showPercent
                      ? `${ocrAccuracy?.matchedChars.toLocaleString()}/${ocrAccuracy?.totalChars.toLocaleString()}文字`
                      : '';
                    return (
                      <Typography sx={regularSm} textAlign="center">
                        読み取り精度：
                        {showPercent
                          ? `${ocrAccuracy?.percent}%（${charText}）${rowDiffText}`
                          : '- %'}
                      </Typography>
                    );
                  })()}
                  {/* コスト（ツールチップで内訳表示） */}
                  <OcrCostDisplay
                    tokenUsage={displayTokenUsage}
                    subPatternClassificationCost={subPatternClassificationCost}
                  />
                  {/* 処理時間（運営スタッフのみ表示） */}
                  {displayTokenUsage?.processing_time_ms !== undefined && (
                    <Typography sx={{ ...regularSm }} textAlign="center">
                      処理時間：
                      {((): string => {
                        const diffSec = Math.floor(displayTokenUsage.processing_time_ms / 1000);

                        if (diffSec < 60) {
                          return `${diffSec}秒`;
                        } else if (diffSec < 3600) {
                          const minutes = Math.floor(diffSec / 60);
                          const seconds = diffSec % 60;
                          return `${minutes}分${seconds}秒`;
                        } else {
                          const hours = Math.floor(diffSec / 3600);
                          const minutes = Math.floor((diffSec % 3600) / 60);
                          return `${hours}時間${minutes}分`;
                        }
                      })()}
                    </Typography>
                  )}
                  {/* 読取履歴セレクト（運営スタッフのみ・2回以上実行された場合に表示） */}
                  {ocrExecutions.length >= 2 && (
                    <FormControl size="small" sx={{ minWidth: 120 }}>
                      <Select
                        value={selectedExecutionIndex}
                        onChange={(e): void => {
                          const newIndex = Number(e.target.value);
                          setSelectedExecutionIndex(newIndex);
                          // 選択した実行履歴のresult_dataでフォームを切り替え
                          if (newIndex >= 0 && ocrExecutions[newIndex]?.result_data) {
                            applyResultDataToForm(
                              ocrExecutions[newIndex].result_data,
                              patternData?.item_list,
                            );
                          } else if (newIndex === -1 && resultRecord?.result_data) {
                            // 「最新」選択時はresultRecordのresult_dataに戻す
                            applyResultDataToForm(
                              resultRecord.result_data as Record<string, unknown>,
                              patternData?.item_list,
                            );
                          }
                        }}
                        sx={{
                          fontSize: '12px',
                          height: '28px',
                          '& .MuiSelect-select': { py: '4px' },
                          '& .MuiOutlinedInput-notchedOutline': {
                            borderColor: 'rgba(0, 0, 0, 0.23)',
                          },
                          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                            borderColor: 'rgba(0, 0, 0, 0.23)',
                            borderWidth: '1px',
                          },
                        }}
                        displayEmpty
                      >
                        <MenuItem value={-1} sx={{ fontSize: '12px' }}>
                          最新
                        </MenuItem>
                        {ocrExecutions.map((execution, index) => (
                          <MenuItem key={execution.id} value={index} sx={{ fontSize: '12px' }}>
                            {getExecutionLabel(
                              execution,
                              index,
                              ocrExecutions,
                              patternData?.item_list,
                            )}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  )}
                </Stack>
              </>
            )}
          </Stack>

          {/* 統一された白い背景コンテナ */}
          <Box sx={styles.deliveryMainInfo}>
            {/* 要注意サマリー（#3262）。項目名クリックで該当項目までスクロールする */}
            {showReviewIssues && (
              <ReviewSummaryPanel issues={reviewIssues} onItemClick={scrollToReviewIssue} />
            )}
            {/* 読取パターンの動的フィールドレンダリング */}
            {patternData?.item_list
              ?.filter((itemGroup: ItemListType) => {
                if (!itemGroup.is_active) return false;
                // 1項目再読取の履歴表示時は対象項目のみ表示
                if (selectedExecutionTargetIds) {
                  const isParentTarget = selectedExecutionTargetIds.includes(itemGroup.id);
                  const hasChildTarget = itemGroup.children?.some((child: ItemListChildType) =>
                    selectedExecutionTargetIds.includes(child.id),
                  );
                  return isParentTarget || hasChildTarget;
                }
                return true;
              })
              .map((itemGroup: ItemListType, groupIndex: number) => {
                // 配列ではない親要素（単一値フィールド）をController形式で表示
                if (itemGroup.type !== 'array' && itemGroup.children.length === 0) {
                  // 単一値フィールドの場合
                  const fieldType = itemGroup.type || 'string';
                  const fieldName = toFormFieldName(itemGroup.parentValue);
                  const isReReading = reReadingItemIds.includes(itemGroup.id);
                  // 計算セル（#3172）。計算値採用中は読み取り専用（グレー背景・編集不可）。
                  // disabled ではなく readOnly にして、計算結果を選択・コピーできるようにする
                  const calculationCellState = calculationCellStates[itemGroup.id];
                  const isCalculationLocked = calculationCellState?.isCalculated === true;

                  return (
                    <Box
                      key={groupIndex}
                      // 要注意サマリーからのスクロール先（#3262）
                      data-review-path={itemGroup.id}
                      sx={{ mb: 2, ...(isReReading ? styles.reReadingOverlay : {}) }}
                    >
                      {isReReading && (
                        <Box sx={styles.reReadingLabel}>
                          <RotateRightRoundedIcon sx={{ ...styles.loadingIcon, fontSize: 18 }} />
                          再読取中...
                        </Box>
                      )}
                      <Box
                        sx={{
                          display: 'flex',
                          flexDirection: 'row',
                          alignItems: 'baseline',
                          flexWrap: 'wrap',
                          columnGap: '4px',
                          rowGap: '2px',
                          mb: '4px',
                        }}
                      >
                        <Typography
                          component="span"
                          sx={{ fontSize: '12px', lineHeight: '18px', color: gray80, minWidth: 0 }}
                        >
                          {itemGroup.parentValue}
                          {itemGroup.required && (
                            <Typography
                              component="span"
                              sx={{
                                ...styles.requiredBadge,
                                marginLeft: '4px',
                                whiteSpace: 'nowrap',
                              }}
                            >
                              必須
                            </Typography>
                          )}
                        </Typography>
                        {renderItemSourceBadge(
                          itemGroup.id,
                          itemGroup.is_ocr_target,
                          itemGroup.fixedValue,
                          itemGroup.is_calculated,
                        )}
                        <IconButton
                          size="small"
                          onClick={(): void =>
                            handleEditClick(
                              itemGroup.id,
                              itemGroup.parentValue,
                              itemGroup.type,
                              itemGroup.displayWidth,
                              itemGroup.extractionRule,
                              itemGroup.outputFormat,
                              false,
                              undefined,
                              {
                                required: itemGroup.required,
                                minLength: itemGroup.minLength,
                                maxLength: itemGroup.maxLength,
                                minValue: itemGroup.minValue,
                                maxValue: itemGroup.maxValue,
                                treatZeroAsEmpty: itemGroup.treatZeroAsEmpty, // #3262
                                // 文字種・入力パターン・日付範囲（#3357）
                                charType: itemGroup.charType,
                                reviewChoiceValues: itemGroup.reviewChoiceValues, // 要注意にする選択肢（#3357）
                                reviewLowConfidence: itemGroup.reviewLowConfidence, // 読み取り精度の要注意（#3384）
                                pattern: itemGroup.pattern,
                                patternMessage: itemGroup.patternMessage,
                                dateNoPast: itemGroup.dateNoPast,
                                dateNoFuture: itemGroup.dateNoFuture,
                                datePastLimit: itemGroup.datePastLimit,
                                datePastUnit: itemGroup.datePastUnit,
                                dateFutureLimit: itemGroup.dateFutureLimit,
                                dateFutureUnit: itemGroup.dateFutureUnit,
                              },
                            )
                          }
                          sx={{ padding: '2px', alignSelf: 'center' }}
                        >
                          <ModeEditOutlinedIcon sx={{ fontSize: '14px', color: gray80 }} />
                        </IconButton>
                      </Box>
                      <Box
                        sx={{
                          display: 'flex',
                          // textarea で入力欄が縦に伸びても照合・変換ボタンが
                          // 下端につられて下がらないよう上端で揃える（#3130）
                          alignItems: 'flex-start',
                          gap: '8px',
                        }}
                      >
                        <Box
                          sx={{
                            flex: 1,
                            minWidth: 0,
                            // 計算値採用中（#3172）も自動入力欄と同じグレー背景にする
                            ...(masterMappingTargetIds.has(itemGroup.id) ||
                            dictionaryMappingTargetIds.has(itemGroup.id) ||
                            isCalculationLocked
                              ? {
                                  '& .MuiInputBase-root': {
                                    backgroundColor: gray5,
                                  },
                                }
                              : {}),
                          }}
                        >
                          <Controller
                            render={({ field, fieldState }): JSX.Element => {
                              if (fieldType === 'number') {
                                return (
                                  <CustomNumberField
                                    {...field}
                                    isRequired={false}
                                    formMb="zero"
                                    value={watch(fieldName) ?? ''}
                                    readOnly={isCalculationLocked}
                                    error={!!fieldState.error}
                                    onFocus={(): void => setFocusItem(itemGroup.parentValue)}
                                    onBlur={(): void => {
                                      field.onBlur();
                                      setFocusItem('');
                                      requestRecalculationForItem(itemGroup.id);
                                    }}
                                  />
                                );
                              } else if (fieldType === 'date') {
                                return (
                                  <CustomDatePicker
                                    value={field.value as Date | null}
                                    labelText=""
                                    width="100%"
                                    formMb="zero"
                                    readOnly={isCalculationLocked}
                                    isRequired={false}
                                    error={!!fieldState.error}
                                    onChange={(newValue): void => {
                                      setValue(fieldName, newValue);
                                      trigger();
                                      // 日付は選択確定＝onChange なのでここで再計算する（#3172）
                                      requestRecalculationForItem(itemGroup.id);
                                    }}
                                    onFocus={(): void => setFocusItem(itemGroup.parentValue)}
                                    onBlur={(): void => setFocusItem('')}
                                    dateFormat={itemGroup.outputFormat}
                                  />
                                );
                              } else if (isChoiceDataType(fieldType)) {
                                // 選択肢型（#3075）。保持するのは出力値、表示するのは表示名
                                return (
                                  <ChoiceField
                                    // 数値型で保存された値が選択肢型へ変更された後も残ることがあるため
                                    // 文字列に変換して渡す（#3260）。明細セル側と扱いを揃える
                                    value={field.value != null ? String(field.value) : ''}
                                    onChange={(next): void => {
                                      setValue(fieldName, next);
                                      trigger();
                                      // 選び直したら要注意を判定し直す（#3358）。
                                      // 「要注意にする選択肢」から別の選択肢に変えたら
                                      // その場で赤字が消えるようにする
                                      setCalculatedValuesByItemId(prev => {
                                        const nextMap = new Map(prev);
                                        nextMap.set(itemGroup.id, next);
                                        return nextMap;
                                      });
                                      requestRecalculationForItem(itemGroup.id);
                                    }}
                                    choices={itemGroup.choices ?? []}
                                    variant={fieldType === 'radio' ? 'radio' : 'select'}
                                    // 計算フィールドは人が選び替えられないようにする（#3358）。
                                    // テキスト・日付の readOnly と揃える。選べてしまうと
                                    // 再計算で上書きされ、変更が消えたように見える
                                    disabled={isCalculationLocked}
                                    // 必須項目は未選択に戻しても保存できないため解除リンクを出さない（#3075）
                                    required={itemGroup.required ?? false}
                                    error={!!fieldState.error}
                                    onFocus={(): void => setFocusItem(itemGroup.parentValue)}
                                    onBlur={(): void => setFocusItem('')}
                                  />
                                );
                              } else {
                                return (
                                  <CustomTextfield
                                    {...field}
                                    value={field.value ?? ''}
                                    inputWidth="100%"
                                    spHeight="51px"
                                    // 入力欄の下余白は 0 にし、間隔は下のメッセージ側で作る（#3262）
                                    formMb="0px"
                                    readOnly={isCalculationLocked}
                                    error={!!fieldState.error}
                                    onFocus={(): void => setFocusItem(itemGroup.parentValue)}
                                    onBlur={(): void => {
                                      setFocusItem('');
                                      requestRecalculationForItem(itemGroup.id);
                                    }}
                                    multiline={fieldType === 'textarea'}
                                    minRows={fieldType === 'textarea' ? 1 : undefined}
                                  />
                                );
                              }
                            }}
                            control={control}
                            name={fieldName}
                          />
                        </Box>
                        {mastersBySearchItemId.has(itemGroup.id) && (
                          <Button
                            onClick={(): void => {
                              const master = mastersBySearchItemId.get(itemGroup.id);
                              if (!master) return;
                              // 「OCR結果：」表示・履歴の query_text には常に最初のOCR値を使う。
                              // 自動照合や手動再照合で書き換えられた現在のフォーム値ではなく、
                              // Firestore に保存している ocr_value を優先する。
                              const storedField = (
                                resultRecord?.result_data as Record<string, unknown> | undefined
                              )?.[itemGroup.id] as { ocr_value?: string } | undefined;
                              const ocrValue =
                                storedField?.ocr_value ??
                                // 未保存の再照合で照合キー欄をマスタ値に上書きしているため（#3274）、
                                // 保存前に選択済みなら、その時に退避した ocr_value を優先する。
                                selectedMasterIds.get(`${master.master_id}-0`)?.ocr_value ??
                                String(getValues(fieldName) ?? '');
                              // AI照合の初期検索クエリは「入力欄で編集中の現在値」を使う。
                              const searchQuery = String(getValues(fieldName) ?? '');
                              setReconciliationTarget({
                                master,
                                ocrValue,
                                searchQuery,
                                supplierId: resolveSupplierId(master),
                                filterConditions: resolveFilterConditions(master),
                              });
                              setMasterModalOpen(true);
                            }}
                            sx={{
                              ...secondaryButtonBackgroundColor,
                              padding: '4px 8px',
                              height: '51px',
                              minWidth: 'auto',
                              gap: '2px',
                              flexShrink: 0,
                            }}
                          >
                            <ManageSearchIcon sx={{ fontSize: '16px' }} />
                            <Typography
                              sx={{ fontSize: '11px', fontWeight: 400, whiteSpace: 'nowrap' }}
                            >
                              照合
                            </Typography>
                          </Button>
                        )}
                        {/* 紐付け解除は照合モーダル内の「照合解除」ボタンに集約（#2896） */}
                        {dictionariesBySearchItemId.has(itemGroup.id) && (
                          <Button
                            onClick={(): void => {
                              const dictionary = dictionariesBySearchItemId.get(itemGroup.id);
                              if (!dictionary) return;
                              // 「OCR結果：」表示には常に最初のOCR値を使う。
                              const storedField = (
                                resultRecord?.result_data as Record<string, unknown> | undefined
                              )?.[itemGroup.id] as { ocr_value?: string } | undefined;
                              const ocrValue =
                                storedField?.ocr_value ?? String(getValues(fieldName) ?? '');
                              // 入力欄の現在値で再変換検索する。
                              const searchQuery = String(getValues(fieldName) ?? '');
                              setDictionaryReconciliationTarget({
                                dictionary,
                                ocrValue,
                                searchQuery,
                              });
                              setDictionaryModalOpen(true);
                            }}
                            sx={{
                              ...secondaryButtonBackgroundColor,
                              padding: '4px 8px',
                              height: '51px',
                              minWidth: 'auto',
                              gap: '2px',
                              flexShrink: 0,
                            }}
                          >
                            <MenuBookIcon sx={{ fontSize: '16px' }} />
                            <Typography
                              sx={{ fontSize: '11px', fontWeight: 400, whiteSpace: 'nowrap' }}
                            >
                              変換
                            </Typography>
                          </Button>
                        )}
                      </Box>
                      {((): React.ReactElement | null => {
                        // マスタ照合・辞書変換のどちらかに紐づく項目なら、
                        // result_data の match_reason_label（自動照合/辞書変換の理由）を表示する。
                        const master = mastersBySearchItemId.get(itemGroup.id);
                        const isVerificationItem =
                          mastersBySearchItemId.has(itemGroup.id) ||
                          dictionariesBySearchItemId.has(itemGroup.id);
                        if (!isVerificationItem) return null;
                        // 表示優先順:
                        //   1) selectedMasterIds（マスタ照合で手動選択し直したラベル）
                        //   2) Firestore result_data 内の自動照合/辞書変換ラベル
                        const mapKey = master ? `${master.master_id}-0` : '';
                        const entry = mapKey ? selectedMasterIds.get(mapKey) : undefined;
                        const singleResultData = resultRecord?.result_data as
                          | Record<string, unknown>
                          | undefined;
                        const firestoreField = singleResultData?.[itemGroup.id] as
                          | {
                              match_reason_label?: string;
                              match_filter_values?: MatchFilterValue[];
                            }
                          | undefined;
                        const firestoreLabel = firestoreField?.match_reason_label;
                        // D-2: 紐付け解除済みなら Firestore 由来ラベルは打ち消す。
                        const isUnlinked = mapKey ? unlinkedMasterKeys.has(mapKey) : false;
                        const label =
                          entry?.match_reason_label ?? (isUnlinked ? '' : firestoreLabel);
                        if (!label) return null;
                        // 絞り込みは照合時の値を出す（#3517）。ラベルと同じ出どころから取る
                        const filterValues = entry?.match_reason_label
                          ? entry.match_filter_values
                          : firestoreField?.match_filter_values;
                        return <MatchReasonLabel label={label} filterValues={filterValues} />;
                      })()}
                      {/* 確認すべき理由（#3262）。照合・辞書変換のラベルより下に出す。
                          バリデーションエラー（#2815）もここに統合するため、
                          Controller 側の helperText は使わない（同じ内容が2箇所に出るため） */}
                      {((): React.ReactElement | null => {
                        // 計算フィールドの「要注意にする選択肢」は現在値で判定し直す（#3358）。
                        // 保存済みの判定は OCR 時点のものなので、金額を直したら
                        // その場で赤字が出る／消えるようにする
                        // reviewIssues の時点で計算フィールドの reviewChoice は
                        // 現在値に差し替わっている（#3358）。ここでは何も足さない
                        const reasons = buildCellReviewReasons(
                          errors[toFormFieldName(itemGroup.parentValue)]?.message as
                            | string
                            | undefined,
                          calculationCellState?.errorMessage,
                          cellReviewIssuesByPath.get(itemGroup.id) ?? [],
                        );
                        if (reasons.length === 0) return null;
                        return (
                          <Typography
                            sx={{
                              // 入力欄との間隔は照合ラベルと揃える（#3262）
                              // 入力欄の直下に置く。次の項目との間隔より近くしてグルーピングを保つ（#3262）
                              mt: '2px',
                              mb: '2px',
                              fontSize: '11px',
                              lineHeight: 1.4,
                              color: red50,
                              textAlign: 'left',
                              wordBreak: 'break-word',
                            }}
                          >
                            [要注意] {reasons.join(' / ')}
                          </Typography>
                        );
                      })()}
                      {/* 計算に使った値（#3358）。要注意かどうかによらず常に出す。
                          「一致」でも何と何を比べたのかが分かるほうが確認しやすい。
                          問題を示すものではないのでグレーにし、要注意（赤）と区別する */}
                      {((): React.ReactElement | null => {
                        const breakdown = calculationBreakdownByItemId.get(itemGroup.id);
                        if (!breakdown) return null;
                        return (
                          <Typography
                            sx={{
                              mt: '2px',
                              mb: '2px',
                              fontSize: '11px',
                              lineHeight: 1.4,
                              color: gray80,
                              textAlign: 'left',
                              wordBreak: 'break-word',
                            }}
                          >
                            {breakdown}
                          </Typography>
                        );
                      })()}
                    </Box>
                  );
                } else {
                  // 配列フィールド（テーブル形式）の場合
                  // 親項目全体が再読取中かどうかを判定
                  const isReReadingWholeArray = reReadingItemIds.includes(itemGroup.id);
                  const fieldResultData = resultRecord?.result_data as
                    | Record<string, unknown>
                    | undefined;
                  // 行数が多いテーブルだけヘッダー行を固定する（#3356）
                  const isDetailTableScrollable =
                    (dynamicFormData[itemGroup.parentValue]?.length ?? 0) >
                    detailTableStickyHeader.minRowCount;

                  return (
                    <TableContainer
                      key={groupIndex}
                      component={Paper}
                      sx={{
                        ...styles.deliveryProductInfo,
                        mb: 3,
                        margin: 0,
                        padding: '16px 0',
                      }}
                    >
                      {/* 配列名（親項目名） */}
                      <Box
                        sx={{
                          padding: '8px 4px',
                          textAlign: 'left',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '4px',
                          ...regularMd,
                        }}
                      >
                        {itemGroup.parentValue}
                        {itemGroup.required && (
                          <Typography component="span" sx={styles.requiredBadge}>
                            必須
                          </Typography>
                        )}
                        <IconButton
                          size="small"
                          onClick={(): void =>
                            handleEditClick(
                              itemGroup.id,
                              itemGroup.parentValue,
                              itemGroup.type,
                              undefined,
                              itemGroup.extractionRule,
                              itemGroup.outputFormat,
                              false,
                              undefined,
                              // 明細（テーブル）の親項目にも制約を渡す（#3309）。
                              // 渡さないとモーダルが未設定状態で開き、そのまま保存すると
                              // 既存の必須設定が消えてしまう
                              {
                                required: itemGroup.required,
                                minLength: itemGroup.minLength,
                                maxLength: itemGroup.maxLength,
                                minValue: itemGroup.minValue,
                                maxValue: itemGroup.maxValue,
                                treatZeroAsEmpty: itemGroup.treatZeroAsEmpty,
                                // 文字種・入力パターン・日付範囲（#3357）
                                charType: itemGroup.charType,
                                reviewChoiceValues: itemGroup.reviewChoiceValues, // 要注意にする選択肢（#3357）
                                reviewLowConfidence: itemGroup.reviewLowConfidence, // 読み取り精度の要注意（#3384）
                                pattern: itemGroup.pattern,
                                patternMessage: itemGroup.patternMessage,
                                dateNoPast: itemGroup.dateNoPast,
                                dateNoFuture: itemGroup.dateNoFuture,
                                datePastLimit: itemGroup.datePastLimit,
                                datePastUnit: itemGroup.datePastUnit,
                                dateFutureLimit: itemGroup.dateFutureLimit,
                                dateFutureUnit: itemGroup.dateFutureUnit,
                              },
                            )
                          }
                          sx={{ padding: '2px' }}
                        >
                          <ModeEditOutlinedIcon sx={{ fontSize: '14px', color: gray80 }} />
                        </IconButton>
                      </Box>
                      <Box
                        ref={(node: HTMLDivElement | null): void => {
                          // 行を追加したときに一番下まで送るために保持する（#3356）
                          if (node) {
                            detailScrollRefs.current.set(itemGroup.id, node);
                          } else {
                            detailScrollRefs.current.delete(itemGroup.id);
                          }
                        }}
                        sx={{
                          overflowX: 'auto',
                          width: '100%',
                          // 行数が多いテーブルだけテーブル自身を縦スクロールさせ、
                          // ヘッダー行を上に貼り付ける（#3356）。
                          // overflowX が auto だとこの Box が sticky のスクロールコンテナになるため、
                          // ここで高さを制限しないとヘッダー行の縦 sticky は効かない。
                          // SP は leftContainer が height:auto でページ全体スクロールのため、
                          // ここで枠を作ると狭い画面に入れ子スクロールができて操作しにくい。
                          // PC（leftContainer が 100vh でスクロールする側）だけ有効にする
                          ...(isDetailTableScrollable
                            ? {
                                '@media (min-width: 960px)': {
                                  overflowY: 'auto',
                                  // 画面の高さに合わせて伸ばす。明細が1つだけの帳票では
                                  // 画面下まで使えるので、固定値より多くの行を出せる。
                                  // reservedVh は画面上部のヘッダーと明細の前後に置く余白の目安。
                                  // maxHeight だけにして高さを固定しない。中身が収まるうちは
                                  // 枠も縮むので、行が溢れたときだけスクロールが出る
                                  maxHeight: `calc(100vh - ${detailTableStickyHeader.reservedVh}vh)`,
                                  // macOS は既定でスクロールバーが消えるため、
                                  // スクロールできることに気づけない。常に表示する
                                  scrollbarWidth: 'thin',
                                  '&::-webkit-scrollbar': {
                                    width: '10px',
                                    height: '10px',
                                  },
                                  '&::-webkit-scrollbar-thumb': {
                                    backgroundColor: gray40,
                                    borderRadius: '5px',
                                  },
                                  '&::-webkit-scrollbar-track': {
                                    backgroundColor: gray5,
                                  },
                                },
                              }
                            : {}),
                          ...(isReReadingWholeArray ? styles.reReadingOverlay : {}),
                        }}
                      >
                        {isReReadingWholeArray && (
                          <Box sx={styles.reReadingLabel}>
                            <RotateRightRoundedIcon sx={{ ...styles.loadingIcon, fontSize: 18 }} />
                            再読取中...
                          </Box>
                        )}
                        {((): React.ReactElement => {
                          // 列レイアウトは detailTableLayouts で計算済み（#3275）。
                          // ここで filter すると毎レンダー別の配列になり、
                          // DetailTableRow の memo 化が効かなくなる
                          const layout = detailTableLayouts.get(itemGroup.id);
                          const activeChildren = layout?.activeChildren ?? EMPTY_CHILDREN;
                          const totalMinWidth = layout?.totalMinWidth ?? 64;
                          const totalFlex = layout?.totalFlex ?? 0;
                          const hasTextareaChild = layout?.hasTextareaChild ?? false;

                          return (
                            <Box sx={{ minWidth: `${totalMinWidth}px` }}>
                              {/* ヘッダー行 */}
                              <Box
                                sx={{
                                  display: 'flex',
                                  borderBottom: `2px solid ${gray40}`,
                                  ...regularXs,
                                  // 行数が多いテーブルはスクロールしてもヘッダー行を残す（#3356）。
                                  // 背景色を敷かないと下の行が透けて重なって見える。
                                  // 縦スクロール枠を作る PC だけに合わせる（SP は枠自体を作らない）
                                  ...(isDetailTableScrollable
                                    ? {
                                        '@media (min-width: 960px)': {
                                          position: 'sticky',
                                          top: 0,
                                          zIndex: zIndexLayer.stickyDetailTableHeader,
                                          backgroundColor: white,
                                        },
                                      }
                                    : {}),
                                }}
                              >
                                {activeChildren.map(
                                  (childField: ItemListChildType, childIndex: number) => {
                                    const flexValue = getColumnFlex(childField.displayWidth);
                                    const widthPercent = (flexValue / totalFlex) * 100;
                                    const isColumnReReading = reReadingItemIds.includes(
                                      childField.id,
                                    );
                                    return (
                                      <Box
                                        key={childIndex}
                                        sx={{
                                          width: `calc((100% - 64px) * ${widthPercent} / 100)`,
                                          minWidth: `${getColumnMinWidth(childField.displayWidth)}px`,
                                          flexShrink: 0,
                                          padding: '8px 4px',
                                          boxSizing: 'border-box',
                                          textAlign: 'left',
                                          ...(isColumnReReading ? styles.reReadingOverlay : {}),
                                        }}
                                      >
                                        <Box
                                          sx={{
                                            display: 'flex',
                                            flexDirection: 'row',
                                            alignItems: 'baseline',
                                            flexWrap: 'wrap',
                                            columnGap: '4px',
                                            rowGap: '2px',
                                          }}
                                        >
                                          <Box
                                            component="span"
                                            sx={{ lineHeight: '18px', minWidth: 0 }}
                                          >
                                            {childField.childValue}
                                            {childField.required && (
                                              <Typography
                                                component="span"
                                                sx={{
                                                  ...styles.requiredBadge,
                                                  marginLeft: '4px',
                                                  whiteSpace: 'nowrap',
                                                }}
                                              >
                                                必須
                                              </Typography>
                                            )}
                                          </Box>
                                          {renderItemSourceBadge(
                                            childField.id,
                                            childField.is_ocr_target,
                                            childField.fixedValue,
                                            childField.is_calculated,
                                          )}
                                          {isColumnReReading ? (
                                            <RotateRightRoundedIcon
                                              sx={{
                                                ...styles.loadingIcon,
                                                fontSize: 14,
                                                alignSelf: 'center',
                                              }}
                                            />
                                          ) : (
                                            <IconButton
                                              size="small"
                                              onClick={(): void =>
                                                handleEditClick(
                                                  childField.id,
                                                  childField.childValue,
                                                  childField.type,
                                                  childField.displayWidth,
                                                  childField.extractionRule,
                                                  childField.outputFormat,
                                                  true,
                                                  itemGroup.id,
                                                  {
                                                    required: childField.required,
                                                    minLength: childField.minLength,
                                                    maxLength: childField.maxLength,
                                                    minValue: childField.minValue,
                                                    maxValue: childField.maxValue,
                                                    treatZeroAsEmpty: childField.treatZeroAsEmpty, // #3262
                                                    // 文字種・入力パターン・日付範囲（#3357）
                                                    charType: childField.charType,
                                                    reviewChoiceValues:
                                                      childField.reviewChoiceValues, // 要注意にする選択肢（#3357）
                                                    reviewLowConfidence:
                                                      childField.reviewLowConfidence, // 読み取り精度の要注意（#3384）
                                                    pattern: childField.pattern,
                                                    patternMessage: childField.patternMessage,
                                                    dateNoPast: childField.dateNoPast,
                                                    dateNoFuture: childField.dateNoFuture,
                                                    datePastLimit: childField.datePastLimit,
                                                    datePastUnit: childField.datePastUnit,
                                                    dateFutureLimit: childField.dateFutureLimit,
                                                    dateFutureUnit: childField.dateFutureUnit,
                                                  },
                                                )
                                              }
                                              sx={{ padding: '2px', alignSelf: 'center' }}
                                            >
                                              <ModeEditOutlinedIcon
                                                sx={{ fontSize: '14px', color: gray80 }}
                                              />
                                            </IconButton>
                                          )}
                                        </Box>
                                      </Box>
                                    );
                                  },
                                )}
                                <Box sx={{ width: '32px', flexShrink: 0 }}></Box>
                                <Box sx={{ width: '32px', flexShrink: 0 }}></Box>
                              </Box>
                              {/* データ行 */}
                              {dynamicFormData[itemGroup.parentValue]?.map(
                                (item: DynamicFieldValue, itemIndex: number) => (
                                  <DetailTableRow
                                    // 行の一意ID を key にする（#3283）。配列位置だと
                                    // 行を挿入・削除したとき React が別の行を使い回す
                                    key={getRowId(item, itemIndex)}
                                    item={item}
                                    itemIndex={itemIndex}
                                    itemGroup={itemGroup}
                                    activeChildren={activeChildren}
                                    totalFlex={totalFlex}
                                    hasTextareaChild={hasTextareaChild}
                                    fieldResultData={fieldResultData}
                                    dynamicFormData={dynamicFormData}
                                    arrayFieldErrors={arrayFieldErrors}
                                    calculationCellStates={calculationCellStates}
                                    mastersBySearchItemId={mastersBySearchItemId}
                                    dictionariesBySearchItemId={dictionariesBySearchItemId}
                                    masterMappingTargetIds={masterMappingTargetIds}
                                    dictionaryMappingTargetIds={dictionaryMappingTargetIds}
                                    selectedMasterIds={selectedMasterIds}
                                    unlinkedMasterKeys={unlinkedMasterKeys}
                                    cellReviewIssuesByPath={cellReviewIssuesByPath}
                                    reReadingItemIds={reReadingItemIds}
                                    isProcessingStatus={isProcessingStatus}
                                    resultRecord={resultRecord}
                                    user={user}
                                    nextRowIdCounter={nextRowIdCounter}
                                    getColumnMinWidth={getColumnMinWidth}
                                    getColumnFlex={getColumnFlex}
                                    createEmptyRow={createEmptyRow}
                                    updateCellValue={updateCellValue}
                                    setDynamicFormData={setDynamicFormData}
                                    setFocusItem={setFocusItem}
                                    setNextRowIdCounter={setNextRowIdCounter}
                                    setReconciliationTarget={setReconciliationTarget}
                                    setMasterModalOpen={setMasterModalOpen}
                                    setDictionaryReconciliationTarget={
                                      setDictionaryReconciliationTarget
                                    }
                                    setDictionaryModalOpen={setDictionaryModalOpen}
                                    validateArrayField={validateArrayField}
                                    updateArrayFieldError={updateArrayFieldError}
                                    discardRowState={discardRowState}
                                    requestRecalculation={requestRecalculation}
                                    requestRecalculationForItem={requestRecalculationForItem}
                                    resolveSupplierId={resolveSupplierId}
                                    resolveFilterConditions={resolveFilterConditions}
                                  />
                                ),
                              )}
                            </Box>
                          );
                        })()}
                      </Box>
                      {/* 明細が1行も無い（#3309）。
                          明細セルはバリデーション由来の要注意をセルに出さない方針だが、
                          0行のときはそのセル自体が存在せず、表示できる場所が無い。
                          テーブルの下に出して「何を直せばよいか」が分かるようにする */}
                      {itemGroup.required === true &&
                        (dynamicFormData[itemGroup.parentValue]?.length ?? 0) === 0 && (
                          <Typography
                            sx={{
                              // 単一項目の要注意（上部）と同じ見た目に揃える
                              mt: '2px',
                              mb: '2px',
                              fontSize: '11px',
                              lineHeight: 1.4,
                              color: red50,
                              textAlign: 'left',
                              wordBreak: 'break-word',
                            }}
                          >
                            [要注意] 必須項目です（1行以上入力してください）
                          </Typography>
                        )}
                      <Button
                        startIcon={<AddIcon />}
                        onClick={(): void => {
                          // 配列をコピーしてから末尾に追加する（#3275）
                          const emptyRow = createEmptyRow(itemGroup.children, nextRowIdCounter);
                          setDynamicFormData(prev => ({
                            ...prev,
                            [itemGroup.parentValue]: [
                              ...(prev[itemGroup.parentValue] ?? []),
                              emptyRow,
                            ],
                          }));
                          setNextRowIdCounter(prev => prev + 1); // カウンターを増加
                          // 追加行の計算フィールドを計算する（#3172）
                          requestRecalculation();
                          // 追加した行は縦スクロール枠の外（下）に増えて見えないため、
                          // 一番下まで送って見えるようにする（#3356）
                          scrollDetailTableToBottom(itemGroup.id);
                        }}
                        sx={styles.addButtom}
                      >
                        <Typography sx={boldSm}>行を追加</Typography>
                      </Button>
                    </TableContainer>
                  );
                }
              })}
          </Box>
        </Grid2>
      </Grid2>
      {patternData?.status !== patternStatus.deleted && (
        <Box sx={styles.footerButtons}>
          <Button
            sx={[saveButtonStyles.buttonChange, { height: '44px' }]}
            onClick={(): void => handleProcessButton()}
          >
            <Typography sx={saveButtonStyles.textColorChange}>{buttonText()}</Typography>
          </Button>
          <Box sx={styles.footerTwoButtons}>
            <Button
              sx={{ ...secondaryButtonBackgroundColor, flexGrow: '4', height: '44px' }}
              onClick={(): void => {
                if (!resultId) return;
                runWithValidationConfirm('保存', () => {
                  openHistoryConfirm(processingStatus.draft);
                });
              }}
            >
              <Typography sx={styles.draftButtonText}>下書き保存</Typography>
            </Button>
            <Tooltip title="その他の操作">
              <Button
                sx={{
                  ...tertiaryButtonBackgroundColorWhiteOverlay80,
                  minWidth: '32px',
                  padding: '0',
                  height: '44px',
                }}
                onClick={(e): void => setActionMenuAnchor(e.currentTarget)}
              >
                <MoreVertIcon />
              </Button>
            </Tooltip>
          </Box>
        </Box>
      )}
      <DeleteModal
        open={isModalOpen}
        onClose={(): void => {
          setIsModalOpen(false);
        }}
        onDelete={async (): Promise<void> => {
          if (isProcessingStatus === null) return;
          const deleteFlg = true;
          handleResults(isProcessingStatus, deleteFlg);
        }}
        title="ファイル"
      />

      <ItemEditModal
        open={editModalOpen}
        onClose={(): void => {
          setEditModalOpen(false);
          setEditItemData(null);
          setEditItemCalcContext(null);
          editBaseItemListRef.current = null;
        }}
        onSave={handleEditSave}
        onSaveAndReOcr={handleEditSaveAndReOcr}
        itemData={editItemData}
        isCustomPrompt={!!patternData?.use_custom_prompt && !isOperatorStaff(user?.role)}
        // 計算フィールド設定（#3145）。結果画面からも設定を確認・編集できるようにする
        calculationContext={editItemCalcContext ?? undefined}
        // 子パターンで読まれた結果では、取得ルールは子パターン側が使われる（#2967）。
        // ここで親の項目設定を直しても効かないため、子パターンの編集へ誘導する
        appliedSubPatternName={resultRecord?.sub_pattern_name ?? null}
        onEditSubPattern={
          resultRecord?.sub_pattern_id
            ? (): void => {
                setEditModalOpen(false);
                setEditItemData(null);
                void openSubPatternEdit();
              }
            : undefined
        }
      />

      <CustomSnackbar
        open={errorText !== '' || snackbarText !== ''}
        text={errorText}
        infoText={snackbarText}
        closeButton={(): void => {
          setSnackbarText('');
          setErrorText('');
        }}
        onClose={(): void => {
          setSnackbarText('');
          setErrorText('');
        }}
      />

      <MasterReconciliationModal
        open={masterModalOpen}
        onClose={(): void => {
          setMasterModalOpen(false);
          setReconciliationTarget(null);
        }}
        onSelect={handleMasterSelect}
        onUnlink={(): void => {
          if (reconciliationTarget) {
            handleMasterUnlink(reconciliationTarget.master, reconciliationTarget.rowIndex);
          }
          setMasterModalOpen(false);
          setReconciliationTarget(null);
        }}
        master={reconciliationTarget?.master ?? null}
        companyId={effectiveCompanyId ?? ''}
        ocrValue={reconciliationTarget?.ocrValue ?? ''}
        searchQuery={reconciliationTarget?.searchQuery}
        supplierId={reconciliationTarget?.supplierId}
        filterConditions={reconciliationTarget?.filterConditions}
      />

      <DictionaryReconciliationModal
        open={dictionaryModalOpen}
        onClose={(): void => {
          setDictionaryModalOpen(false);
          setDictionaryReconciliationTarget(null);
        }}
        onSelect={handleDictionarySelect}
        onUnlink={(): void => {
          if (dictionaryReconciliationTarget) {
            handleDictionaryUnlink(
              dictionaryReconciliationTarget.dictionary,
              dictionaryReconciliationTarget.rowIndex,
            );
          }
          setDictionaryModalOpen(false);
          setDictionaryReconciliationTarget(null);
        }}
        dictionary={dictionaryReconciliationTarget?.dictionary ?? null}
        ocrValue={dictionaryReconciliationTarget?.ocrValue}
        searchQuery={dictionaryReconciliationTarget?.searchQuery}
      />

      <MasterHistorySaveConfirmModal
        open={masterHistoryConfirmOpen}
        onClose={(): void => setMasterHistoryConfirmOpen(false)}
        onSkip={(): void => {
          setMasterHistoryConfirmOpen(false);
          setSelectedMasterIds(new Map());
          // マスタ履歴を保存しない場合も、辞書変換の変換履歴を確認する
          openDictionaryHistoryConfirmOrFinish(masterHistoryNextStatus);
        }}
        onConfirm={handleMasterHistoryConfirm}
        items={masterHistoryItems}
        loading={isLoading}
      />

      <DictionaryHistorySaveConfirmModal
        open={dictionaryHistoryConfirmOpen}
        onClose={(): void => setDictionaryHistoryConfirmOpen(false)}
        onSkip={(): void => {
          setDictionaryHistoryConfirmOpen(false);
          setSelectedDictionaryHistories(new Map());
          handleResults(dictionaryHistoryNextStatus);
        }}
        onConfirm={handleDictionaryHistoryConfirm}
        items={dictionaryHistoryItems}
        loading={isLoading}
      />

      {/* バリデーションエラー確認ダイアログ（#2815）エラーがあっても操作を続行するか確認 */}
      <StatusChangeConfirmDialog
        open={validationConfirmOpen}
        title="入力内容の確認"
        body={validationConfirmBody}
        confirmLabel="続行する"
        onClose={handleValidationConfirmClose}
        onConfirm={handleValidationConfirm}
      />

      {/* 検証ログパネル（#3109 運営のみ・検証トレースがある実行のみ）。
          帳票画像に被らないよう右側 Drawer で OCR結果/プロンプト/値取得結果を表示する。 */}
      {selectedVerificationTrace && (
        <VerificationTracePanel
          open={verificationPanelOpen}
          onClose={(): void => setVerificationPanelOpen(false)}
          trace={selectedVerificationTrace}
          isProxyMode={isProxyMode}
        />
      )}

      {/* 担当者変更ダイアログ（#2642）親が保持する候補リストを渡して二重フェッチを防ぐ */}
      <AssigneeChangeDialog
        open={assigneeDialogOpen}
        onClose={(): void => setAssigneeDialogOpen(false)}
        mode="single"
        targetResultIds={resultId ? [resultId] : []}
        currentAssigneeId={resultRecord?.assignee_id as string | undefined}
        targetLabel={fileName}
        companyId={effectiveCompanyId ?? user?.company_id ?? ''}
        options={assigneeOptions}
        onSuccess={handleAssigneeChangeSuccess}
        onError={handleAssigneeChangeError}
      />

      {/* 仕分け変更モーダル */}
      <PatternSelectModal
        open={reclassifyModalOpen}
        onClose={(): void => setReclassifyModalOpen(false)}
        onSelect={handleReclassifySelect}
        currentPatternId={patternId}
        companyId={effectiveCompanyId || ''}
        title="仕分け変更"
        usesCredit={calculateOcrLimitInfo(company, baseRemainingCount).remainingCount <= 0}
        hideWarning={isProxyMode}
      />

      <OcrLimitDialog
        open={limitDialogOpen}
        onClose={(): void => setLimitDialogOpen(false)}
        companyPlan={company?.plan}
      />

      {/* 再読取設定モーダル（#2960 運営スタッフ限定）。運営はクレジット案内を出さない。 */}
      <ReOcrSettingsModal
        open={reOcrModalOpen}
        onClose={(): void => setReOcrModalOpen(false)}
        settings={reOcrSettings}
        onChange={setReOcrSettings}
        onExecute={(subPatternId): void => {
          void executeReOcrAll(reOcrSettings, subPatternId);
        }}
        // 子パターンの選択（#2967）。子パターンを持つパターンでのみ選択欄が出る
        subPatterns={canChangeSubPattern ? existingSubPatterns : []}
        currentSubPatternId={resultRecord?.sub_pattern_id as string | null | undefined}
      />

      {/* 一般ユーザー向け OCR枚数消費の確認モーダル（従来どおり） */}
      <OcrCreditConsumeConfirmModal
        open={creditConfirmOpen}
        onClose={(): void => setCreditConfirmOpen(false)}
        onConfirm={(subPatternId): void => {
          setCreditConfirmOpen(false);
          void handleReOcrWithSubPattern(subPatternId);
        }}
        usesCredit={calculateOcrLimitInfo(company, baseRemainingCount).remainingCount <= 0}
        // 子パターンの選択（#2967）。子パターンを持つパターンでのみ選択欄が出る
        subPatterns={canChangeSubPattern ? existingSubPatterns : []}
        currentSubPatternId={resultRecord?.sub_pattern_id as string | null | undefined}
      />

      {/* ファイル出力モーダル（表示中の1件のみ出力） */}
      <FileExportDialog
        open={fileExportDialogOpen}
        onClose={(): void => {
          setFileExportDialogOpen(false);
        }}
        onExport={handleFileExport}
        pattern={patternData}
        selectedTab={
          typeof resultRecord?.processing_status === 'number'
            ? resultRecord.processing_status
            : processingStatus.completed
        }
        exportTemplates={exportTemplates}
        onTemplateExport={handleTemplateExport}
        hasUnsavedEdits={hasUnsavedResultEdits}
      />

      {/* AI取得ルール改善提案モーダル */}
      <PatternRuleImprovementModal
        open={ruleImprovementOpen}
        suggestions={ruleSuggestions}
        loading={ruleImprovementLoading}
        onClose={(): void => setRuleImprovementOpen(false)}
        onApply={handleApplyRuleImprovements}
        onSaveAsSubPattern={canCreateSubPattern ? handleSaveSuggestionsAsSubPattern : undefined}
      />

      {/* 子パターンの作成（#2967） */}
      <SubPatternEditModal
        open={subPatternModalOpen}
        variant={editingSubPattern}
        candidates={subPatternCandidates}
        itemList={patternData?.item_list ?? []}
        existingSubPatterns={existingSubPatterns}
        initialOverrides={subPatternInitialOverrides}
        initialItemNames={subPatternInitialItemNames}
        hasChangedItems={hasChangedItems}
        changedItemNames={changedItemNames}
        suggesting={subPatternSuggesting}
        onResuggest={(): void => {
          void suggestSubPatternRulesForResult();
        }}
        hasSuggested={subPatternSuggested}
        hasUnsavedEdits={hasUnsavedResultEdits}
        alignRight
        canUseJson={isOperatorStaff(user?.role)}
        onShowMessage={(message): void => setSnackbarText(message)}
        submitting={subPatternSubmitting}
        onClose={(): void => {
          setSubPatternModalOpen(false);
          setEditingSubPattern(null);
        }}
        onSave={(input): void => {
          void handleSubmitSubPattern(input);
        }}
      />
    </ThemeProvider>
  );
};

/**
 * Provider を外側に置く（#3275）。
 * Inner の中に置くと Inner 自身が Provider の再レンダー対象に含まれ、
 * フォーカス更新で全体が再レンダーされてしまう。
 */
const PatternAiOcrResult: React.FC = () => (
  <FocusItemProvider>
    <PatternAiOcrResultInner />
  </FocusItemProvider>
);

export default PatternAiOcrResult;
